import React from 'react';
import ReactDOM from 'react-dom';
import OrigReact, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { fetchProtectedFile, getFileUrl, getFileFetchUrl, getVideoPlaybackUrl } from "../utils/fileUrlHelper";
import { appAlert } from "../components/AppDialog";
import AvatarImg from "../components/AvatarImg";
import SecureMediaViewer from "../components/SecureMediaViewer";
import CachedImage from "../components/CachedImage";
import RenewableFileImage from "../components/RenewableFileImage";
import UyeModal from "../components/UyeModal";
import MediaActionIcon, { MediaActionLoading } from "../components/MediaActionIcon";
import { yasHesapla } from "../utils/formatters";
import { loadPersistentImage } from "../services/persistentImageCache";
import { useLanguage } from "../i18n/LanguageContext";
import {
  odalariDinleService,
  getCachedChatRooms,
  getCachedChatMessages,
  odaOlusturService,
  odaSilService,
  odaAdGuncelleService,
  mesajlariDinleService,
  mesajGonderService,
  medyaGonderService,
  sesGonderBackend,
  mesajSilService,
  mesajDuzenleService,
  reaksiyonService,
  mesajSabitleService,
  quickActionMesajService,
  odayiOkunduYap,
  odayaUyeEkleService,
  odayaUyeCikarService,
  odaAvatarGuncelleService,
  odaAyarGuncelleService,
  otomatikGaleriKayitKontrolService,
  otomatikGaleriKayitIsaretleService,
  menuOyVerService,
  menuYenileService,
  aktifChatSessionService,
  oylamaGonderService,
  oylamaOyVerService,
  oylamaKapatService,
  OYLAMA_MIN_SECENEK,
  OYLAMA_MAX_SECENEK,
} from "../services/chatService";
import { MENULER } from "../data/menuData";
import { isAndroidPlatform, isIOSPlatform } from "../utils/platformFeatures";
import { Keyboard } from "@capacitor/keyboard";
import callManager from "../services/callManager";
import ChatAramaAfisi from "../components/ChatAramaAfisi";
import { androidDosyaPaylas } from "../utils/androidFileShare";
import appLaunchIcon from "../assets/app-launch-icon.png";
import aileSohbetiAsset from "../assets/ailesohbetiasset.png";
import { readSensitiveItem, removeSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";

/* ── Sabitler ── */
/* Oda avatarları, kartlar çizilmeden hemen önce ortak kalıcı cache katmanına
   hazırlanır. CachedImage aynı in-flight Promise'i kullandığı için ikinci bir
   ağ isteği oluşturmaz. */
if (typeof window !== "undefined") {
  const chatEmptyAssetPreload = new window.Image();
  chatEmptyAssetPreload.decoding = "sync";
  chatEmptyAssetPreload.src = aileSohbetiAsset;
}

function odaAvatarCacheKey(oda) {
  const roomId = String(oda?.id || oda?.firebaseId || "").trim();
  const fileKey = String(oda?.avatarFileKey || "").trim();
  return roomId && fileKey ? `chat-room-avatar:${roomId}:${fileKey}` : "";
}

function preloadOdaAvatars(odalar, getUrl) {
  if (!Array.isArray(odalar)) return;
  odalar.forEach((oda) => {
    const avatarUrl = oda?.avatarPreviewUrl || oda?.avatarUrl;
    if (!avatarUrl) return;
    const src = getUrl(avatarUrl);
    if (!src) return;
    loadPersistentImage(src, odaAvatarCacheKey(oda)).catch(() => {});
  });
}

const EMOJILER = ["❤️","😄","😂","👍","🔥","🎉","😢","😮"];

/* MADDE 3: Mesaj yazma alanı en fazla bu kadar satır büyür; sonrası kendi
   içinde kaydırılır. WhatsApp/Telegram davranışıyla aynı. */
const CHAT_INPUT_MAX_SATIR = 3;
/* Fotoğraf ve video balonları yüklenmeden önce, yüklendikten sonra ve saklama
   süresi dolduğunda aynı geometriyi kullanır. `width`/`height` HTML ölçüleri
   tarayıcının ağ cevabını beklemeden doğru yer ayırmasını sağlar. Bu yalnızca
   yerleşim bilgisidir; sunucuya ek medya/metadata isteği göndermez. */
const CHAT_MEDIA_FRAME_WIDTH = 176;
const CHAT_MEDIA_FRAME_HEIGHT = 268;
const CHAT_MEDIA_FRAME_STYLE = Object.freeze({
  width: `${CHAT_MEDIA_FRAME_WIDTH}px`,
  height: `${CHAT_MEDIA_FRAME_HEIGHT}px`,
  minWidth: `${CHAT_MEDIA_FRAME_WIDTH}px`,
  maxWidth: `${CHAT_MEDIA_FRAME_WIDTH}px`,
  minHeight: `${CHAT_MEDIA_FRAME_HEIGHT}px`,
  maxHeight: `${CHAT_MEDIA_FRAME_HEIGHT}px`,
  boxSizing: "border-box",
  flex: `0 0 ${CHAT_MEDIA_FRAME_HEIGHT}px`,
});
const QUICK_ACTIONS = [
  { key: "photo",    icon: "📸", labelKey: "chat.photo" },
  { key: "audio",    icon: "🎤", labelKey: "chat.audioRecord" },
  { key: "file",     icon: "📎", labelKey: "nav.documents" },
  // MADDE 4: Oylama butonu — Belgeler ile Görevler arasında.
  { key: "poll",     icon: "📊", labelKey: "chat.poll" },
  { key: "task",     icon: "✅", labelKey: "nav.tasks" },
  { key: "shopping", icon: "🛒", labelKey: "nav.shopping" },
  { key: "calendar", icon: "📅", labelKey: "nav.calendar" },
  { key: "menu",     icon: "🍽️", labelKey: "nav.cooking" },
];


const nativeChatMediaSec = ({ lang = "tr", source = "library", allowVideo = true, allowEditing = false } = {}) => new Promise((resolve, reject) => {
  const bridge = window?.webkit?.messageHandlers?.aileNativeBridge;
  if (!bridge) {
    reject(new Error("native_bridge_unavailable"));
    return;
  }

  const callbackId = `chatMedia_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const timeout = window.setTimeout(() => {
    window.removeEventListener("aileChatMediaPicked", handler);
    reject(new Error("native_picker_timeout"));
  }, 120000);

  const handler = async (event) => {
    const detail = event?.detail || {};
    if (detail.callbackId !== callbackId) return;

    window.clearTimeout(timeout);
    window.removeEventListener("aileChatMediaPicked", handler);

    if (!detail.ok) {
      if (detail.cancelled) {
        resolve(null);
        return;
      }
      reject(new Error(detail.message || "native_picker_failed"));
      return;
    }

    try {
      const fileUrl = detail.fileUrl || detail.webPath;
      const webUrl = window.Capacitor?.convertFileSrc && fileUrl
        ? window.Capacitor.convertFileSrc(fileUrl)
        : fileUrl;

      const fileName = detail.fileName || (detail.mediaType === "video" ? `chat-video-${Date.now()}.mp4` : `chat-image-${Date.now()}.jpg`);
      const mimeType = detail.mimeType || (detail.mediaType === "video" ? "video/mp4" : "image/jpeg");

      const blobFromBase64 = () => {
        const base64 = detail.base64 || detail.dataBase64;
        if (!base64 || typeof base64 !== "string") return null;
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        return new Blob([bytes], { type: mimeType });
      };

      const blobFromXhr = (url) => new Promise((res, rej) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.responseType = "blob";
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300 && xhr.response) res(xhr.response);
          else rej(new Error(`native_picker_xhr_${xhr.status}`));
        };
        xhr.onerror = () => rej(new Error("native_picker_xhr_error"));
        xhr.send();
      });

      let blob = blobFromBase64();
      if (!blob && webUrl) {
        try {
          const response = await fetch(webUrl);
          if (!response.ok) throw new Error(`native_picker_fetch_${response.status}`);
          blob = await response.blob();
        } catch (fetchErr) {
          console.warn("native picker fetch failed, trying xhr", fetchErr?.message || fetchErr);
          try {
            blob = await blobFromXhr(webUrl);
          } catch (xhrErr) {
            console.warn("native picker xhr failed", xhrErr?.message || xhrErr);
          }
        }
      }

      if (!blob || !blob.size) throw new Error("native_picker_empty_blob");
      const finalMimeType = mimeType || blob.type || (detail.mediaType === "video" ? "video/mp4" : "image/jpeg");
      const file = new File([blob], fileName, { type: finalMimeType });
      file.nativeCompressed = !!detail.compressed;
      file.durationSeconds = Number(detail.durationSeconds || 0);
      file.mediaTypeHint = detail.mediaType || (finalMimeType.startsWith("video/") ? "video" : "image");
      resolve(file);
    } catch (err) {
      reject(err);
    }
  };

  window.addEventListener("aileChatMediaPicked", handler);
  bridge.postMessage({ action: "pickChatMedia", callbackId, lang, source, allowVideo, allowEditing });
});

const CHAT_UI = {
  "Sohbetler": "Chats",
  "sohbet": "chat",
  "sohbetler": "chats",
  "Yeni Sohbet": "New Chat",
  "Sohbeti Sil": "Delete Chat",
  "Evet, Sil": "Yes, Delete",
  "Sil": "Delete",
  "Kurucu": "Founder",
  "Baba": "Father",
  "Anne": "Mother",
  "Çocuk": "Child",
  "Bebek": "Baby",
  "Dede": "Grandfather",
  "Nene": "Grandmother",
  "Amca": "Uncle",
  "Teyze": "Aunt",
  "Dayı": "Uncle",
  "Aile Yöneticisi": "Family Manager",
  "Henüz sohbet yok": "No chats yet",
  "Yeni bir sohbet oluşturun, ailenizle güzel anları ve planları paylaşmaya başlayın.": "Create a new chat and start sharing special moments and plans with your family.",
};
const ct = (lang, text) => (lang === "en" ? (CHAT_UI[text] || text) : text);
const chatRoleLabel = (lang, role) => ct(lang, role || "");


const CHAT_URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>()]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>()]*)?)/ig;
const normalizeChatLink = (raw) => {
  const cleaned = String(raw || "").replace(/[.,!?;:]+$/g, "");
  return cleaned.startsWith("http://") || cleaned.startsWith("https://") ? cleaned : `https://${cleaned}`;
};
const extractChatLinks = (text) => {
  const value = String(text || "");
  const matches = [];
  value.replace(CHAT_URL_REGEX, (match) => {
    const cleaned = String(match || "").replace(/[.,!?;:]+$/g, "");
    if (cleaned && !matches.includes(cleaned)) matches.push(cleaned);
    return match;
  });
  return matches;
};

function normalizeVideoMimeType(value, fileName = "") {
  const raw = String(value || "").toLowerCase();
  const name = String(fileName || "").toLowerCase();
  if (raw.startsWith("video/")) return raw;
  if (name.endsWith(".mov")) return "video/quicktime";
  if (name.endsWith(".m4v")) return "video/x-m4v";
  return "video/mp4";
}

function ChatPreviewVideo({ src, rawUrl, fileName, mimeType, className, onReady, onFail, onClick }) {
  const safeMimeType = normalizeVideoMimeType(mimeType, fileName);
  const [playSrc, setPlaySrc] = useState(src || "");
  const objectUrlRef = useRef(null);
  const fallbackTriedRef = useRef(false);

  useEffect(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    fallbackTriedRef.current = false;
    setPlaySrc(src || "");
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src]);

  const tryBlobFallback = useCallback(async (videoEl) => {
    if (fallbackTriedRef.current || !rawUrl) {
      onFail?.(videoEl);
      return;
    }
    fallbackTriedRef.current = true;
    try {
      const response = await fetchProtectedFile(rawUrl, { cache: "force-cache" });
      if (!response.ok) throw new Error(`video_blob_${response.status}`);
      const blob = await response.blob();
      if (!blob || !blob.size) throw new Error("video_blob_empty");
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      setPlaySrc(objectUrl);
      setTimeout(() => {
        try { videoEl?.load?.(); } catch (_) {}
      }, 0);
    } catch (err) {
      console.warn("chat pwa video blob fallback failed", err?.message || err);
      onFail?.(videoEl);
    }
  }, [rawUrl, onFail]);

  return (
    <video
      src={playSrc}
      className={className}
      width={CHAT_MEDIA_FRAME_WIDTH}
      height={CHAT_MEDIA_FRAME_HEIGHT}
      /* MADDE 6 NOTU: preload="metadata" KORUNDU. Video kucuk resmi (ilk kare)
         yalnizca metadata yuklendiginde uretilebiliyor; "none" yapilirsa
         onLoadedData/onCanPlay hic tetiklenmez ve kucuk resim gorunmez.
         Metadata istegi kucuk bir Range istegidir, sunucuyu yormaz. Medya
         kutusu zaten sabit olcude oldugu icin yuklenme ziplama yaratmaz. */
      preload="metadata"
      muted
      playsInline
     
      onLoadedMetadata={(e) => {
        try { if (e.currentTarget.currentTime < 0.1) e.currentTarget.currentTime = 0.1; } catch (_) {}
      }}
      onLoadedData={(e) => onReady?.(e.currentTarget)}
      onCanPlay={(e) => onReady?.(e.currentTarget)}
      onError={(e) => tryBlobFallback(e.currentTarget)}
      onClick={onClick}
    >
      <source src={playSrc} type={safeMimeType} />
    </video>
  );
}

function ChatModalVideo({ item }) {
  const rawUrl = item?.rawUrl;
  const src = item?.url;
  const mimeType = normalizeVideoMimeType(item?.mimeType, item?.fileName || item?.name || "video.mp4");
  const [playSrc, setPlaySrc] = useState(src || "");
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef(null);
  const fallbackTriedRef = useRef(false);

  useEffect(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    fallbackTriedRef.current = false;
    setFailed(false);
    setPlaySrc(src || "");
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src]);

  const handleError = useCallback(async () => {
    if (fallbackTriedRef.current || !rawUrl) {
      setFailed(true);
      return;
    }
    fallbackTriedRef.current = true;
    try {
      const response = await fetchProtectedFile(rawUrl, { cache: "force-cache" });
      if (!response.ok) throw new Error(`video_blob_${response.status}`);
      const blob = await response.blob();
      if (!blob || !blob.size) throw new Error("video_blob_empty");
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      setPlaySrc(objectUrl);
      setFailed(false);
    } catch (err) {
      console.warn("chat modal video fallback failed", err?.message || err);
      setFailed(true);
    }
  }, [rawUrl]);

  return (
    <div onClick={(e) => e.stopPropagation()} style={{display:"flex",alignItems:"center",justifyContent:"center",maxWidth:"96%",maxHeight:"70dvh"}}>
      {failed ? (
        <div style={{color:"#fff",background:"rgba(0,0,0,.55)",borderRadius:16,padding:"18px 20px",fontWeight:700,textAlign:"center"}}>Video oynatılamadı.</div>
      ) : (
        <video key={playSrc} src={playSrc}
          controls autoPlay preload="auto" playsInline
          onError={handleError}
          style={{maxWidth:"96vw",maxHeight:"70dvh",objectFit:"contain",borderRadius:12,
            boxShadow:"0 8px 40px rgba(0,0,0,0.5)",background:"#000"}}>
          <source src={playSrc} type={mimeType} />
        </video>
      )}
    </div>
  );
}

const renderChatTextWithLinks = (text) => {
  const value = String(text || "");
  if (!value) return null;
  const parts = [];
  let lastIndex = 0;
  value.replace(CHAT_URL_REGEX, (match, _g, offset) => {
    const cleaned = String(match || "").replace(/[.,!?;:]+$/g, "");
    const trailing = String(match || "").slice(cleaned.length);
    if (offset > lastIndex) parts.push(value.slice(lastIndex, offset));
    if (cleaned) {
      parts.push(
        <a
          key={`link-${offset}-${cleaned}`}
          className="chatMessageLink"
          href={normalizeChatLink(cleaned)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {cleaned}
        </a>
      );
    }
    if (trailing) parts.push(trailing);
    lastIndex = offset + String(match || "").length;
    return match;
  });
  if (lastIndex < value.length) parts.push(value.slice(lastIndex));
  return parts;
};


const normalizeChatDosyaTipi = (tip) => {
  const t = String(tip || "").toLowerCase().trim();
  const map = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
    "jpeg": "jpg",
  };
  if (map[t]) return map[t];
  if (t.includes("/")) return t.split("/").pop() || "jpg";
  return t || "jpg";
};

const safeChatDosyaAdi = (dosyaAdi, mimeType, fallback = "chat-foto") => {
  const clean = String(dosyaAdi || fallback).replace(/[\\/:*?"<>|]+/g, "_").trim() || fallback;
  if (/\.[a-z0-9]{2,8}$/i.test(clean)) return clean;
  const ext = normalizeChatDosyaTipi(mimeType || "image/jpeg");
  return `${clean}.${ext === "jpeg" ? "jpg" : ext}`;
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const fetchChatFileBlob = async (url) => {
  const fileUrl = getFileFetchUrl(url);
  if (!fileUrl) throw new Error("File URL not found");
  const res = await fetchProtectedFile(fileUrl, { method: "GET", cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.blob();
};

const AVATAR_RENKLER = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#ec4899,#f43f5e)",
  "linear-gradient(135deg,#10b981,#059669)",
  "linear-gradient(135deg,#f59e0b,#d97706)",
  "linear-gradient(135deg,#06b6d4,#0891b2)",
  "linear-gradient(135deg,#f97316,#ea580c)",
];
const uyeRenk = (uid, uyeler) => {
  const uye = uyeler.find((u) => u.uid === uid);
  if (uye?.calendarColor) return uye.calendarColor;

  const idx = uyeler.findIndex((u) => u.uid === uid);
  return AVATAR_RENKLER[(idx < 0 ? 0 : idx) % AVATAR_RENKLER.length];
};

const uyeInitials = (uye) => {
  const ad = (uye?.ad || uye?.displayName || uye?.name || "").trim();
  const soyad = (uye?.soyad || uye?.surname || "").trim();
  const ilk = `${ad?.[0] || ""}${soyad?.[0] || ""}`.trim();
  return ilk || uye?.email?.[0]?.toUpperCase?.() || "?";
};

const avatarDuzRenk = (uye, uyeler) => {
  const renk = uye?.calendarColor || uyeRenk(uye?.uid, uyeler);
  return typeof renk === "string" && renk.trim().startsWith("#") ? renk : "#6366f1";
};

const UyeSecAvatar = ({ uye, uyeler }) => {
  const initials = uyeInitials(uye);
  if (uye?.avatar) {
    return (
      <AvatarImg
        avatar={uye.avatar}
        renk={avatarDuzRenk(uye, uyeler)}
        initials={initials}
        size={34}
        glow={false}
        radius="50%"
        className="chatUyeSecAv"
      
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
    );
  }

  return (
    <div className="chatUyeSecAv" style={{ background: uyeRenk(uye?.uid, uyeler) }}>
      {initials}
    </div>
  );
};

const saatStr = (ts, lang = "tr") => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString(lang === "tr" ? "tr-TR" : "en-US", { hour: "2-digit", minute: "2-digit" });
};
const gunStr = (ts, todayLabel = "Bugün", yesterdayLabel = "Dün", lang = "tr") => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const bugun = new Date();
  const dun = new Date(); dun.setDate(dun.getDate() - 1);
  if (d.toDateString() === bugun.toDateString()) return todayLabel;
  if (d.toDateString() === dun.toDateString()) return yesterdayLabel;
  return d.toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", { day: "numeric", month: "long" });
};
const boyutStr = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
};

/* ── Menü kartı (sohbet içi) ── */
function ChatMenuKart({ msg, user, familyId, roomId, uyeler }) {
  const { t } = useLanguage();
  const menu = msg.menuData;
  if (!menu) return null;

  const oylar = msg.menuVotes || {};

  // Kullanıcının mevcut oyunu bul
  const kullaniciOyu = Object.keys(oylar).find(tip =>
    Array.isArray(oylar[tip]) && oylar[tip].includes(user?.uid)
  );

  const oyVer = async (tip) => {
    try {
      await menuOyVerService({ familyId, roomId, mesaj: msg, tip, userUid: user?.uid });
    } catch(e) { console.error(e); }
  };

  const yeniMenuOlustur = async () => {
    try {
      const secilen = MENULER[Math.floor(Math.random() * MENULER.length)];
      await menuYenileService({ familyId, roomId, mesajId: msg.id, menuData: secilen });
    } catch(e) { console.error(e); }
  };

  const begenenUids = Array.isArray(oylar.begendi) ? oylar.begendi : [];
  const begenmeyenUids = Array.isArray(oylar.begenMedi) ? oylar.begenMedi : [];

  // Avatar renderı
  const OyAvatarlar = ({ uids, renk }) => {
    if (!uids || uids.length === 0) return null;
    return (
      <div className="chatMenuOyAvatarlar">
        {uids.slice(0, 5).map((uid) => {
          const uye = uyeler.find(u => u.uid === uid);
          const idx = uyeler.findIndex(u => u.uid === uid);
          const grad = AVATAR_RENKLER[idx < 0 ? 0 : idx % AVATAR_RENKLER.length];
          return (
            <div
              key={uid}
              className="chatMenuOyAvatar"
              style={{ background: grad }}
              title={uye ? `${uye.ad} ${uye.soyad}` : t("health.userFallback")}
            >
              {uye?.ad?.[0] || "?"}
            </div>
          );
        })}
        {uids.length > 5 && <div className="chatMenuOyAvatarFazla">+{uids.length - 5}</div>}
      </div>
    );
  };

  return (
    <div className="chatMenuKart">
      <div className="chatMenuKartGlow" />
      <div className="chatMenuKartBaslik">🍽️ {t("chat.menuSuggestionTitle")}</div>
      <div className="chatMenuSatir chatMenuAna">
        <span className="chatMenuSatirIkon">🍲</span>
        <div>
          <div className="chatMenuSatirEtiket">{t("chat.mainDish")}</div>
          <div className="chatMenuSatirDeger">{menu.anaYemek}</div>
        </div>
      </div>
      <div className="chatMenuSatir">
        <span className="chatMenuSatirIkon">🥣</span>
        <div>
          <div className="chatMenuSatirEtiket">{t("chat.soup")}</div>
          <div className="chatMenuSatirDeger">{menu.corba}</div>
        </div>
      </div>
      <div className="chatMenuSatir">
        <span className="chatMenuSatirIkon">🥗</span>
        <div>
          <div className="chatMenuSatirEtiket">{t("chat.saladMeze")}</div>
          <div className="chatMenuSatirDeger">{menu.salata}</div>
        </div>
      </div>

      {/* Oy alanı */}
      <div className="chatMenuOyArea">
        <div className="chatMenuOyKol">
          <button
            className={`chatMenuOyBtn begendi ${kullaniciOyu === "begendi" ? "aktif" : ""}`}
            onClick={() => oyVer("begendi")}
          >
            👍 {begenenUids.length > 0 && <span>{begenenUids.length}</span>}
          </button>
          <OyAvatarlar uids={begenenUids} />
        </div>

        <button className="chatMenuYeniBtn" onClick={yeniMenuOlustur} title={t("chat.newMenuSuggestion")}>
          🔀
        </button>

        <div className="chatMenuOyKol chatMenuOyKolSag">
          <OyAvatarlar uids={begenmeyenUids} />
          <button
            className={`chatMenuOyBtn begenmedi ${kullaniciOyu === "begenMedi" ? "aktif" : ""}`}
            onClick={() => oyVer("begenMedi")}
          >
            👎 {begenmeyenUids.length > 0 && <span>{begenmeyenUids.length}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   MADDE 5 — PAYLAŞILAN AVATAR YIĞINI

   Hem "okuyanlar" rozetinde hem oylama kartında kullanılır. Avatarlar
   üst üste binerek (stacked) dizilir; böylece 5 avatar bile küçük bir
   alan kaplar. Sınırın üstü "+N" rozetiyle özetlenir — ekran asla
   avatarla dolmaz.
   ════════════════════════════════════════════════════════════════════ */
function ChatAvatarYigini({ uids = [], uyeler = [], max = 5, boyut = 16, className = "", onFazlaClick = null, onAvatarClick = null }) {
  const liste = Array.isArray(uids) ? uids : [];
  if (liste.length === 0) return null;

  const gosterilecek = liste.slice(0, max);
  const kalan = Math.max(0, liste.length - gosterilecek.length);

  return (
    <div className={`chatAvatarYigin ${className}`} style={{ "--cav-boyut": `${boyut}px` }}>
      {gosterilecek.map((uid, i) => {
        const uye = uyeler.find((u) => u.uid === uid);
        const adSoyad = uye ? `${uye.ad || ""} ${uye.soyad || ""}`.trim() : "";
        return (
          <span
            key={uid}
            className="chatAvatarYiginItem"
            style={{ background: uyeRenk(uid, uyeler), zIndex: gosterilecek.length - i }}
            title={adSoyad}
            onClick={onAvatarClick || undefined}
          >
            {(uye?.ad || "?")[0]}
          </span>
        );
      })}
      {kalan > 0 && (
        <span
          className="chatAvatarYiginFazla"
          style={{ zIndex: 0 }}
          onClick={onFazlaClick || undefined}
        >
          +{kalan}
        </span>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   MADDE 4 — OYLAMA KARTI (poll)

   WhatsApp anket kartının mantığı, uygulamanın premium diline uyarlandı:
   soru + seçenekler, animasyonlu yüzde çubukları, oy verenlerin avatarları
   (anonim değilse), tekli/çoklu seçim, oylamayı bitirme.

   Oy verme mevcut PATCH /messages/:id ucunu kullanır (menü kartıyla aynı) —
   yeni endpoint, yeni polling ya da ek veri akışı YOKTUR.
   ════════════════════════════════════════════════════════════════════ */
function ChatOylamaKart({ msg, user, familyId, roomId, uyeler, yoneticiMi = false }) {
  const { t, lang } = useLanguage();
  const [islemde, setIslemde] = useState(false);
  const [oyVerenlerPanel, setOyVerenlerPanel] = useState(null);
  // Sunucu cevabı gelene kadar arayüz anında tepki versin (optimistic).
  const [yerelOylar, setYerelOylar] = useState(null);

  const poll = msg?.pollData || null;
  const oylar = yerelOylar || msg?.pollVotes || {};

  useEffect(() => { setYerelOylar(null); }, [msg?.pollVotes]);

  if (!poll || !Array.isArray(poll.secenekler) || poll.secenekler.length === 0) return null;

  const kapali = !!poll.kapali;
  const benimOylarim = Object.keys(oylar).filter((key) => (oylar[key] || []).includes(user?.uid));
  const oyVerdim = benimOylarim.length > 0;

  // Toplam oy VEREN kişi sayısı (çoklu seçimde bir kişi birden fazla oy verebilir).
  const oyVerenler = new Set();
  Object.keys(oylar).forEach((key) => (oylar[key] || []).forEach((uid) => oyVerenler.add(uid)));
  const toplamOyVeren = oyVerenler.size;

  const enYuksek = poll.secenekler.reduce((max, sec) => Math.max(max, (oylar[sec.id] || []).length), 0);

  const oyVer = async (secenekId) => {
    if (kapali || islemde) return;

    // Optimistic: kullanıcı dokunduğu anda çubuk hareket etsin.
    const kopya = {};
    Object.keys(oylar).forEach((k) => { kopya[k] = [...(oylar[k] || [])]; });
    const zatenVar = (kopya[secenekId] || []).includes(user?.uid);
    if (!poll.cokluSecim) {
      Object.keys(kopya).forEach((k) => { kopya[k] = kopya[k].filter((uid) => uid !== user?.uid); });
    } else if (zatenVar) {
      kopya[secenekId] = (kopya[secenekId] || []).filter((uid) => uid !== user?.uid);
    }
    if (!zatenVar) kopya[secenekId] = [...(kopya[secenekId] || []), user?.uid];
    Object.keys(kopya).forEach((k) => { if (!kopya[k].length) delete kopya[k]; });
    setYerelOylar(kopya);

    setIslemde(true);
    try {
      await oylamaOyVerService({ familyId, roomId, mesaj: msg, secenekId, userUid: user?.uid });
    } catch (err) {
      console.error("[chat oylama]", err);
      setYerelOylar(null);
    } finally {
      setIslemde(false);
    }
  };

  const oylamayiBitir = async () => {
    if (islemde) return;
    setIslemde(true);
    try {
      await oylamaKapatService({ familyId, roomId, mesaj: msg, kapali: true });
    } catch (err) {
      console.error("[chat oylama kapat]", err);
    } finally {
      setIslemde(false);
    }
  };

  const kapatabilir = !kapali && (poll.olusturanUid === user?.uid || yoneticiMi);

  return (
    <div className={`chatOylamaKart ${kapali ? "chatOylamaKapali" : ""}`}>
      <div className="chatOylamaGlow" aria-hidden="true" />

      <div className="chatOylamaUst">
        <span className="chatOylamaRozet">📊 {t("chat.poll")}</span>
        {poll.cokluSecim && <span className="chatOylamaEtiket">{t("chat.pollMulti")}</span>}
        {poll.anonim && <span className="chatOylamaEtiket">{t("chat.pollAnonymous")}</span>}
        {kapali && <span className="chatOylamaEtiket chatOylamaEtiketBitti">{t("chat.pollClosed")}</span>}
      </div>

      <div className="chatOylamaSoru">{poll.soru}</div>

      <div className="chatOylamaSecenekler">
        {poll.secenekler.map((sec) => {
          const secOylar = oylar[sec.id] || [];
          const sayi = secOylar.length;
          const yuzde = toplamOyVeren > 0 ? Math.round((sayi / toplamOyVeren) * 100) : 0;
          const benimSecimim = secOylar.includes(user?.uid);
          const kazanan = kapali && sayi > 0 && sayi === enYuksek;

          return (
            <button
              key={sec.id}
              type="button"
              disabled={kapali}
              className={`chatOylamaSecenek ${benimSecimim ? "chatOylamaSecili" : ""} ${kazanan ? "chatOylamaKazanan" : ""}`}
              onClick={() => oyVer(sec.id)}
            >
              <span className="chatOylamaDolgu" style={{ width: `${yuzde}%` }} aria-hidden="true" />
              <span className={`chatOylamaIsaret ${poll.cokluSecim ? "chatOylamaKare" : ""}`} aria-hidden="true">
                {benimSecimim ? "✓" : ""}
              </span>
              <span className="chatOylamaSecenekMetin">{sec.metin}</span>
              <span className="chatOylamaSecenekSag">
                {!poll.anonim && sayi > 0 && (
                  <ChatAvatarYigini
                    uids={secOylar}
                    uyeler={uyeler}
                    max={5}
                    boyut={16}
                    className="chatOylamaAvatarlar"
                    onFazlaClick={(e) => { e.stopPropagation(); setOyVerenlerPanel({ sec, uids: secOylar }); }}
                  />
                )}
                <span className="chatOylamaYuzde">{yuzde}%</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="chatOylamaAlt">
        <span className="chatOylamaToplam">
          {toplamOyVeren === 0
            ? t("chat.pollNoVotes")
            : t("chat.pollVoterCount", { count: toplamOyVeren })}
        </span>
        {kapatabilir && (
          <button type="button" className="chatOylamaBitirBtn" onClick={oylamayiBitir} disabled={islemde}>
            {t("chat.pollEnd")}
          </button>
        )}
        {!kapali && !kapatabilir && oyVerdim && !poll.cokluSecim && (
          <span className="chatOylamaIpucu">{t("chat.pollChangeHint")}</span>
        )}
      </div>

      {oyVerenlerPanel && ReactDOM.createPortal(
        <div className="chatOkuyanlarOverlay" onClick={() => setOyVerenlerPanel(null)}>
          <div className="chatOkuyanlarPanel" onClick={(e) => e.stopPropagation()}>
            <div className="chatOkuyanlarBaslik">
              <span>{oyVerenlerPanel.sec?.metin}</span>
              <button type="button" className="chatOkuyanlarKapat" onClick={() => setOyVerenlerPanel(null)}>×</button>
            </div>
            <div className="chatOkuyanlarListe">
              {oyVerenlerPanel.uids.map((uid) => {
                const uye = uyeler.find((u) => u.uid === uid);
                return (
                  <div key={uid} className="chatOkuyanlarSatir">
                    <span className="chatOkuyanlarAv" style={{ background: uyeRenk(uid, uyeler) }}>
                      {(uye?.ad || "?")[0]}
                    </span>
                    <span className="chatOkuyanlarAd">
                      {uye ? `${uye.ad || ""} ${uye.soyad || ""}`.trim() : (lang === "en" ? "Member" : "Üye")}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ── Ses mesajı özel player ── */
function ChatAudioPlayer({ src, benimMi, durationSeconds = 0 }) {
  const audioRef   = useRef(null);
  const ilkHazirlikRef = useRef(false);
  const sonDokunusRef  = useRef(0);
  const [oynatiliyor, setOynatiliyor] = useState(false);
  const [sure,      setSure]          = useState(Number(durationSeconds) > 0 ? Number(durationSeconds) : 0);
  const [simdiki,   setSimdiki]       = useState(0);

  const sureStr = (sn) => {
    if (!sn || !isFinite(sn)) return "0:00";
    const m = Math.floor(sn / 60);
    const s = Math.floor(sn % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const togglePlay = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    // iOS/WebKit ilk dokunuşta sadece metadata yükleyip oynatmayı ikinci dokunuşa bırakabiliyor.
    // Bu yüzden ilk kullanıcı etkileşiminde audio'yu aynı gesture içinde load + play yapıyoruz.
    const now = Date.now();
    if (now - sonDokunusRef.current < 260) return;
    sonDokunusRef.current = now;

    const audio = audioRef.current;
    if (!audio) return;

    if (oynatiliyor) {
      audio.pause();
      return;
    }

    try {
      audio.preload = "auto";
      if (!ilkHazirlikRef.current || audio.readyState === 0) {
        ilkHazirlikRef.current = true;
        audio.load();
      }
      const playPromise = audio.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          const yenidenDene = () => {
            audio.removeEventListener("canplay", yenidenDene);
            audio.play().catch(() => {});
          };
          audio.addEventListener("canplay", yenidenDene, { once: true });
          audio.load();
        });
      }
    } catch (_) {}
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio || !sure) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0]?.clientX : e.clientX;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    audio.currentTime = ratio * sure;
    setSimdiki(ratio * sure);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay  = () => setOynatiliyor(true);
    const onPause = () => setOynatiliyor(false);
    const onEnded = () => { setOynatiliyor(false); setSimdiki(0); if (audio) audio.currentTime = 0; };
    const onTime  = () => {
      const current = audio.currentTime || 0;
      const knownDuration = Number(durationSeconds) > 0 ? Number(durationSeconds) : sure;
      setSimdiki(current);
      // iOS/WebKit bazı hatalı mp4/webm bloblarında ended event'i üretmiyor.
      // Kayıt süresi biliniyorsa süre bitince manuel durdurup başa alıyoruz.
      if (knownDuration > 0 && current >= knownDuration - 0.15) {
        audio.pause();
        audio.currentTime = 0;
        setSimdiki(0);
        setOynatiliyor(false);
      }
    };
    const onMeta  = () => {
      if (audio.duration && isFinite(audio.duration)) setSure(audio.duration);
      else if (Number(durationSeconds) > 0) setSure(Number(durationSeconds));
    };
    audio.addEventListener("play",       onPlay);
    audio.addEventListener("pause",      onPause);
    audio.addEventListener("ended",      onEnded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata",  onMeta);
    audio.addEventListener("durationchange",  onMeta);
    if (audio.readyState >= 1 && audio.duration && isFinite(audio.duration)) setSure(audio.duration);
    else if (Number(durationSeconds) > 0) setSure(Number(durationSeconds));
    return () => {
      audio.removeEventListener("play",       onPlay);
      audio.removeEventListener("pause",      onPause);
      audio.removeEventListener("ended",      onEnded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata",  onMeta);
      audio.removeEventListener("durationchange",  onMeta);
    };
  }, [src, durationSeconds, sure]);

  const ilerleme = sure > 0 ? (simdiki / sure) * 100 : 0;

  return (
    <div
      className={`chatAudioPlayer${benimMi ? " benimki" : ""}`}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        playsInline
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />
      <button
        className="chatAudioPlayBtn"
        onPointerDown={togglePlay}
        onTouchEnd={togglePlay}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        aria-label={oynatiliyor ? "Durdur" : "Oynat"}
      >
        {oynatiliyor ? "⏸" : "▶"}
      </button>
      <div className="chatAudioTrack">
        <div className="chatAudioBar" onClick={handleSeek} onTouchEnd={handleSeek}>
          <div className="chatAudioFill" style={{ width: `${ilerleme}%` }} />
          <div className="chatAudioThumb" style={{ left: `${ilerleme}%` }} />
        </div>
        <div className="chatAudioSure">
          <span>{oynatiliyor || simdiki > 0 ? sureStr(simdiki) : sureStr(sure)}</span>
          {oynatiliyor && sure > 0 && <span className="chatAudioToplam"> / {sureStr(sure)}</span>}
        </div>
      </div>
      <span className="chatAudioWaveIcon">🎤</span>
    </div>
  );
}

/* ── Mesaj bileşeni ── */
function MesajItem({ msg, benimMi, uyeler, user, familyId, roomId, islemIzniVarMi, yoneticiMi = false, onReply, onEdit, onQuoteClick, onImagePreview, onFileDownload }) {
  const { t, lang } = useLanguage();
  const [menuAcik, setMenuAcik] = useState(false);
  const [swipePeek, setSwipePeek] = useState(false);
  // MADDE 5: "Okuyanlar" listesi paneli (avatar yığınına dokununca açılır)
  const [okuyanlarAcik, setOkuyanlarAcik] = useState(false);
  const menuRef = useRef(null);
  const portalMenuRef = useRef(null);
  const bubbleRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const mesajSwipeRef = useRef({ startX: 0, startY: 0, moved: false });
  const imageTouchRef = useRef({ startX: 0, startY: 0 });

  useEffect(() => {
    if (!menuAcik) return;
    const fn = (e) => {
      if (menuRef.current?.contains(e.target) || portalMenuRef.current?.contains(e.target)) return;
      setMenuAcik(false);
      setSwipePeek(false);
    };
    document.addEventListener("mousedown", fn);
    document.addEventListener("touchstart", fn);
    return () => { document.removeEventListener("mousedown", fn); document.removeEventListener("touchstart", fn); };
  }, [menuAcik]);

  const openImagePreview = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const fileName = msg.fileName || msg.dosyaAdi || (msg.type === "video" ? `chat-video-${msg.id || Date.now()}.mp4` : `chat-foto-${msg.id || Date.now()}.jpg`);
    onImagePreview?.({
      url: msg.type === "video" ? getVideoPlaybackUrl(msg.mediaUrl, fileName) : getFileUrl(msg.mediaUrl),
      rawUrl: msg.mediaUrl,
      mesajId: msg.id,
      gonderenUid: msg.uid || msg.gonderenUid,
      fileName,
      mimeType: msg.mimeType || msg.mediaType || msg.fileType || msg.dosyaTipi || (msg.type === "video" ? "video/mp4" : "image/jpeg"),
      type: msg.type,
    });
  }, [msg, onImagePreview]);

  const openContextMenu = useCallback((anchorEl) => {
    const rect = (anchorEl || bubbleRef.current || menuRef.current)?.getBoundingClientRect?.();
    const menuWidth = 176;
    const gap = 8;
    const top = rect ? Math.max(8, Math.min(window.innerHeight - 220, rect.top - gap)) : 120;
    let left = rect ? (benimMi ? rect.right - menuWidth : rect.left) : 24;
    left = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, left));
    setMenuPos({ top, left });
    setMenuAcik(true);
    setSwipePeek(true);
  }, [benimMi]);

  const handleMediaTouchEnd = useCallback((e) => {
    const t1 = e.changedTouches?.[0];
    if (!t1) return;
    const dxRaw = t1.clientX - imageTouchRef.current.startX;
    const dx = Math.abs(dxRaw);
    const dy = Math.abs(t1.clientY - imageTouchRef.current.startY);
    if (dy < 45 && dxRaw < -48) {
      e.preventDefault?.();
      e.stopPropagation?.();
      openContextMenu(bubbleRef.current);
      return;
    }
    if (dx < 12 && dy < 12) openImagePreview(e);
  }, [openContextMenu, openImagePreview]);

  /* FAZ 2 — SESLİ ARAMA KAYDI
     Sunucu, arama bitince sohbet akışına `message_type = 'call'` bir kayıt
     düşer. Bunu normal balon yerine ORTALANMIŞ ince bir şerit olarak
     gösteriyoruz (tarih ayırıcılarıyla aynı görsel dil). */
  if (msg.type === "call" && msg.callData) {
    const cd = msg.callData;
    const iptalEdildi = cd.endReason === "cancelled";
    const cevapsiz = cd.endReason === "missed"
      || cd.endReason === "declined"
      || iptalEdildi;
    const sn = Math.max(0, Math.round((cd.durationMs || 0) / 1000));
    const dk = Math.floor(sn / 60);
    const sureMetni = sn < 60
      ? `${sn} ${t("call.sec")}`
      : `${dk} ${t("call.min")}`;
    const metin = iptalEdildi
      ? t("call.logCancelled")
      : (cevapsiz
        ? (cd.endReason === "declined" ? t("call.logDeclined") : t("call.logMissed"))
        : `${t("call.voiceCall")} · ${sureMetni}`);
    return (
      <div className={`chatCallLog ${cevapsiz ? "chatCallLogMissed" : ""}`}>
        <span className="chatCallLogIcon">{cevapsiz ? "📵" : "📞"}</span>
        <span className="chatCallLogText">{metin}</span>
      </div>
    );
  }

  if (msg.deletedAt) {
    const deletedText = t("chat.deletedMessage");
    const safeDeletedText = deletedText && deletedText !== "chat.deletedMessage"
      ? deletedText
      : (lang === "en" ? "This message was deleted" : "Bu mesaj silindi");
    return (
      <div className={`chatMsgRow ${benimMi ? "me" : ""}`}>
        <div className="chatBubble chatBubbleDeleted">🗑 {safeDeletedText}</div>
      </div>
    );
  }

  const reactions = msg.reactions || {};
  const hasReactions = Object.keys(reactions).some((e) => reactions[e]?.length > 0);
  const readByOthers = (msg.readBy || []).filter((u) => u !== msg.uid);
  const canDelete = benimMi ? islemIzniVarMi("deleteOwnMessage") : islemIzniVarMi("deleteAllMessages");
  const canPin    = islemIzniVarMi("pinMessage");
  const canReact  = islemIzniVarMi("reactMessage");
  const canEdit   = benimMi && islemIzniVarMi("editOwnMessage") && msg.type === "text";
  // MADDE 2: Yerel (optimistic) medya mesajlarinda mediaUrl henuz yoktur; bunlar
  // "suresi dolmus medya" olarak gosterilmemeli. msg.pending / msg.localPreviewUrl
  // varsa mesaj hala yukleniyor demektir.
  const yerelMedyaBekliyor = !!(msg.pending || msg.localOnly) && !!msg.localPreviewUrl;
  const mediaExpired = !yerelMedyaBekliyor
    && (!!msg.mediaExpired || (["image", "video", "file", "audio"].includes(msg.type) && !msg.mediaUrl && !msg.fileKey));
  const expiredText = msg.type === "video"
    ? (lang === "en" ? "This video was removed because the 10-day storage period has expired." : "Bu video 10 günlük saklama süresi dolduğu için kaldırıldı.")
    : (t("chat.mediaExpired") || (lang === "en" ? "This media file was removed because the 30-day storage period has expired." : "Bu medya dosyası 30 günlük saklama süresi dolduğu için kaldırıldı."));
  return (
    <div className={`chatMsgRow ${benimMi ? "me" : ""}`} ref={menuRef}>
      {!benimMi && (() => {
        const uye = uyeler.find(u => u.uid === msg.uid);
        return (
          <AvatarImg
            avatar={uye?.avatar || null}
            renk={uyeRenk(msg.uid, uyeler)}
            initials={(msg.ad || "?")[0]}
            size={28}
            glow={false}
            radius="50%"
            className="chatAvatar"
          />
        );
      })()}
      <div className="chatMsgCol">
        {!benimMi && <span className="chatMsgName">{msg.ad}</span>}

        {msg.replyTo && (
          <button
            type="button"
            className="chatQuoteBar"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuoteClick?.(msg.replyTo); }}
            title={t("chat.goToQuotedMessage") || (lang === "en" ? "Go to quoted message" : "Alıntılanan mesaja git")}
          >
            <span className="chatQuoteAd">{msg.replyTo.ad}</span>
            <span className="chatQuoteText">{msg.replyTo.metin?.slice(0, 60) || t("chat.media")}</span>
          </button>
        )}

        <div
          className={`chatBubble ${benimMi ? "chatBubbleMe" : "chatBubbleOther"} ${
            msg.type === "menu" ? "chatBubbleMenu" :
            msg.type === "poll" ? "chatBubblePoll" :
            !["text","image","video","file","audio"].includes(msg.type) ? "chatBubbleAction" : ""
          } ${swipePeek ? "chatBubbleSwipePeek" : ""}`}
          ref={bubbleRef}
          onContextMenu={(e) => { e.preventDefault(); openContextMenu(e.currentTarget); }}
          onTouchStart={(e) => {
            const t = e.touches?.[0];
            if (!t) return;
            mesajSwipeRef.current = { startX: t.clientX, startY: t.clientY, moved: false };
          }}
          onTouchMove={(e) => {
            const t = e.touches?.[0];
            if (!t) return;
            const dx = t.clientX - mesajSwipeRef.current.startX;
            const dy = Math.abs(t.clientY - mesajSwipeRef.current.startY);
            if (dy < 35 && dx < -18) {
              mesajSwipeRef.current.moved = true;
              setSwipePeek(true);
            }
          }}
          onTouchEnd={(e) => {
            const t = e.changedTouches?.[0];
            if (!t) return;
            const dx = t.clientX - mesajSwipeRef.current.startX;
            const dy = Math.abs(t.clientY - mesajSwipeRef.current.startY);
            if (dy < 45 && dx < -48) {
              openContextMenu(e.currentTarget);
            } else if (!menuAcik) {
              setSwipePeek(false);
            }
          }}
        >
          {msg.type === "task"     && <div className="chatActionLabel">📋 {t("chat.taskCreated")}</div>}
          {msg.type === "shopping" && <div className="chatActionLabel">🛒 {t("chat.shoppingAdded")}</div>}
          {msg.type === "calendar" && <div className="chatActionLabel">📅 {t("chat.calendarAdded")}</div>}
          {msg.type === "menu"     && <ChatMenuKart msg={msg} user={user} familyId={familyId} roomId={roomId} uyeler={uyeler} />}
          {msg.type === "poll"     && <ChatOylamaKart msg={msg} user={user} familyId={familyId} roomId={roomId} uyeler={uyeler} yoneticiMi={yoneticiMi} />}

          {mediaExpired && ["image", "video"].includes(msg.type) && (
            <div
              className="chatExpiredMedia chatExpiredMediaFrame"
              style={{
                ...CHAT_MEDIA_FRAME_STYLE,
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
                cursor: "default",
              }}
              role="status"
            >
              <span className="chatExpiredMediaIcon">⏳</span>
              <span>{expiredText}</span>
            </div>
          )}
          {mediaExpired && msg.type === "file" && (
            <div className="chatExpiredMedia">
              <span className="chatExpiredMediaIcon">⏳</span>
              <span>{expiredText}</span>
            </div>
          )}
          {/* MADDE 2 + 6: Gönderilmeyi bekleyen (optimistic) medya, gerçek mesajla
              BİREBİR AYNI sabit kutuda gösterilir. Böylece sunucu cevabı gelince
              yükseklik hiç değişmez ve sohbet zıplamaz. */}
          {msg.type === "image" && yerelMedyaBekliyor && (
            <div className="chatImageWrap chatMediaPending" style={CHAT_MEDIA_FRAME_STYLE}>
              {String(msg.localPreviewUrl).startsWith("blob:")
                ? <img src={msg.localPreviewUrl} alt="" className="chatImage chatImageLoaded" width={CHAT_MEDIA_FRAME_WIDTH} height={CHAT_MEDIA_FRAME_HEIGHT} decoding="async" />
                : <span className="chatMediaLogoPlaceholder" aria-hidden="true"><img src={appLaunchIcon} alt="" /></span>}
              <span className="chatMediaPendingVeil" aria-hidden="true"><span className="chatMediaPendingSpin" /></span>
            </div>
          )}
          {msg.type === "video" && yerelMedyaBekliyor && (
            <div className="chatVideoWrap chatMediaPending" style={CHAT_MEDIA_FRAME_STYLE}>
              <span className="chatMediaLogoPlaceholder" aria-hidden="true"><img src={appLaunchIcon} alt="" /></span>
              <span className="chatMediaPendingVeil" aria-hidden="true"><span className="chatMediaPendingSpin" /></span>
            </div>
          )}
          {msg.type === "image" && msg.mediaUrl && !mediaExpired && (
            <div
              className="chatImageWrap"
              style={CHAT_MEDIA_FRAME_STYLE}
              role="button"
              tabIndex={0}
              onTouchStart={(e) => {
                const t0 = e.touches?.[0];
                if (t0) imageTouchRef.current = { startX: t0.clientX, startY: t0.clientY };
                e.stopPropagation();
              }}
              onTouchEnd={handleMediaTouchEnd}
              onClick={openImagePreview}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openImagePreview(e); }}
            >
              <span className="chatMediaLogoPlaceholder" aria-hidden="true"><img src={appLaunchIcon} alt="" /></span>
              <RenewableFileImage src={msg.mediaPreviewUrl || msg.mediaUrl} fallbackSrc={msg.mediaUrl} loadTimeoutMs={10000} alt={t("chat.sharedImageAlt")} className="chatImage chatImageLoading" width={CHAT_MEDIA_FRAME_WIDTH} height={CHAT_MEDIA_FRAME_HEIGHT} loading="lazy" decoding="async"
                onLoad={(e) => {
                  e.currentTarget.closest?.(".chatImageWrap")?.classList.add("chatMediaLoaded");
                  e.target.classList.remove("chatImageLoading");
                  e.target.classList.add("chatImageLoaded");
                }}
                onError={(e) => {
                  e.currentTarget.closest?.(".chatImageWrap")?.classList.add("chatMediaFailed");
                }} />
            </div>
          )}
          {msg.type === "video" && msg.mediaUrl && !mediaExpired && (
            <div
              className="chatVideoWrap"
              style={CHAT_MEDIA_FRAME_STYLE}
              role="button"
              tabIndex={0}
              onTouchStart={(e) => {
                const t0 = e.touches?.[0];
                if (t0) imageTouchRef.current = { startX: t0.clientX, startY: t0.clientY };
                e.stopPropagation();
              }}
              onTouchEnd={handleMediaTouchEnd}
              onClick={openImagePreview}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openImagePreview(e); }}
            >
              <span className="chatMediaLogoPlaceholder" aria-hidden="true"><img src={appLaunchIcon} alt="" /></span>
              <ChatPreviewVideo
                src={getVideoPlaybackUrl(msg.mediaUrl, msg.fileName || msg.dosyaAdi || `chat-video-${msg.id || Date.now()}.mp4`)}
                rawUrl={msg.mediaUrl}
                fileName={msg.fileName || msg.dosyaAdi || `chat-video-${msg.id || Date.now()}.mp4`}
                mimeType={msg.mimeType || msg.mediaType || msg.fileType || msg.dosyaTipi || "video/mp4"}
                className="chatVideoPreview chatVideoPreviewLoading"
                onReady={(videoEl) => {
                  videoEl.closest?.(".chatVideoWrap")?.classList.add("chatMediaLoaded");
                  videoEl.classList.remove("chatVideoPreviewLoading");
                  videoEl.classList.add("chatVideoPreviewLoaded");
                }}
                onFail={(videoEl) => {
                  videoEl?.closest?.(".chatVideoWrap")?.classList.add("chatMediaFailed");
                }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openImagePreview(e); }}
              />
              <span className="chatVideoPlayBadge">▶</span>
            </div>
          )}
          {msg.type === "file" && !mediaExpired && msg.mediaUrl && (
            <button type="button" className="chatFile"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFileDownload?.({
                  url: getFileUrl(msg.mediaUrl),
                  rawUrl: msg.mediaUrl,
                  fileName: msg.fileName || msg.dosyaAdi || `chat-dosya-${msg.id || Date.now()}`,
                  mimeType: msg.mimeType || msg.mediaType || msg.fileType || msg.dosyaTipi || "application/octet-stream",
                });
              }}>
              <span className="chatFileIcon">📎</span>
              <div className="chatFileInfo">
                <span className="chatFileName">{msg.fileName}</span>
                <span className="chatFileSize">{boyutStr(msg.fileSize)}</span>
              </div>
              <span className="chatFileDownload">⬇</span>
            </button>
          )}
          {msg.type === "audio" && msg.mediaUrl && !mediaExpired && (
            <ChatAudioPlayer src={getFileUrl(msg.mediaUrl)} benimMi={benimMi} durationSeconds={msg.durationSeconds || msg.audioDurationSeconds || 0} />
          )}
          {msg.type === "audio" && mediaExpired && (
            <div className="chatExpiredMedia">
              <span className="chatExpiredMediaIcon">⏳</span>
              <span>{expiredText}</span>
            </div>
          )}
          {msg.metin && msg.type !== "menu" && <span className="chatBubbleText">{renderChatTextWithLinks(msg.metin)}</span>}
          {msg.edited && <span className="chatEdited"> · {t("chat.edited")}</span>}
        </div>

        {hasReactions && (
          <div className="chatReactions">
            {Object.entries(reactions).map(([emoji, uids]) =>
              uids?.length > 0 ? (
                <button key={emoji}
                  className={`chatReact ${uids.includes(user.uid) ? "chatReactMine" : ""}`}
                  onClick={() => reaksiyonService({ familyId, roomId, mesajId: msg.id, emoji, userUid: user.uid })}>
                  {emoji} {uids.length}
                </button>
              ) : null
            )}
          </div>
        )}

        <div className={`chatMsgMeta ${benimMi ? "chatMsgMetaMe" : ""}`}>
          <span className="chatTime">{saatStr(msg.createdAt, lang)}</span>
          {/* MADDE 5: Okuyan kullanıcı avatarları.
              Önceden 4 avatar yan yana basılıyordu ve taşma göstergesi yoktu;
              kalabalık odada mesaj altı avatarla doluyordu. Artık avatarlar
              üst üste binen (stacked) düzende, EN FAZLA 5 tanesi gösterilir;
              geri kalanlar "+N" rozetiyle özetlenir. Rozete ya da yığına
              dokununca okuyanların tam listesi açılır. */}
          {benimMi && readByOthers.length > 0 && (
            <button
              type="button"
              className="chatReadAvsBtn"
              onClick={(e) => { e.stopPropagation(); setOkuyanlarAcik(true); }}
              aria-label={t("chat.readByTitle")}
              title={t("chat.readByTitle")}
            >
              <span className="chatReadTick" aria-hidden="true">✓✓</span>
              <ChatAvatarYigini uids={readByOthers} uyeler={uyeler} max={5} boyut={16} className="chatReadAvsYigin" />
            </button>
          )}
          {msg.pinned && <span className="chatPinBadge">📌</span>}
          <button className="chatMenuBtn" onClick={(e) => {
            if (menuAcik) {
              setMenuAcik(false);
              setSwipePeek(false);
            } else {
              openContextMenu(e.currentTarget);
            }
          }} aria-label={t("chat.options")}>···</button>
        </div>

        {okuyanlarAcik && ReactDOM.createPortal(
          <div className="chatOkuyanlarOverlay" onClick={() => setOkuyanlarAcik(false)}>
            <div className="chatOkuyanlarPanel" onClick={(e) => e.stopPropagation()}>
              <div className="chatOkuyanlarBaslik">
                <span>✓✓ {t("chat.readByTitle")}</span>
                <button type="button" className="chatOkuyanlarKapat" onClick={() => setOkuyanlarAcik(false)}>×</button>
              </div>
              <div className="chatOkuyanlarListe">
                {readByOthers.map((uid) => {
                  const uye = uyeler.find((u) => u.uid === uid);
                  return (
                    <div key={uid} className="chatOkuyanlarSatir">
                      <span className="chatOkuyanlarAv" style={{ background: uyeRenk(uid, uyeler) }}>
                        {(uye?.ad || "?")[0]}
                      </span>
                      <span className="chatOkuyanlarAd">
                        {uye ? `${uye.ad || ""} ${uye.soyad || ""}`.trim() : t("chat.readByUnknown")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}

        {menuAcik && ReactDOM.createPortal(
          <div
            ref={portalMenuRef}
            className={`chatContextMenu chatContextMenuPortal ${benimMi ? "chatContextMenuMe" : ""}`}
            style={{ top: `${menuPos?.top ?? 120}px`, left: `${menuPos?.left ?? 16}px` }}
          >
            {canReact && (
              <div className="chatContextEmojiRow">
                {EMOJILER.map((e) => (
                  <button key={e} className="chatContextEmoji"
                    onClick={() => { reaksiyonService({ familyId, roomId, mesajId: msg.id, emoji: e, userUid: user.uid }); setMenuAcik(false); setSwipePeek(false); }}>
                    {e}
                  </button>
                ))}
              </div>
            )}
            <button className="chatContextItem" onClick={() => { onReply(msg); setMenuAcik(false); setSwipePeek(false); }}>↩ {t("chat.reply")}</button>
            {canEdit && (
              <button className="chatContextItem" onClick={() => { onEdit(msg); setMenuAcik(false); setSwipePeek(false); }}>✏️ {t("chat.edit")}</button>
            )}
            {canPin && (
              <button className="chatContextItem"
                onClick={() => { mesajSabitleService({ familyId, roomId, mesajId: msg.id, pinned: msg.pinned }); setMenuAcik(false); setSwipePeek(false); }}>
                {msg.pinned ? t("chat.unpin") : t("chat.pin")}
              </button>
            )}
            {canDelete && (
              <button className="chatContextItem chatContextDanger"
                onClick={() => { mesajSilService({ familyId, roomId, mesajId: msg.id }); setMenuAcik(false); setSwipePeek(false); }}>
                🗑 {t("common.delete")}
              </button>
            )}
            {!benimMi && (
              <button
                className="chatContextItem"
                onClick={() => {
                  setMenuAcik(false);
                  setSwipePeek(false);
                  const konu = encodeURIComponent(lang === "en" ? "Report Message" : "Mesaj Bildirimi");
                  const govde = encodeURIComponent(
                    lang === "en"
                      ? `I would like to report a message in the family chat.\n\nMessage ID: ${msg.id || ""}\nSender: ${msg.ad || msg.uid || ""}\n\nReason:\n`
                      : `Aile sohbetinde bir mesajı bildirmek istiyorum.\n\nMesaj ID: ${msg.id || ""}\nGönderen: ${msg.ad || msg.uid || ""}\n\nBildirme nedeni:\n`
                  );
                  window.open(`mailto:help@aileyonetimi.com?subject=${konu}&body=${govde}`, "_blank");
                }}
              >
                🚩 {lang === "en" ? "Report" : "Bildir"}
              </button>
            )}
          </div>,
          document.body
        )}
      </div>
      {benimMi && (() => {
        const uye = uyeler.find(u => u.uid === msg.uid);
        return (
          <AvatarImg
            avatar={uye?.avatar || null}
            renk={uyeRenk(msg.uid, uyeler)}
            initials={(msg.ad || "?")[0]}
            size={28}
            glow={false}
            radius="50%"
            className="chatAvatar"
          />
        );
      })()}
    </div>
  );
}

/* ── Üye Ekleme Modalı (ayrı bileşen — Hook kuralları için) ── */
function UyeEkleModal({ odalar, uyeEkleOdaId, uyeler, familyId, onKapat }) {
  const { t } = useLanguage();
  const [secilen, setSecilen] = useState([]);
  const hedefOda = odalar.find(o => o.id === uyeEkleOdaId);
  const mevcutUyeUidler = hedefOda?.uyeUidler || [];
  const eklenebilecek = uyeler.filter(u => !mevcutUyeUidler.includes(u.uid));

  const ekle = async () => {
    try {
      for (const uid of secilen) {
        await odayaUyeEkleService({ familyId, roomId: uyeEkleOdaId, uyeUid: uid });
      }
      onKapat();
    } catch(e) { console.error(e); }
  };

  return (
    <div className="modalOverlay" onClick={onKapat}>
      <div className="darkModal" onClick={(e) => e.stopPropagation()}>
        <div className="darkModalHeader">
          <span className="darkModalTitle">👤 {t("chat.addMember")}</span>
          <button className="darkModalClose" onClick={onKapat}>×</button>
        </div>
        <p style={{color:"var(--text-secondary)",fontSize:"13px",marginBottom:"12px"}}>
          {t("chat.addMembersToRoom", { room: hedefOda?.ad || "" })}
        </p>
        {eklenebilecek.length === 0 ? (
          <div style={{color:"var(--text-muted)",textAlign:"center",padding:"20px"}}>
            {t("chat.allMembersAlreadyInRoom")}
          </div>
        ) : (
          <div className="chatUyeSecGrid">
            {eklenebilecek.map((uye) => {
              const sec = secilen.includes(uye.uid);
              return (
                <button key={uye.uid}
                  className={`chatUyeSecBtn ${sec ? "secili" : ""}`}
                  onClick={() => setSecilen(prev => sec ? prev.filter(u => u !== uye.uid) : [...prev, uye.uid])}>
                  <UyeSecAvatar uye={uye} uyeler={uyeler} />
                  <span className="chatUyeSecAd">{uye.ad} {uye.soyad}</span>
                  {sec && <span className="chatUyeSecCheck">✓</span>}
                </button>
              );
            })}
          </div>
        )}
        {secilen.length > 0 && (
          <button className="primaryButton" onClick={ekle}>
            {t("chat.addSelectedMembers", { count: secilen.length })}
          </button>
        )}
        <button className="darkSecondaryBtn" style={{marginTop:"8px"}} onClick={onKapat}> {t("common.cancel")}</button>
      </div>
    </div>
  );
}

/* ── Sohbet Üye Yönetim Paneli ── */
function OdaUyePaneli({ oda, uyeler, user, familyId, yoneticiMi, onKapat }) {
  const { t, lang } = useLanguage();
  const [uyeEkleAcik, setUyeEkleAcik] = useState(false);
  const [secilen, setSecilen]         = useState([]);
  const [yukleniyor, setYukleniyor]   = useState(false);
  const [detayUye, setDetayUye]       = useState(null);

  const [cikarilanUidler, setCikarilanUidler] = useState([]);
  const aktifUyeUidler = (oda?.uyeUidler || []).filter((uid) => !cikarilanUidler.includes(uid));
  const odaUyeleri     = uyeler.filter(u => aktifUyeUidler.includes(u.uid));
  const eklenebilecek  = uyeler.filter(u => !aktifUyeUidler.includes(u.uid));
  const kurucuMu       = (uid) => uid === oda?.olusturanUid;
  const yonetebilirMi  = oda?.olusturanUid === user?.uid || yoneticiMi;

  const uyeEkle = async () => {
    if (!secilen.length) return;
    setYukleniyor(true);
    try {
      for (const uid of secilen) {
        await odayaUyeEkleService({ familyId, roomId: oda.id, uyeUid: uid });
      }
      setSecilen([]);
      setUyeEkleAcik(false);
    } catch(e) { console.error(e); }
    finally { setYukleniyor(false); }
  };

  const uyeCikar = async (uid) => {
    if (!yonetebilirMi || kurucuMu(uid) || uid === user?.uid) return;
    const ok = window.confirm(lang === "en" ? "Remove this member from the room?" : "Bu üyeyi sohbet odasından çıkar? ");
    if (!ok) return;
    setYukleniyor(true);
    try {
      await odayaUyeCikarService({ familyId, roomId: oda.id, uyeUid: uid });
      setCikarilanUidler((prev) => prev.includes(uid) ? prev : [...prev, uid]);
      setSecilen((prev) => prev.filter((x) => x !== uid));
    } catch (e) {
      console.error(e);
      appAlert(e?.message || (lang === "en" ? "Member could not be removed." : "Üye çıkarılamadı."));
    } finally { setYukleniyor(false); }
  };

  return (
    <div className="modalOverlay" onClick={onKapat}>
      <div className="darkModal chatUyePanel" onClick={e => e.stopPropagation()}>
        <div className="darkModalHeader">
          <span className="darkModalTitle">💬 {oda?.ad} — {t("chat.roomMembers")}</span>
          <button className="darkModalClose" onClick={onKapat}>×</button>
        </div>

        {/* Mevcut üyeler */}
        <div className="chatUyePanelListe">
          {odaUyeleri.map(u => (
            <div key={u.uid} className="chatUyePanelSatir chatUyePanelSatir--interactive" role="button" tabIndex={0}
              onClick={() => setDetayUye(u)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setDetayUye(u); }}>
              <AvatarImg
                avatar={u.avatar || null}
                renk={uyeRenk(u.uid, uyeler)}
                initials={u.ad?.[0] || "?"}
                size={32}
                glow={false}
                radius="50%"
                className="chatUyeSecAv"
              
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
              <div className="chatUyePanelBilgi">
                <span className="chatUyePanelAd">{u.ad} {u.soyad}</span>
                <span className="chatUyePanelRol">{chatRoleLabel(lang, u.rol)}{kurucuMu(u.uid) ? ` · ${ct(lang, "Kurucu")}` : ""}</span>
              </div>
              {u.uid === user?.uid && <span className="chatUyePanelSen">{t("chat.me")}</span>}
              {yonetebilirMi && !kurucuMu(u.uid) && u.uid !== user?.uid && (
                <button type="button" className="chatUyeRemoveBtn" onClick={(event) => { event.stopPropagation(); uyeCikar(u.uid); }} disabled={yukleniyor}>−</button>
              )}
            </div>
          ))}
        </div>

        {/* Üye ekleme */}
        {yonetebilirMi && eklenebilecek.length > 0 && (
          <>
            {!uyeEkleAcik ? (
              <button className="chatUyePanelEkleBtn" onClick={() => setUyeEkleAcik(true)}>
                {t("chat.addNewMember")}
              </button>
            ) : (
              <>
                <p className="chatUyePanelEkleSub">{t("chat.selectPeopleToAdd")}</p>
                <div className="chatUyeSecGrid">
                  {eklenebilecek.map(uye => {
                    const sec = secilen.includes(uye.uid);
                    return (
                      <button key={uye.uid}
                        className={`chatUyeSecBtn ${sec ? "secili" : ""}`}
                        onClick={() => setSecilen(prev => sec ? prev.filter(u => u !== uye.uid) : [...prev, uye.uid])}>
                        <UyeSecAvatar uye={uye} uyeler={uyeler} />
                        <span className="chatUyeSecAd">{uye.ad} {uye.soyad}</span>
                        {sec && <span className="chatUyeSecCheck">✓</span>}
                      </button>
                    );
                  })}
                </div>
                {secilen.length > 0 && (
                  <button className="primaryButton" onClick={uyeEkle} disabled={yukleniyor}>
                    {yukleniyor ? t("chat.adding") : t("chat.addPeople", { count: secilen.length })}
                  </button>
                )}
                <button className="darkSecondaryBtn" style={{ marginTop: "8px" }} onClick={() => { setUyeEkleAcik(false); setSecilen([]); }}> {t("common.cancel")}</button>
              </>
            )}
          </>
        )}

        <button className="darkSecondaryBtn" style={{ marginTop: "12px" }} onClick={onKapat}> {t("common.close")}</button>
      </div>
      {detayUye && (
        <UyeModal
          seciliUye={detayUye}
          setSeciliUye={setDetayUye}
          yasHesapla={yasHesapla}
          yoneticiMi={false}
          ownerUid=""
        />
      )}
    </div>
  );
}

/* ── Oda listesi ── */
function OdaListesi({ odalar, odalarYuklendi = true, uyeler, user, familyId, tamAd, yoneticiMi, islemIzniVarMi, onOdaSec, onYeniOdaOlustur, isPremium = false, premiumConfig = {}, onPremiumLock, isBgInstance = false }) {
  const odaKartlariRef = useRef(null);
  const [odaListesiTasiyor, setOdaListesiTasiyor] = useState(false);

  // iOS'ta overflow:auto, içerik sığsa bile esneme/scroll hissi verebilir.
  // Kartların gerçek yüksekliğini ölçerek scroll'u sadece gerektiğinde açıyoruz.
  useEffect(() => {
    const liste = odaKartlariRef.current;
    if (!liste || !odalarYuklendi) {
      setOdaListesiTasiyor(false);
      return undefined;
    }

    let frameId = 0;

    const olc = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const elemanlar = Array.from(liste.children).filter(
          (el) => el instanceof HTMLElement
        );

        if (elemanlar.length === 0) {
          setOdaListesiTasiyor(false);
          return;
        }

        const sonEleman = elemanlar[elemanlar.length - 1];
        const icerikAltSiniri = sonEleman.offsetTop + sonEleman.offsetHeight;
        const listeYuksekligi = liste.clientHeight;

        // 2px tolerans alt-piksel yuvarlama kaynaklı sahte overflow'u engeller.
        setOdaListesiTasiyor(icerikAltSiniri > listeYuksekligi + 2);
      });
    };

    olc();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(olc) : null;

    resizeObserver?.observe(liste);
    Array.from(liste.children).forEach((child) => resizeObserver?.observe(child));
    window.addEventListener("resize", olc);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", olc);
    };
  }, [odalarYuklendi, odalar.length]);

  const { t, lang } = useLanguage();
  const [yeniOdaAcik, setYeniOdaAcik] = useState(false);
  const [odaAdi, setOdaAdi]           = useState("");
  const [seciliUyeler, setSeciliUyeler] = useState([]);
  const [yukleniyor, setYukleniyor]   = useState(false);
  const [silOnayId, setSilOnayId]     = useState(null);
  const [uyeEkleOdaId, setUyeEkleOdaId] = useState(null);
  const [swipeId, setSwipeId]         = useState(null);
  const swipeData                     = React.useRef({ startX: 0, startY: 0, id: null });
  const swipeClickEngelRef = React.useRef(false);

  const CHAT_ROL_SIRASI = ["Kişisel","Personal","Baba","Father","Anne","Mother","Çocuk","Child","Bebek","Baby","Amca","Uncle","Hala","Aunt","Teyze","Dayı","Dede","Grandfather","Nene","Grandmother","Diğer","Other"];
  const diger = uyeler
    .filter((u) => u.uid !== user.uid)
    .sort((a, b) => {
      const ai = CHAT_ROL_SIRASI.indexOf(a.rol || a.profilRol || "");
      const bi = CHAT_ROL_SIRASI.indexOf(b.rol || b.profilRol || "");
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

  const toggleUye = (uid) =>
    setSeciliUyeler((prev) =>
      prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]
    );

  const olustur = async () => {
    if (!odaAdi.trim()) return;
    setYukleniyor(true);
    try {
      const id = await odaOlusturService({ familyId, ad: odaAdi, uyeUidler: seciliUyeler, user, tamAd });
      setYeniOdaAcik(false); setOdaAdi(""); setSeciliUyeler([]);
      onYeniOdaOlustur(id, odaAdi.trim(), [...new Set([user.uid, ...seciliUyeler])]);
    } catch (e) { console.error(e); }
    finally { setYukleniyor(false); }
  };

  const sil = async (oda) => {
    try { await odaSilService({ familyId, roomId: oda.id }); }
    catch (e) { console.error(e); }
    setSilOnayId(null);
  };

  const silebilirMi = (oda) => oda.olusturanUid === user.uid || yoneticiMi;

  // Kullanıcıya özel okunmamış sayaç
  const okunmamis = (oda) => {
    if (!oda.lastMessage || !oda.lastMessageUid || oda.lastMessageUid === user.uid) return 0;
    const sayac = oda.okunmamisSayac || {};
    return sayac[user.uid] || 0;
  };

  // Toplam okunmamış (tüm odalarda)
  const toplamOkunmamis = odalar.reduce((a, o) => a + okunmamis(o), 0);

  return (
    <div className="chatOdaListesi">
      {/* isBgInstance: swipeBgLayer arka plan render'ında App.js Header ile çakışmaması için gizle */}
      {!isBgInstance && <div className="chatOdaListeHeader">
        <div>
          <div className="chatOdaListeTitle">
            💬 {ct(lang, "Sohbetler")}
            {toplamOkunmamis > 0 && <span className="chatOdaToplamBadge">{toplamOkunmamis}</span>}
          </div>
          <div className="chatOdaListeSub">{odalar.length} {odalar.length === 1 ? ct(lang, "sohbet") : ct(lang, "sohbetler")}</div>
        </div>
        <button className="chatOdaYeniBtn" onClick={() => {
          if (!isPremium && premiumConfig?.lockedFeatures?.cokluSohbetOdasi && odalar.length >= 1) {
            onPremiumLock && onPremiumLock(t("chat.premium.multiRoom"), t("chat.premium.multiRoomDesc"), [t("chat.premium.b1"), t("chat.premium.b2"), t("chat.premium.b3"), t("chat.premium.b4")]);
            return;
          }
          setYeniOdaAcik(true);
        }}>
          {t("chat.newRoomBtn")}
        </button>
      </div>}

      <div className="chatOdaListePanel">
        {!odalarYuklendi ? (
          <div className="chatOdaKartlari chatOdaSkeletonList" aria-hidden="true">
            {[1, 2, 3].map((i) => (
              <div key={i} className="chatOdaKart chatOdaSkeletonKart">
                <div className="chatOdaSkeletonAvatar" />
                <div className="chatOdaBilgi">
                  <div className="chatOdaSkeletonLine title" />
                  <div className="chatOdaSkeletonLine sub" />
                </div>
                <div className="chatOdaSkeletonTime" />
              </div>
            ))}
          </div>
        ) : odalar.length === 0 ? (
          <div className="chatEmpty">
            <img
              className="chatEmptyAsset"
              src={aileSohbetiAsset}
              alt=""
              aria-hidden="true"
              decoding="sync"
              loading="eager"
              fetchPriority="high"
              draggable="false"
            />
            <div className="chatEmptyText">{ct(lang, "Henüz sohbet yok")}</div>
            <div className="chatEmptySubtext">
              {ct(lang, "Yeni bir sohbet oluşturun, ailenizle güzel anları ve planları paylaşmaya başlayın.")}
            </div>
          </div>
        ) : (
          <div
            ref={odaKartlariRef}
            className={`chatOdaKartlari ${
              odaListesiTasiyor ? "chatOdaKartlariScrollable" : "chatOdaKartlariNoScroll"
            }`.trim()}
          >
          {odalar.map((oda) => {
            const badge = okunmamis(oda);
            const uyeler2 = uyeler.filter((u) => oda.uyeUidler?.includes(u.uid));
            const isSwipeOpen = swipeId === oda.id;
            return (
              <div key={oda.id} className="chatOdaKartWrap">
                {/* Arka plan — silme paneli */}
                <div className={`chatOdaSwipeBg ${isSwipeOpen ? "acik" : ""}`}>
                  {silebilirMi(oda) && (
                    <button
                      className="chatOdaSwipeSilBtn"
                      onClick={(e) => { e.stopPropagation(); setSilOnayId(oda.id); setSwipeId(null); }}
                    >
                      <span className="chatOdaSwipeSilIkon">🗑</span>
                      <span className="chatOdaSwipeSilYazi">{ct(lang, "Sil")}</span>
                    </button>
                  )}
                  <button
                    className="chatOdaSwipeUyeBtn"
                    onClick={(e) => { e.stopPropagation(); setUyeEkleOdaId(oda.id); setSwipeId(null); }}
                  >
                    <span className="chatOdaSwipeSilIkon">👥</span>
                    <span className="chatOdaSwipeSilYazi">{t("chat.member")}</span>
                  </button>
                </div>
                {/* Öne gelen kart */}
                <div
                  className="chatOdaKart"
                  style={{ transform: isSwipeOpen ? "translateX(-132px)" : "translateX(0)" }}
                  onClick={() => {
  if (swipeClickEngelRef.current) {
    swipeClickEngelRef.current = false;
    return;
  }

  if (isSwipeOpen) {
    setSwipeId(null);
    return;
  }

  onOdaSec(oda.id);
}}
                  onTouchStart={(e) => {
                    const t = e.touches[0];
                    swipeData.current = { startX: t.clientX, startY: t.clientY, id: oda.id };
                  }}

                  onTouchEnd={(e) => {
                    const dx = e.changedTouches[0].clientX - swipeData.current.startX;
                    const dy = Math.abs(e.changedTouches[0].clientY - swipeData.current.startY);
                    if (dy > 40) return;
                    if (dx < -55)     setSwipeId(oda.id);
                    else if (dx > 20) setSwipeId(null);
                  }}
                >
                <div className="chatOdaRoomAvatar" aria-hidden="true">
                  {(oda.avatarPreviewUrl || oda.avatarUrl) ? <CachedImage src={getFileUrl(oda.avatarPreviewUrl || oda.avatarUrl)} cacheKey={odaAvatarCacheKey(oda)} alt="" loading="eager" decoding="async" /> : <span>💬</span>}
                </div>
                <div className="chatOdaBilgi">
                  <div className="chatOdaAdi">{oda.ad}</div>
                  <div className="chatOdaSon">
                    {oda.lastMessage ? oda.lastMessage.slice(0, 45) : ""}
                  </div>
                </div>
                <div className="chatOdaSag">
                  <div className="chatOdaMemberStack" aria-hidden="true">
                    {uyeler2.slice(0, 3).map((u, i) => (
                      <AvatarImg
                        key={u.uid}
                        avatar={u.avatar || null}
                        renk={uyeRenk(u.uid, uyeler)}
                        initials={u.ad?.[0] || "?"}
                        size={24}
                        glow={false}
                        radius="50%"
                        className="chatOdaAv"
                        style={{ zIndex: 10 - i, marginLeft: i === 0 ? 0 : -9 }}
                      
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
                    ))}
                  </div>
                  {oda.lastMessageAt && <div className="chatOdaSaat">{saatStr(oda.lastMessageAt, lang)}</div>}
                  {badge > 0 && <div className="chatOdaBadge">{badge}</div>}
                </div>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {yeniOdaAcik && (
        <div className="modalOverlay" onClick={() => setYeniOdaAcik(false)}>
          <div className="darkModal" onClick={(e) => e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">💬 {ct(lang, "Yeni Sohbet")}</span>
              <button className="darkModalClose" onClick={() => setYeniOdaAcik(false)}>×</button>
            </div>
            <div className="inputLabelGroup" style={{ marginBottom: "14px" }}>
              <label className="darkLabel">{t("chat.roomName")}</label>
              <input className="darkInput" placeholder={t("chat.roomNamePlaceholder")}
                value={odaAdi} onChange={(e) => setOdaAdi(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && olustur()} autoFocus />
            </div>
            <label className="darkLabel" style={{ marginBottom: "8px", display: "block" }}>{t("chat.selectMembers")}</label>
            <div className="chatUyeSecGrid">
              {diger.map((uye) => {
                const secili = seciliUyeler.includes(uye.uid);
                return (
                  <button key={uye.uid} className={`chatUyeSecBtn ${secili ? "secili" : ""}`} onClick={() => toggleUye(uye.uid)}>
                    <UyeSecAvatar uye={uye} uyeler={uyeler} />
                    <span className="chatUyeSecAd">{uye.ad} {uye.soyad}</span>
                    {secili && <span className="chatUyeSecCheck">✓</span>}
                  </button>
                );
              })}
            </div>
            <button className="primaryButton" onClick={olustur} disabled={!odaAdi.trim() || yukleniyor}>
              {yukleniyor ? "⏳" : `✓ ${t("chat.newRoom")}`}
            </button>
            <button className="darkSecondaryBtn" style={{ marginTop: "8px" }} onClick={() => setYeniOdaAcik(false)}>{t("common.cancel")}</button>
          </div>
        </div>
      )}

      {/* Üye Ekleme Modalı */}
      {uyeEkleOdaId && (
        <UyeEkleModal
          odalar={odalar}
          uyeEkleOdaId={uyeEkleOdaId}
          uyeler={uyeler}
          familyId={familyId}
          onKapat={() => setUyeEkleOdaId(null)}
        />
      )}

      {silOnayId && (
        <div className="modalOverlay" onClick={() => setSilOnayId(null)}>
          <div className="darkModal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "340px" }}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">🗑 {ct(lang, "Sohbeti Sil")}</span>
              <button className="darkModalClose" onClick={() => setSilOnayId(null)}>×</button>
            </div>
            <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "16px" }}>
              {t("chat.deleteRoomConfirm")}
            </p>
            <button className="darkDangerBtn" onClick={() => sil(odalar.find((o) => o.id === silOnayId))}>{ct(lang, "Evet, Sil")}</button>
            <button className="darkSecondaryBtn" style={{ marginTop: "8px" }} onClick={() => setSilOnayId(null)}> {t("common.cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sohbet ekranı ── */
function SohbetEkrani({ oda, mesajlar, mesajYuklendi = false, uyeler, user, tamAd, familyId, yoneticiMi, islemIzniVarMi, setAktifSayfa, onGeri, transitionClass = "idle", isPremium = false, premiumConfig = {}, onPremiumLimitHit, onLoadOlder, hasMoreOlder = false, loadingOlder = false }) {
  const { t, lang } = useLanguage();
  const olderLoadAnchorRef = useRef(null);
  const olderLoadRequestedRef = useRef(false);
  // MADDE 2 (2. tur): Eski mesaj yüklemesi sırasında liste yüksekliği
  // (özellikle medyalar) değişir. Bu pencere boyunca hem ResizeObserver'ın
  // hem de "en alta sabitle" mantığının DEVREYE GİRMEMESİ gerekir; yoksa
  // ekran eskiye/alta zıplıyor. Bu ref o pencereyi işaretler.
  const olderLoadAktifRef = useRef(false);
  // DOM tabanlı çapa: yükleme öncesi en üstteki görünür mesajın kimliği ve
  // o mesajın viewport içindeki dikey konumu. scrollHeight deltası yerine
  // bu kullanılır — medya yüksekliği sonradan değişse bile konum kesin korunur.
  const olderLoadDomAnchorRef = useRef(null);
  const [metin, setMetin]             = useState("");
  const [aramaAcik, setAramaAcik]     = useState(false);
  const [aramaMetni, setAramaMetni]   = useState("");
  const [replyTo, setReplyTo]         = useState(null);
  const [editMsg, setEditMsg]         = useState(null);
  const [uploadPct, setUploadPct]     = useState(0);
  const [uploading, setUploading]     = useState(false);
  const [mediaPreparing, setMediaPreparing] = useState(false);
  const [attachMenuAcik, setAttachMenuAcik] = useState(false);
  const [emojiPanel, setEmojiPanel]   = useState(false);
  const [qaModal, setQaModal]         = useState(null);
  const [qaBaslik, setQaBaslik]       = useState("");
  // MADDE 4: Oylama oluşturma modalı
  const [oylamaModal, setOylamaModal]         = useState(false);
  const [oylamaSoru, setOylamaSoru]           = useState("");
  const [oylamaSecenekler, setOylamaSecenekler] = useState(["", ""]);
  const [oylamaCoklu, setOylamaCoklu]         = useState(false);
  const [oylamaAnonim, setOylamaAnonim]       = useState(false);
  const [oylamaGonderiliyor, setOylamaGonderiliyor] = useState(false);
  const [pinnedMsg, setPinnedMsg]     = useState(null);
  const [adDuzenle, setAdDuzenle]     = useState(false);
  const [yeniAd, setYeniAd]           = useState(oda?.ad || "");
  const [uyePanelAcik, setUyePanelAcik] = useState(false);
  const [odaBilgiPanelAcik, setOdaBilgiPanelAcik] = useState(false);
  const [odaBilgiSekme, setOdaBilgiSekme] = useState("media");
  const [keyboardState, setKeyboardState] = useState({ open: false, height: 0, visualHeight: 0, viewportTop: 0 });
  const [fotoOnizleme, setFotoOnizleme] = useState(null);
  const [fotoOnizlemeYuklendi, setFotoOnizlemeYuklendi] = useState(false);
  const [secureViewerItem, setSecureViewerItem] = useState(null);
  const [fotoIndiriliyor, setFotoIndiriliyor] = useState(false);
  const [previewZoom, setPreviewZoom] = useState({ scale: 1, x: 0, y: 0 });
  const [odaAvatarYukleniyor, setOdaAvatarYukleniyor] = useState(false);
  const [medyaOtoKaydet, setMedyaOtoKaydet] = useState(false);
  const [mesajlarKonumHazir, setMesajlarKonumHazir] = useState(false);
  const [asagiInButonuGoster, setAsagiInButonuGoster] = useState(false);
  // Ses kaydı
  const [sesKayitAktif,      setSesKayitAktif]      = useState(false);
  const [sesKayitSaniye,     setSesKayitSaniye]     = useState(0);
  const [sesKayitYukleniyor, setSesKayitYukleniyor] = useState(false);

  const chatPageRef  = useRef(null);
  const altRef        = useRef(null);
  const messagesRef   = useRef(null);
  const premiumHistoryPopupShownRef = useRef(false);
  const userHistoryScrollIntentRef = useRef(false);
  const bottomSettleTimersRef = useRef([]);
  const chatTouchRef = useRef({ startX: 0, startY: 0, moved: false });
  const chatInfoTouchRef = useRef({ startX: 0, startY: 0, moved: false });
  const previewTouchRef = useRef({ lastDistance: 0, lastScale: 1, mode: null, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const inputRef      = useRef(null);
  const inputMirrorRef = useRef(null);
  const dosyaRef      = useRef(null);
  const fotografRef   = useRef(null);
  const kameraRef     = useRef(null);
  // MADDE 7: Android'de kamera doğrudan açılsın diye AYRI input. Android 16'da
  // accept="image/*,video/*" + capture birlikte kullanılınca sistem kamerayı
  // değil medya seçiciyi açıyor. Sadece accept="image/*" + capture ile Android
  // doğrudan kamera uygulamasını başlatır. iOS/web bu ref'i kullanmaz.
  const kameraAndroidRef = useRef(null);
  const odaAvatarRef  = useRef(null);
  const mediaRecorderRef   = useRef(null);
  const audioChunksRef     = useRef([]);
  const audioDurationRef   = useRef(0);
  const sesKayitTimerRef   = useRef(null);

  const roomId = oda?.id;
  const fotoOnizlemeVideoMu = fotoOnizleme?.type === "video" || String(fotoOnizleme?.mimeType || "").startsWith("video/");
  const fotoOnizlemeHazir = fotoOnizlemeVideoMu || fotoOnizlemeYuklendi;

  useEffect(() => {
    try { setMedyaOtoKaydet(localStorage.getItem(`chatAutoSaveMedia.${familyId}.${roomId}`) === "1"); } catch { setMedyaOtoKaydet(false); }
  }, [familyId, roomId]);

  const chatRoomStyle = keyboardState.open
    ? {
        "--chat-keyboard-offset": `${keyboardState.height}px`,
        "--chat-visual-height": `${keyboardState.visualHeight || window.innerHeight}px`,
        "--chat-viewport-top": `${keyboardState.viewportTop || 0}px`,
      }
    : undefined;
  /* Chromium/Android, DOM başına ekleme sırasında görünür satırı momentum
     kaydırmasını kesmeden native olarak sabitleyebilir. iOS/WKWebView'da bu
     davranış güvenilir olmadığı için aşağıdaki tek-seferlik DOM çapası kalır. */
  const nativeScrollAnchorAktif = !isIOSPlatform()
    && typeof CSS !== "undefined"
    && typeof CSS.supports === "function"
    && CSS.supports("overflow-anchor: auto");

  const [optimisticMesajlar, setOptimisticMesajlar] = useState([]);
  useEffect(() => {
    if (fotoOnizleme) {
      setPreviewZoom({ scale: 1, x: 0, y: 0 });
      setFotoOnizlemeYuklendi(false);
    }
  }, [fotoOnizleme]);

  const previewGit = useCallback((direction) => {
    setFotoOnizleme((prev) => {
      const gallery = Array.isArray(prev?.gallery) ? prev.gallery : [];
      if (!prev || gallery.length <= 1) return prev;
      const current = Math.max(0, Math.min(gallery.length - 1, Number(prev.index || 0)));
      const next = Math.max(0, Math.min(gallery.length - 1, current + direction));
      if (next === current) return prev;
      setPreviewZoom({ scale: 1, x: 0, y: 0 });
      return { ...gallery[next], gallery, index: next };
    });
  }, []);

  const handlePreviewTouchStart = useCallback((e) => {
    if (!fotoOnizleme) return;
    if (e.touches?.length === 2 && !(fotoOnizleme.type === "video" || String(fotoOnizleme.mimeType || "").startsWith("video/"))) {
      const [a, b] = e.touches;
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      previewTouchRef.current = {
        ...previewTouchRef.current,
        mode: "pinch",
        lastDistance: distance,
        lastScale: previewZoom.scale,
        baseX: previewZoom.x || 0,
        baseY: previewZoom.y || 0,
      };
      return;
    }
    if (e.touches?.length === 1) {
      const t0 = e.touches[0];
      previewTouchRef.current = {
        ...previewTouchRef.current,
        mode: previewZoom.scale > 1 && !(fotoOnizleme.type === "video" || String(fotoOnizleme.mimeType || "").startsWith("video/")) ? "pan" : "swipe",
        startX: t0.clientX,
        startY: t0.clientY,
        baseX: previewZoom.x || 0,
        baseY: previewZoom.y || 0,
      };
    }
  }, [fotoOnizleme, previewZoom.scale, previewZoom.x, previewZoom.y]);

  const handlePreviewTouchMove = useCallback((e) => {
    if (!fotoOnizleme) return;
    const videoMu = fotoOnizleme.type === "video" || String(fotoOnizleme.mimeType || "").startsWith("video/");
    if (!videoMu && e.touches?.length === 2 && previewTouchRef.current.lastDistance > 0) {
      e.preventDefault();
      e.stopPropagation();
      const [a, b] = e.touches;
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const nextScale = Math.min(4, Math.max(1, previewTouchRef.current.lastScale * (distance / previewTouchRef.current.lastDistance)));
      setPreviewZoom((prev) => {
        if (nextScale <= 1.02) return { scale: 1, x: 0, y: 0 };
        return { ...prev, scale: nextScale };
      });
      return;
    }
    if (!videoMu && e.touches?.length === 1 && previewTouchRef.current.mode === "pan" && previewZoom.scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      const t0 = e.touches[0];
      const dx = t0.clientX - previewTouchRef.current.startX;
      const dy = t0.clientY - previewTouchRef.current.startY;
      const maxX = Math.max(80, window.innerWidth * 0.55 * (previewZoom.scale - 1));
      const maxY = Math.max(80, window.innerHeight * 0.45 * (previewZoom.scale - 1));
      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
      setPreviewZoom((prev) => ({
        ...prev,
        x: clamp((previewTouchRef.current.baseX || 0) + dx, -maxX, maxX),
        y: clamp((previewTouchRef.current.baseY || 0) + dy, -maxY, maxY),
      }));
    }
  }, [fotoOnizleme, previewZoom.scale]);

  const handlePreviewTouchEnd = useCallback((e) => {
    const t1 = e.changedTouches?.[0];
    const mode = previewTouchRef.current.mode;
    if (t1 && mode === "swipe" && previewZoom.scale <= 1.02) {
      const dx = t1.clientX - previewTouchRef.current.startX;
      const dy = Math.abs(t1.clientY - previewTouchRef.current.startY);
      if (Math.abs(dx) > 58 && dy < 54) {
        e.preventDefault?.();
        e.stopPropagation?.();
        previewGit(dx < 0 ? 1 : -1);
      }
    }
    previewTouchRef.current = { ...previewTouchRef.current, mode: null, lastDistance: 0 };
  }, [previewGit, previewZoom.scale]);

  const mesajTarihMs = useCallback((value) => {
    if (!value) return 0;
    if (typeof value === "number") return value;
    if (typeof value?.toDate === "function") return value.toDate().getTime();
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const ayniOptimisticMesajMi = useCallback((opt, real) => {
    if (!opt || !real || real.id === opt.id) return false;
    if ((real.uid || real.gonderenUid) !== opt.uid) return false;
    if ((real.type || "text") !== (opt.type || "text")) return false;
    if (String(real.metin || "").trim() !== String(opt.metin || "").trim()) return false;
    // MADDE 2: Medya mesajlarında metin boş olduğu için, 2 dakika içinde
    // gönderilen iki farklı fotoğraf birbirini temizleyebiliyordu. Dosya adı
    // her ikisinde de varsa eşleşme bunun üzerinden yapılır.
    const optDosyaAdi = String(opt.fileName || "").trim();
    const realDosyaAdi = String(real.fileName || real.dosyaAdi || "").trim();
    if (optDosyaAdi && realDosyaAdi && optDosyaAdi !== realDosyaAdi) return false;

    const optMs = mesajTarihMs(opt.createdAt);
    const realMs = mesajTarihMs(real.createdAt);
    if (optMs && realMs && Math.abs(realMs - optMs) > 120000) return false;

    const optReplyId = opt.replyTo?.id || opt.replyTo?.mesajId || "";
    const realReplyId = real.replyTo?.id || real.replyTo?.mesajId || "";
    return !optReplyId || !realReplyId || optReplyId === realReplyId;
  }, [mesajTarihMs]);

  useEffect(() => {
    setOptimisticMesajlar([]);
  }, [roomId]);

  useEffect(() => {
    if (!optimisticMesajlar.length) return;
    setOptimisticMesajlar((prev) => prev.filter((opt) => !mesajlar.some((real) => ayniOptimisticMesajMi(opt, real))));
  }, [mesajlar, optimisticMesajlar.length, ayniOptimisticMesajMi]);

  const gorunenMesajlar = useMemo(() => {
    if (!optimisticMesajlar.length) return mesajlar;
    const aktifOptimistic = optimisticMesajlar.filter((opt) => !mesajlar.some((real) => ayniOptimisticMesajMi(opt, real)));
    if (!aktifOptimistic.length) return mesajlar;
    return [...mesajlar, ...aktifOptimistic].sort((a, b) => mesajTarihMs(a.createdAt) - mesajTarihMs(b.createdAt));
  }, [mesajlar, optimisticMesajlar, ayniOptimisticMesajMi, mesajTarihMs]);

  const chatBildirimiGonder = useCallback(async () => {
    // Chat bildirimleri backend /messages endpointinde, aktif oda kontrolüyle üretiliyor.
    // Frontend'den ayrıca bildirim yazmak active-session kontrolünü bypass edip
    // kullanıcı aynı chat odasındayken native bildirim gelmesine sebep oluyordu.
  }, []);

  const alintiyaGit = useCallback((reply) => {
    if (!reply) return;

    const normalized = (v) => String(v || "").trim().toLowerCase();
    const replyText = normalized(reply.metin || reply.text || reply.message || "");
    const replyUid = reply.uid || reply.userUid || "";
    const replyAd = normalized(reply.ad || reply.name || "");

    let hedefId =
      reply.id ||
      reply.mesajId ||
      reply.messageId ||
      mesajlar.find((m) => {
        if (!m || m.deletedAt) return false;
        const mText = normalized(m.metin || "");
        const mAd = normalized(m.ad || "");
        const textMatch = replyText && (mText === replyText || mText.includes(replyText) || replyText.includes(mText));
        const uidMatch = replyUid ? m.uid === replyUid : true;
        const adMatch = replyAd ? mAd === replyAd : true;
        return textMatch && uidMatch && adMatch;
      })?.id;

    let hedef = hedefId ? document.getElementById(`msg-${hedefId}`) : null;

    if (!hedef && replyText && messagesRef.current) {
      const adaylar = Array.from(messagesRef.current.querySelectorAll("[id^='msg-']"));
      hedef = adaylar.find((el) => normalized(el.textContent).includes(replyText));
    }

    if (!hedef) return;

    requestAnimationFrame(() => {
      hedef.scrollIntoView({ behavior: "smooth", block: "center" });
      hedef.classList.add("chatQuotedMessageFlash");
      window.setTimeout(() => hedef.classList.remove("chatQuotedMessageFlash"), 1800);
    });
  }, [mesajlar, gorunenMesajlar]);

  // Sohbet ilk açıldığında direkt en son mesaja konumlanmak için kullanılır.
  // İlk açılışta smooth kullanmıyoruz; yoksa konuşmaları yukarıdan aşağı kaydırıyormuş gibi gösteriyor.
  const ilkKaydirmaYapildiRef = useRef(false);
  const oncekiRoomIdRef       = useRef(null);
  const acilmisOdalarRef      = useRef(new Set()); // Daha önce açılmış odalar — tekrar girişte bekleme yok
  // Kullanıcının en altta olup olmadığını kontrol et (WhatsApp mantığı)
  const kullaniciEnAltaMiRef  = useRef(true);
  const oncekiMesajSayisiRef  = useRef(0);
  // MADDE 6: "Yeni mesaj geldi mi?" kontrolü artık mesaj SAYISINA değil,
  // listenin SON MESAJ ID'sine bakar. Eski mesajlar başa eklendiğinde de sayı
  // arttığı için, önceki sürüm bunu "yeni mesaj" sanıp ekranı en alta atıyordu.
  const sonMesajIdRef         = useRef(null);

  const draftKey = roomId && user?.uid && familyId ? `familyPlanner.chatDraft.${familyId}.${roomId}.${user.uid}` : null;
  const draftKeyRef = useRef(null);

  const taslakOku = useCallback((key) => {
    if (!key) return "";
    try {
      return readSensitiveItem(key) || "";
    } catch (error) {
      console.warn(t("chat.draftReadWarn"), error);
      return "";
    }
  }, [t]);

  const taslakKaydet = useCallback((key, value) => {
    if (!key) return;
    try {
      const temizDeger = value || "";
      if (temizDeger.trim()) writeSensitiveItem(key, temizDeger);
      else removeSensitiveItem(key);
    } catch (error) {
      console.warn(t("chat.draftSaveWarn"), error);
    }
  }, [t]);

  /* ══════════════════════════════════════════════════════════════════
     MADDE 3 — ÇOK SATIRLI MESAJ YAZMA ALANI

     Önceki sürümde girdi bir <input> idi; yapısı gereği tek satırdır ve uzun
     mesaj yazarken yalnızca son birkaç kelime görünüyordu. Artık <textarea>
     kullanılıyor ve yükseklik içeriğe göre 1 → 3 satır arasında büyüyor.
     3 satırı aşan metinler alanın KENDİ İÇİNDE kaydırılarak okunabiliyor;
     sohbet ekranının yerleşimi bozulmuyor (mesaj listesi flex ile kısalır ve
     aşağıdaki ResizeObserver en-alt konumunu korur).

     Aynı alan mesaj DÜZENLEME modunda da kullanıldığı için düzenleme de
     otomatik olarak çok satırlı ve kullanıcı dostu hale gelir.
     ══════════════════════════════════════════════════════════════════ */
  const inputYuksekligiAyarla = useCallback(() => {
    const el = inputRef.current;
    const ayna = inputMirrorRef.current;
    if (!el || !ayna) return;
    try {
      const stil = window.getComputedStyle(el);
      const satirYuksekligi = parseFloat(stil.lineHeight) || 20;
      const ustBosluk = parseFloat(stil.paddingTop) || 0;
      const altBosluk = parseFloat(stil.paddingBottom) || 0;
      const minYukseklik = Math.round(satirYuksekligi + ustBosluk + altBosluk);
      const maxYukseklik = Math.round(satirYuksekligi * CHAT_INPUT_MAX_SATIR + ustBosluk + altBosluk);

      /* iOS, odaklanmış textarea'nın yüksekliği ölçülürken `height:auto`
         yapıldığında native caret katmanını farklı bir geometriden çizebiliyor.
         Videodaki imlecin harf yazınca yukarı, silince aşağı atlamasının kesin
         nedeni buydu. Ölçümü görünmez aynada yapıp odaklı textarea'ya yalnızca
         GERÇEKTEN satır sayısı değiştiğinde tek bir sabit yükseklik uygularız. */
      ayna.style.width = `${el.clientWidth}px`;
      ayna.textContent = `${el.value || "\u200b"}\u200b`;
      const icerikYuksekligi = Math.max(minYukseklik, ayna.scrollHeight);
      const hedef = Math.min(icerikYuksekligi, maxYukseklik);
      const oncekiYukseklik = parseFloat(el.style.height || stil.height) || minYukseklik;
      const yeniOverflow = icerikYuksekligi > maxYukseklik + 1 ? "auto" : "hidden";

      if (Math.abs(oncekiYukseklik - hedef) > 0.5) {
        el.style.height = `${hedef}px`;
      }
      if (el.style.overflowY !== yeniOverflow) {
        el.style.overflowY = yeniOverflow;
      }

      /* MADDE 3 (2. tur) — SATIR ARTINCA SON MESAJ YARIM KALMASIN
         ─────────────────────────────────────────────────────────────
         Textarea 1→2→3 satıra büyüdüğünde yazma alanı yükselir ve mesaj
         listesinin görünür yüksekliği küçülür. Kullanıcı en alttaysa, bu
         küçülme sonrası son mesaj klavyenin/yazma alanının arkasında yarım
         kalıyordu (özellikle Android'de, çünkü viewport değişmediği için
         Android klavye efekti tetiklenmiyor).

         Yükseklik GERÇEKTEN değiştiyse ve kullanıcı en alttaysa, listeyi
         yeniden en alta sabitle. Layout güncellendikten sonra çalışması için
         requestAnimationFrame kullanılır. Yalnızca yerel ölçüm; sunucuya
         istek yok. */
      if (Math.abs(oncekiYukseklik - hedef) > 0.5 && kullaniciEnAltaMiRef.current) {
        requestAnimationFrame(() => {
          const liste = messagesRef.current;
          if (liste) liste.scrollTop = liste.scrollHeight - liste.clientHeight;
        });
      }

    } catch (_) {
      // Hesaplama yapılamazsa alan varsayılan tek satır yüksekliğinde kalır.
    }
  }, []);

  const metinDegistir = useCallback((value) => {
    setMetin(value);
    if (!editMsg && draftKeyRef.current) {
      taslakKaydet(draftKeyRef.current, value);
    }
  }, [editMsg, taslakKaydet]);

  // Metin her değiştiğinde (yazarken, taslak yüklenince, gönderimden sonra
  // temizlenince, düzenleme başlayınca) yükseklik yeniden hesaplanır.
  useLayoutEffect(() => {
    inputYuksekligiAyarla();
  }, [metin, editMsg?.id, roomId, inputYuksekligiAyarla]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => inputYuksekligiAyarla());
    observer.observe(el);
    return () => observer.disconnect();
  }, [inputYuksekligiAyarla]);

  useEffect(() => {
    draftKeyRef.current = draftKey;
    if (editMsg) return;
    setMetin(taslakOku(draftKey));
  }, [draftKey, editMsg?.id, taslakOku]); // eslint-disable-line

  useEffect(() => {
    const mevcutKey = draftKey;
    if (!mevcutKey || editMsg) return;

    const kaydet = () => {
      taslakKaydet(mevcutKey, inputRef.current?.value ?? metin);
    };

    window.addEventListener("pagehide", kaydet);
    window.addEventListener("beforeunload", kaydet);
    document.addEventListener("visibilitychange", kaydet);

    return () => {
      kaydet();
      window.removeEventListener("pagehide", kaydet);
      window.removeEventListener("beforeunload", kaydet);
      document.removeEventListener("visibilitychange", kaydet);
    };
  }, [draftKey, editMsg, metin, taslakKaydet]);

  useEffect(() => {
    const pinned = [...gorunenMesajlar].reverse().find((m) => m.pinned && !m.deletedAt);
    setPinnedMsg(pinned || null);
  }, [gorunenMesajlar]);

  const temizleBottomSettleTimers = useCallback(() => {
    bottomSettleTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    bottomSettleTimersRef.current = [];
  }, []);

  // Kullanıcı en altta mı? (150px tolerans — WhatsApp mantığı)
  const enAltaMiKontrol = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  /* ══════════════════════════════════════════════════════════════════
     MADDE 2 + 6 — TEK KAYNAKLI KAYDIRMA MOTORU

     ÖNCEKİ SORUN:
     Her yerde `altRef.scrollIntoView({block:"end"})` çağrılıyordu. Bu çağrı
     yalnızca mesaj listesini değil ÜST kapsayıcıları da kaydırabildiği için
     iOS/Android WebView'de sohbet ekranı zıplıyordu. Ayrıca gönderim sonrası
     konum, sabit gecikmelerle (0/80/220/520 ms) tahmin ediliyordu; layout
     bu sürelerden önce ya da sonra oturduğunda mesaj yarım kalıyordu.

     ÇÖZÜM:
     Sadece mesaj listesinin kendi `scrollTop` değeri ayarlanır ve layout
     oturana kadar birkaç ANIMASYON KARESİ boyunca sabitlenir. Zamanlayıcı
     tahmini yok, üst kapsayıcı kaydırması yok. Sunucuya hiçbir ek istek
     üretmez — tamamen yerel bir yerleşim düzeltmesidir.
     ══════════════════════════════════════════════════════════════════ */

  // Anında ve kesin: listeyi en alta sabitler (gönderim, medya, klavye).
  const enAltaSabitle = useCallback(() => {
    const uygula = () => {
      const el = messagesRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight - el.clientHeight;
      kullaniciEnAltaMiRef.current = true;
    };
    uygula();
    requestAnimationFrame(() => {
      uygula();
      requestAnimationFrame(uygula);
    });
    setAsagiInButonuGoster(false);
  }, []);

  // Kullanıcıya görünür kaydırma (aşağı in butonu, yeni mesaj bildirimi).
  const sohbetiEnAltaKaydir = useCallback((behavior = "auto") => {
    const el = messagesRef.current;
    if (!el) return;
    if (behavior === "smooth") {
      const hedef = el.scrollHeight - el.clientHeight;
      try {
        el.scrollTo({ top: hedef, behavior: "smooth" });
      } catch (_) {
        el.scrollTop = hedef;
      }
      kullaniciEnAltaMiRef.current = true;
      setAsagiInButonuGoster(false);
      return;
    }
    enAltaSabitle();
  }, [enAltaSabitle]);

  /* ── ANDROID: Klavye açılınca sohbeti en alta kaydır ──────────────────
     Android'de güvenli alan ve klavye boşluğu native tarafta WebView'in üst
     kabına dolgu verilerek sağlanıyor (MainActivity.setupInsets). Klavye
     açıldığında görünür alan küçülüyor, ancak mesaj listesi kendi kaydırma
     konumunu koruduğu için SON MESAJ klavyenin arkasında kalıyordu.

     Burada görünür alan değişimini (visualViewport / window resize) dinleyip,
     kullanıcı zaten en alttaysa listeyi tekrar en alta kaydırıyoruz. Böylece
     klavye açılınca son mesaj her zaman görünür kalır — WhatsApp davranışı.

     Kullanıcı yukarı kaydırıp eski mesajları okuyorsa (kullaniciEnAltaMiRef
     false) konumu ZORLA değiştirmiyoruz; okuduğu yeri kaybetmesin.

     Yalnızca Android'de çalışır: iOS kendi visualViewport tabanlı klavye
     akışını kullanmaya devam eder, oraya hiç dokunulmaz. */
  useEffect(() => {
    if (!isAndroidPlatform()) return undefined;
    if (!roomId) return undefined;

    let zamanlayici = null;

    const gorunurAlanDegisti = () => {
      // Layout oturduktan sonra kaydır (klavye animasyonu ~150-250ms sürer)
      if (zamanlayici) clearTimeout(zamanlayici);
      zamanlayici = setTimeout(() => {
        if (kullaniciEnAltaMiRef.current) {
          sohbetiEnAltaKaydir("auto");
        }
      }, 60);
    };

    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", gorunurAlanDegisti);
    window.addEventListener("resize", gorunurAlanDegisti);

    return () => {
      if (zamanlayici) clearTimeout(zamanlayici);
      if (vv) vv.removeEventListener("resize", gorunurAlanDegisti);
      window.removeEventListener("resize", gorunurAlanDegisti);
    };
  }, [roomId, sohbetiEnAltaKaydir]);

  useEffect(() => {
    if (!roomId) return;

    // Farklı sohbet odasına geçildiyse ilk kaydırmayı tekrar "auto" yap.
    if (oncekiRoomIdRef.current !== roomId) {
      oncekiRoomIdRef.current = roomId;
      // Daha önce açılmış odada ilk kaydırma zaten yapıldı — tekrar bekleme yok
      const odaDahaOnceAcildi = acilmisOdalarRef.current.has(roomId);
      ilkKaydirmaYapildiRef.current = odaDahaOnceAcildi;
      premiumHistoryPopupShownRef.current = false;
      userHistoryScrollIntentRef.current = false;
      kullaniciEnAltaMiRef.current = true;
      oncekiMesajSayisiRef.current = 0;
      sonMesajIdRef.current = null; // MADDE 6: yeni odada ilk hesap "yeni mesaj" sayılmaz
      setMesajlarKonumHazir(odaDahaOnceAcildi); // Daha önce açıldıysa direk hazır
      temizleBottomSettleTimers();
    }

    if (gorunenMesajlar.length === 0) {
      setMesajlarKonumHazir(true);
      return;
    }

    const ilkAcilis = !ilkKaydirmaYapildiRef.current;

    /* MADDE 6 — "EN ALTA ATMA" HATASININ KÖK NEDENİ VE ÇÖZÜMÜ
       ───────────────────────────────────────────────────────────────
       ÖNCEKİ KOD:
         const yeniMesajVar = gorunenMesajlar.length > oncekiMesajSayisiRef.current;
         if (benimMesajim || enAltta) sohbetiEnAltaKaydir("smooth");

       Kullanıcı geçmişe doğru kaydırdığında premium sayfalama devreye girip
       ESKİ mesajları listenin BAŞINA ekliyor. Bu da uzunluğu artırdığı için
       "yeni mesaj var" sanılıyor; listenin son mesajı kullanıcının kendi
       mesajıysa (`benimMesajim`) ekran ZORLA en alta atılıyordu. Kullanıcının
       tarif ettiği "yukarı kaydırırken bazen en alta atıyor" davranışı buydu.

       YENİ KOD: Sadece listenin SONUNA gerçekten yeni bir mesaj eklendiğinde
       (son mesaj ID'si değiştiğinde) kaydırılır. Başa ekleme hiçbir şekilde
       kaydırma tetiklemez; okuma konumu korunur.
       ─────────────────────────────────────────────────────────────── */
    const sonMesajObj = gorunenMesajlar[gorunenMesajlar.length - 1];
    const sonMesajId = sonMesajObj?.id ?? null;
    const oncekiSonId = sonMesajIdRef.current;
    const yeniMesajVar = !!sonMesajId && oncekiSonId !== null && sonMesajId !== oncekiSonId;
    sonMesajIdRef.current = sonMesajId;
    oncekiMesajSayisiRef.current = gorunenMesajlar.length;

    if (ilkAcilis) {
      // İlk açılış: doğrudan en alta git, titreme olmadan
      temizleBottomSettleTimers();
      enAltaSabitle();
      // Resimler ve layout oturana kadar mesajları gizli tut, sonra göster
      acilmisOdalarRef.current.add(roomId); // Bu odayı açılmış olarak işaretle
      [80, 200].forEach((ms) => {
        const timerId = setTimeout(enAltaSabitle, ms);
        bottomSettleTimersRef.current.push(timerId);
      });
      const readyTimerId = setTimeout(() => {
        enAltaSabitle();
        setMesajlarKonumHazir(true);
      }, 350);
      bottomSettleTimersRef.current.push(readyTimerId);
      ilkKaydirmaYapildiRef.current = true;
      return;
    }

    // MADDE 2 (2. tur): Eski mesaj yükleme penceresindeyken, listeye eklenen
    // mesajlar BAŞA gelir; buradaki "sona yeni mesaj" mantığı çalışmamalı.
    // Konum korumasını older-load telafisi (useLayoutEffect) yönetir.
    if (olderLoadAktifRef.current) return;

    // Listenin SONUNA yeni mesaj eklendi mi?
    if (yeniMesajVar) {
      const benimMesajim = sonMesajObj?.uid === user?.uid;
      const enAltta = kullaniciEnAltaMiRef.current || enAltaMiKontrol();

      /* MADDE 6 — İKİNCİ KORUMA: KULLANICI GEÇMİŞTEYSE ASLA ZORLAMA
         ─────────────────────────────────────────────────────────────
         Önceki sürümde `benimMesajim` tek başına kaydırmayı tetikliyordu.
         Bu, geçmişi okurken de ekranı en alta atabiliyordu. Örneğin mesaj
         gönderip yukarı kaydıran kullanıcıda, geçici (optimistic) mesaj
         sunucudan gelen gerçek mesajla değiştiğinde "yeni mesaj" görünür
         ve ekran aşağı fırlardı.

         Artık kullanıcı en altta DEĞİLSE hiçbir koşulda kaydırma yapılmaz;
         yalnızca "aşağı in" butonu görünür (handleMessagesScroll yönetir).
         Kendi mesajını gönderme anındaki kaydırma zaten gonder() ve
         medyaYukle() içinde açıkça yapılıyor.
         ───────────────────────────────────────────────────────────── */
      if (!enAltta) return;

      if (benimMesajim) {
        // Kendi mesajım: konumu kesin olarak sabitle (medya yükseklikleri
        // sabit olduğu için tek seferde doğru yere oturur).
        enAltaSabitle();
      } else {
        sohbetiEnAltaKaydir("smooth");
      }
    }
  }, [roomId, gorunenMesajlar.length, mesajYuklendi, temizleBottomSettleTimers, sohbetiEnAltaKaydir, enAltaSabitle, enAltaMiKontrol, user?.uid]); // eslint-disable-line

  useEffect(() => () => temizleBottomSettleTimers(), [temizleBottomSettleTimers]);

  /* ══════════════════════════════════════════════════════════════════
     MADDE 3 + 2 — YERLEŞİM DEĞİŞİMİNDE EN-ALT KONUMUNUN KORUNMASI

     Yazma alanı 1→2→3 satıra büyüdüğünde mesaj listesinin YÜKSEKLİĞİ küçülür.
     Tarayıcı bu durumda scrollTop'u aynen bıraktığı için son mesaj kısmen
     kayboluyordu. ResizeObserver ile liste yüksekliği her değiştiğinde,
     kullanıcı zaten en alttaysa konum yeniden sabitlenir.

     Kullanıcı geçmişi okuyorsa (kullaniciEnAltaMiRef=false) hiçbir şey yapılmaz.
     Tamamen yerel bir düzeltmedir; sunucuya hiçbir istek üretmez.
     ══════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const el = messagesRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    let ilkOlcum = true;
    const gozlemci = new ResizeObserver(() => {
      if (ilkOlcum) { ilkOlcum = false; return; }
      // MADDE 2 (2. tur): Eski mesaj yükleme penceresi boyunca bu gözlemci
      // DEVRE DIŞI — konum korumasını older-load telafisi yönetir. Aksi halde
      // ikisi çakışıp ekran zıplıyordu.
      if (olderLoadAktifRef.current) return;
      // Yalnızca kullanıcı zaten en alttaysa sabitle (geçmişi okuyanın yeri korunur).
      if (!kullaniciEnAltaMiRef.current) return;
      el.scrollTop = el.scrollHeight - el.clientHeight;
    });

    gozlemci.observe(el);
    return () => gozlemci.disconnect();
  }, [roomId]);

  /* Okunmamış mesaj varsa oda başına tek toplu read isteği gönderilir.
     Backend mesaj readBy alanlarını ve oda sayacını birlikte günceller;
     değişiklik yoksa rooms/message refresh üretmez. */
  useEffect(() => {
    if (!user?.uid || !familyId || !roomId) return;

    const okunmamisMesajIds = mesajlar.filter(
      (msg) => msg.uid !== user.uid && !(msg.readBy || []).includes(user.uid) && !msg.deletedAt
    ).map((msg) => msg.id).filter(Boolean);

    if (okunmamisMesajIds.length > 0) {
      odayiOkunduYap({ familyId, roomId, userUid: user.uid, messageIds: okunmamisMesajIds }).catch(() => {});
    }
  }, [mesajlar, user?.uid, familyId, roomId]);

  const gonder = async () => {
    const sendText = metin.trim();

    if (!sendText && !editMsg) return;
    if (!islemIzniVarMi("sendMessage")) return;

    const replySnapshot = replyTo ? {
      id: replyTo.id,
      mesajId: replyTo.id,
      uid: replyTo.uid,
      ad: replyTo.ad,
      metin: replyTo.metin || "",
      type: replyTo.type || "text",
    } : null;

    const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Mesajı backend cevabını beklemeden kendi ekranında göster.
    // Backend/notification akışı yine normal çalışır; gerçek mesaj gelince bu geçici kayıt otomatik temizlenir.
    if (!editMsg) {
      const optimisticMsg = {
        id: tempId,
        localOnly: true,
        pending: true,
        type: "text",
        metin: sendText,
        uid: user?.uid,
        gonderenUid: user?.uid,
        ad: tamAd || user?.displayName || user?.email || "",
        createdAt: new Date().toISOString(),
        readBy: user?.uid ? [user.uid] : [],
        replyTo: replySnapshot,
      };
      setOptimisticMesajlar((prev) => [...prev, optimisticMsg]);
    }

    setMetin("");
    if (draftKey) removeSensitiveItem(draftKey);
    if (!editMsg) setReplyTo(null);

    // MADDE 2: Mesaj gönderildiği anda liste kesin olarak en alta sabitlenir.
    // (Önceki sürümdeki scrollIntoView + setTimeout(80) ikilisi kaldırıldı.)
    enAltaSabitle();

    try {
      if (editMsg) {
        await mesajDuzenleService({
          familyId,
          roomId,
          mesajId: editMsg.id,
          yeniMetin: sendText,
        });

        setEditMsg(null);
      } else {
        const yeniMesaj = await mesajGonderService({
          familyId,
          roomId,
          metin: sendText,
          user,
          tamAd,
          replyTo: replySnapshot,
        });

        chatBildirimiGonder(yeniMesaj, sendText);
      }

      inputRef.current?.focus();
      enAltaSabitle();
    } catch (err) {
      console.error(t("chat.messageSendError"), err);

      if (!editMsg) {
        setOptimisticMesajlar((prev) => prev.filter((m) => m.id !== tempId));
      }

      setMetin(sendText);
      if (!editMsg) setReplyTo(replySnapshot);
      if (draftKey) writeSensitiveItem(draftKey, sendText);
    }
  };


  const medyaSec = async (source = "library") => {
    let bridge = window?.webkit?.messageHandlers?.aileNativeBridge;
    if (!bridge && source === "camera") {
      await new Promise((r) => setTimeout(r, 350));
      bridge = window?.webkit?.messageHandlers?.aileNativeBridge;
    }
    if (!bridge) {
      // ANDROID (MADDE 7): iOS native köprüsü yok. Kamera istendiğinde,
      // accept="image/*,video/*"+capture birlikte Android 16'da medya seçici
      // açtığı için doğrudan kamerayı başlatan ayrı input'u (accept="image/*"
      // +capture) kullanıyoruz. Galeri/video seçimi mevcut fotografRef ile devam.
      if (source === "camera") {
        if (isAndroidPlatform() && kameraAndroidRef.current) {
          kameraAndroidRef.current.click();
        } else {
          kameraRef.current?.click();
        }
      } else {
        fotografRef.current?.click();
      }
      return;
    }
    try {
      setAttachMenuAcik(false);
      setMediaPreparing(true);
      setUploadPct(0);
      const nativeFile = await nativeChatMediaSec({ lang, source, allowVideo: true, allowEditing: false });
      setMediaPreparing(false);
      setUploadPct(0);
      if (nativeFile) await medyaYukle(nativeFile);
    } catch (err) {
      setMediaPreparing(false);
      setUploading(false);
      setUploadPct(0);
      console.error("native media picker error", err);
      appAlert(lang === "en"
        ? "The video could not be prepared. Please try a shorter video."
        : "Video hazırlanamadı. Lütfen daha kısa bir video deneyin.");
    }
  };

  const medyaYukle = async (dosya) => {
    if (!dosya || !islemIzniVarMi("sendMedia")) return;

    const isVideoFile = dosya?.mediaTypeHint === "video" || String(dosya.type || "").startsWith("video/") || /\.(mov|mp4|m4v|webm)$/i.test(dosya.name || "");
    const maxVideoBytes = 50 * 1024 * 1024;
    if (isVideoFile && Number(dosya.size || 0) > maxVideoBytes) {
      appAlert(lang === "en" ? "Videos can be up to 50 MB." : "Videolar en fazla 50 MB olabilir.");
      return;
    }

    setUploading(true); setUploadPct(0);

    /* MADDE 2 — MEDYA GÖNDERİMİNDE ZIPLAMANIN ÖNLENMESİ
       ─────────────────────────────────────────────────────────────
       Önceki sürümde medya mesajı ancak sunucu cevabı + bir sonraki
       yenilemeden sonra listeye giriyordu; bu arada [0,80,220,520] ms'lik
       tahmini zamanlayıcılarla kaydırma yapılmaya çalışılıyordu. Mesaj
       geç geldiğinde kaydırma boşa düşüyor, sonra mesaj eklenince ekran
       zıplıyordu.

       Artık medya, gönderilir gönderilmez GERÇEK MESAJLA BİREBİR AYNI
       sabit kutuda (176×268) listeye ekleniyor. Yükseklik baştan kesin
       olduğu için sunucu cevabı geldiğinde tek piksel bile kaymıyor.
       ───────────────────────────────────────────────────────────── */
    const medyaTempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const gorselMi = String(dosya.type || "").startsWith("image/");
    const optimistikTip = gorselMi ? "image" : (isVideoFile ? "video" : "file");
    let onizlemeUrl = "";
    try {
      if (gorselMi) onizlemeUrl = URL.createObjectURL(dosya);
    } catch (_) { onizlemeUrl = ""; }

    const optimistikMedya = {
      id: medyaTempId,
      localOnly: true,
      pending: true,
      type: optimistikTip,
      metin: "",
      uid: user?.uid,
      gonderenUid: user?.uid,
      ad: tamAd || user?.displayName || user?.email || "",
      createdAt: new Date().toISOString(),
      readBy: user?.uid ? [user.uid] : [],
      fileName: dosya.name || "",
      fileSize: Number(dosya.size || 0),
      mimeType: dosya.type || "",
      localPreviewUrl: onizlemeUrl || "pending",
      replyTo: replyTo ? { id: replyTo.id, mesajId: replyTo.id, uid: replyTo.uid, ad: replyTo.ad, metin: replyTo.metin || "", type: replyTo.type || "text" } : null,
    };
    const optimistikKullanildi = optimistikTip === "image" || optimistikTip === "video";
    if (optimistikKullanildi) setOptimisticMesajlar((prev) => [...prev, optimistikMedya]);
    enAltaSabitle();

    const optimistigiTemizle = () => {
      if (optimistikKullanildi) setOptimisticMesajlar((prev) => prev.filter((m) => m.id !== medyaTempId));
      if (onizlemeUrl) {
        try { URL.revokeObjectURL(onizlemeUrl); } catch (_) {}
      }
    };

    try {
      const yeniMesaj = await medyaGonderService({
        familyId,
        roomId,
        dosya,
        user,
        tamAd,
        replyTo: replyTo ? {
          id: replyTo.id,
          mesajId: replyTo.id,
          uid: replyTo.uid,
          ad: replyTo.ad,
          metin: replyTo.metin || "",
          type: replyTo.type || "text",
        } : null,
        onProgress: setUploadPct,
      });
      chatBildirimiGonder(yeniMesaj, dosya.type?.startsWith("image/") ? "Fotoğraf gönderdi" : (dosya.type?.startsWith("video/") ? "Video gönderdi" : `Dosya gönderdi: ${dosya.name}`));
      setReplyTo(null);
      enAltaSabitle();
    } catch (e) {
      optimistigiTemizle();
      console.error("[chat media upload]", e);
      appAlert(lang === "en" ? "Media could not be uploaded. Please try again." : "Medya yüklenemedi. Lütfen tekrar deneyin.");
    }
    finally { setUploading(false); setUploadPct(0); }
  };

  // ── Ses kaydı ──
  const sesKayitSureStr = (sn) => {
    const m = Math.floor(sn / 60);
    const s = sn % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const sesGonderFn = async (audioFile) => {
    if (!islemIzniVarMi("sendMedia")) return;
    setUploading(true); setUploadPct(0);
    enAltaSabitle(); // MADDE 2
    try {
      const yeniMesaj = await sesGonderBackend({
        familyId, roomId, dosya: audioFile, user, tamAd, durationSeconds: audioFile?.durationSeconds || 0,
        replyTo: replyTo ? { id: replyTo.id, mesajId: replyTo.id, uid: replyTo.uid, ad: replyTo.ad, metin: replyTo.metin || "", type: replyTo.type || "text" } : null,
        onProgress: setUploadPct,
      });
      chatBildirimiGonder(yeniMesaj, lang === "en" ? "Sent a voice message" : "Ses mesajı gönderdi");
      setReplyTo(null);
    } catch (e) { console.error("[ses gönder]", e); }
    finally { setUploading(false); setUploadPct(0); }
  };

  const sesKaydiBaslat = async () => {
    if (sesKayitAktif) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      audioDurationRef.current = 0;
      const ua = navigator.userAgent || "";
      const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const candidates = isiOS
        ? ["audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/aac"]
        : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4"];
      const mimeType = candidates.find((type) => {
        try { return MediaRecorder.isTypeSupported(type); } catch { return false; }
      }) || "";
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        // Bazı WebKit sürümlerinde son dataavailable event'i onstop çevriminden hemen sonra
        // microtask/macrotask kuyruğuna düşebiliyor. Kısa bekleme sıfır byte/boş chunk
        // yüzünden upload'ın hiç başlamamasını engeller.
        await new Promise((resolve) => window.setTimeout(resolve, 160));
        stream.getTracks().forEach((tr) => tr.stop());

        const chunks = audioChunksRef.current.filter((chunk) => chunk && chunk.size > 0);
        if (chunks.length === 0) {
          console.error("[ses kayıt] kayıt blob'u boş geldi, upload başlatılmadı");
          setSesKayitYukleniyor(false);
          appAlert(lang === "en" ? "Voice recording could not be created. Please try again." : "Ses kaydı oluşturulamadı. Lütfen tekrar deneyin.");
          return;
        }

        const finalMimeType = recorder.mimeType || mimeType || chunks[0]?.type || "audio/mp4";
        const ext = finalMimeType.includes("mp4") || finalMimeType.includes("aac") ? "m4a" : "webm";
        const blob = new Blob(chunks, { type: finalMimeType });
        const file = new File([blob], `ses-kaydi-${Date.now()}.${ext}`, { type: finalMimeType });
        file.durationSeconds = Math.max(1, Number(audioDurationRef.current || sesKayitSaniye || 0));

        setSesKayitYukleniyor(true);
        try {
          await sesGonderFn(file);
        } catch (err) {
          console.error("[ses gönder] upload/mesaj başarısız", err);
          appAlert(lang === "en" ? "Voice message could not be sent." : "Ses mesajı gönderilemedi.");
        } finally {
          setSesKayitYukleniyor(false);
          audioChunksRef.current = [];
          audioDurationRef.current = 0;
        }
      };
      recorder.start();
      setSesKayitAktif(true);
      setSesKayitSaniye(0);
      audioDurationRef.current = 0;
      sesKayitTimerRef.current = setInterval(() => {
        setSesKayitSaniye((s) => {
          const next = s + 1;
          audioDurationRef.current = next;
          return next;
        });
      }, 1000);
    } catch (err) {
      console.error("[ses kayıt]", err);
      appAlert(lang === "en" ? "Microphone access denied." : "Mikrofon erişimine izin verin.");
    }
  };

  const sesKaydiDurdur = () => {
    if (!sesKayitAktif) return;

    const recorder = mediaRecorderRef.current;
    clearInterval(sesKayitTimerRef.current);
    audioDurationRef.current = Math.max(audioDurationRef.current, sesKayitSaniye || 0);
    setSesKayitAktif(false);
    setSesKayitSaniye(0);

    if (!recorder || recorder.state === "inactive") {
      console.warn("[ses kayıt] aktif MediaRecorder bulunamadı");
      return;
    }

    // iOS/WKWebView bazı sürümlerde requestData() çağrısından hemen sonra stop edilirse
    // dataavailable henüz gelmeden onstop çalışabiliyor. Bu da dosyanın hiç upload
    // edilmemesine sebep olur. Önce final blob'u istemek, sonra kısa gecikmeyle stop etmek
    // bu yarışı engeller.
    try { recorder.requestData?.(); } catch (err) { console.warn("[ses kayıt] requestData başarısız", err); }
    window.setTimeout(() => {
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } catch (err) {
        console.error("[ses kayıt] stop başarısız", err);
        recorder.stream?.getTracks?.().forEach((tr) => tr.stop());
      }
    }, 120);
  };

  const sesKaydiIptal = () => {
    clearInterval(sesKayitTimerRef.current);
    audioDurationRef.current = Math.max(audioDurationRef.current, sesKayitSaniye || 0);
    setSesKayitAktif(false);
    setSesKayitSaniye(0);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      try { mediaRecorderRef.current.requestData?.(); } catch {}
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => { mediaRecorderRef.current?.stream?.getTracks?.().forEach((tr) => tr.stop()); };
      mediaRecorderRef.current.stop();
    }
    audioChunksRef.current = [];
    audioDurationRef.current = 0;
  };

  // Unmount cleanup
  useEffect(() => {
    return () => {
      clearInterval(sesKayitTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Menü paylaşma (Bölüm 7)
  const menuGonder = async () => {
    try {
      const secilen = MENULER[Math.floor(Math.random() * MENULER.length)];
      const yeniMesaj = await mesajGonderService({
        familyId, roomId,
        metin: t("chat.menuSuggestion", { meal: secilen.anaYemek }),
        user, tamAd, replyTo,
        ekstra: { type: "menu", menuData: secilen, menuVotes: {} }
      });
      chatBildirimiGonder(yeniMesaj, t("chat.menuSuggestion", { meal: secilen.anaYemek }));
      setReplyTo(null);
      setQaModal(null);
    } catch (e) { console.error(e); }
  };

  /* MADDE 4 — Oylama oluşturma akışı */
  const oylamaModalAc = () => {
    setOylamaSoru("");
    setOylamaSecenekler(["", ""]);
    setOylamaCoklu(false);
    setOylamaAnonim(false);
    setOylamaModal(true);
  };

  const oylamaSecenekDegistir = (index, deger) => {
    setOylamaSecenekler((prev) => prev.map((s, i) => (i === index ? deger : s)));
  };

  const oylamaSecenekEkle = () => {
    setOylamaSecenekler((prev) => (prev.length >= OYLAMA_MAX_SECENEK ? prev : [...prev, ""]));
  };

  const oylamaSecenekSil = (index) => {
    setOylamaSecenekler((prev) => (prev.length <= OYLAMA_MIN_SECENEK ? prev : prev.filter((_, i) => i !== index)));
  };

  const oylamaGecerliMi =
    oylamaSoru.trim().length > 0 &&
    oylamaSecenekler.filter((sec) => sec.trim().length > 0).length >= OYLAMA_MIN_SECENEK;

  const oylamaGonder = async () => {
    if (!oylamaGecerliMi || oylamaGonderiliyor) return;
    if (!islemIzniVarMi("sendMessage")) return;

    setOylamaGonderiliyor(true);
    try {
      await oylamaGonderService({
        familyId,
        roomId,
        user,
        tamAd,
        soru: oylamaSoru,
        secenekler: oylamaSecenekler
          .map((metin, i) => ({ id: `o${i + 1}`, metin }))
          .filter((sec) => sec.metin.trim()),
        cokluSecim: oylamaCoklu,
        anonim: oylamaAnonim,
      });
      setOylamaModal(false);
      enAltaSabitle();
    } catch (err) {
      console.error("[chat oylama gönder]", err);
      appAlert(lang === "en" ? "The poll could not be created. Please try again." : "Oylama oluşturulamadı. Lütfen tekrar deneyin.");
    } finally {
      setOylamaGonderiliyor(false);
    }
  };

  const qaGonder = async () => {
    if (qaModal?.key === "menu") { await menuGonder(); return; }
    if (!qaBaslik.trim() || !qaModal) return;
    try {
      const yeniMesaj = await quickActionMesajService({ familyId, roomId, user, tamAd, type: qaModal.key, baslik: qaBaslik });
      chatBildirimiGonder(yeniMesaj, qaBaslik);
      if (qaModal.key === "task")     setAktifSayfa("gorev");
      if (qaModal.key === "shopping") setAktifSayfa("alisveris");
      if (qaModal.key === "calendar") setAktifSayfa("takvim");
      setQaModal(null); setQaBaslik("");
    } catch (e) { console.error(e); }
  };

  const adKaydet = async () => {
    if (!yeniAd.trim()) return;
    await odaAdGuncelleService({ familyId, roomId, yeniAd });
    setAdDuzenle(false);
  };

  const odaAvatarSecildi = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setOdaAvatarYukleniyor(true);
    try {
      await odaAvatarGuncelleService({ familyId, roomId, dosya: file, user });
    } catch (err) {
      console.error("[chat room avatar]", err);
      await appAlert(lang === "en" ? "Room photo could not be updated." : "Sohbet fotoğrafı güncellenemedi.");
    } finally {
      setOdaAvatarYukleniyor(false);
      if (odaAvatarRef.current) odaAvatarRef.current.value = "";
    }
  };

  const medyaOtoKaydetDegistir = async () => {
    const next = !medyaOtoKaydet;
    // WhatsApp mantığı: özellik açıldığı anda geçmiş medyaları kaydetme.
    // Sadece bundan sonra gelen yeni fotoğraflar otomatik Fotoğraflar'a yazılır.
    if (next) {
      autoSavedMediaRef.current = new Set(
        gorunenMesajlar
          .filter((m) => m.type === "image" && m.id)
          .map((m) => String(m.id))
      );
    }
    setMedyaOtoKaydet(next);
    try { localStorage.setItem(`chatAutoSaveMedia.${familyId}.${roomId}`, next ? "1" : "0"); } catch {}
    try { await odaAyarGuncelleService({ familyId, roomId, patch: { autoSaveMedia: next } }); } catch (_) {}
  };

  const mesajGrupla = useCallback(() => {
    const items = [];
    let sonGun = "";
    gorunenMesajlar.forEach((msg) => {
      const gun = msg.createdAt ? gunStr(msg.createdAt, t("chat.today"), t("chat.yesterday"), lang) : "";
      if (gun !== sonGun) { items.push({ type: "sep", label: gun, key: `sep-${gun}-${msg.id}` }); sonGun = gun; }
      items.push({ type: "msg", msg });
    });
    return items;
  }, [gorunenMesajlar, t, lang]);

  const filtrelenmis = aramaMetni
    ? gorunenMesajlar.filter((m) => m.metin?.toLowerCase().includes(aramaMetni.toLowerCase()) && !m.deletedAt)
    : null;

  const odaMedyaMesajlari = useMemo(() =>
    gorunenMesajlar
      .filter((m) => ["image", "video"].includes(m.type) && m.mediaUrl && !m.mediaExpired && !m.deletedAt)
      .slice()
      .sort((a, b) => mesajTarihMs(b.createdAt) - mesajTarihMs(a.createdAt)),
    [gorunenMesajlar, mesajTarihMs]
  );
  const odaBelgeMesajlari = useMemo(() =>
    gorunenMesajlar
      .filter((m) => m.type === "file" && m.mediaUrl && !m.mediaExpired && !m.deletedAt)
      .slice()
      .sort((a, b) => mesajTarihMs(b.createdAt) - mesajTarihMs(a.createdAt)),
    [gorunenMesajlar, mesajTarihMs]
  );
  const odaBaglantiMesajlari = useMemo(() => {
    return gorunenMesajlar
      .filter((m) => !m.deletedAt && typeof m.metin === "string")
      .map((m) => ({ ...m, links: extractChatLinks(m.metin) }))
      .filter((m) => m.links.length > 0)
      .sort((a, b) => mesajTarihMs(b.createdAt) - mesajTarihMs(a.createdAt));
  }, [gorunenMesajlar, mesajTarihMs]);

  const previewItemFromMessage = useCallback((m) => {
    const fileName = m.fileName || m.dosyaAdi || (m.type === "video" ? `chat-video-${m.id}.mp4` : `chat-foto-${m.id}.jpg`);
    return {
      url: m.type === "video" ? getVideoPlaybackUrl(m.mediaUrl, fileName) : getFileUrl(m.mediaUrl),
      rawUrl: m.mediaUrl,
      mesajId: m.id,
      gonderenUid: m.uid || m.gonderenUid,
      fileName,
      mimeType: m.mimeType || m.mediaType || m.fileType || m.dosyaTipi || (m.type === "video" ? "video/mp4" : "image/jpeg"),
      type: m.type,
    };
  }, []);

  const openMediaPreview = useCallback((item, sourceList = odaMedyaMesajlari) => {
    const gallery = (sourceList || []).map(previewItemFromMessage);
    const index = Math.max(0, gallery.findIndex((g) => String(g.mesajId) === String(item?.mesajId || item?.id)));
    const fallback = item?.url ? item : (gallery[index] || null);
    if (!fallback) return;
    setFotoOnizleme({ ...fallback, gallery, index: index >= 0 ? index : 0 });
  }, [odaMedyaMesajlari, previewItemFromMessage]);

  const odaBilgiSekmeler = ["media", "links", "docs"];
  const odaBilgiSekmeIndex = Math.max(0, odaBilgiSekmeler.indexOf(odaBilgiSekme));
  const odaBilgiSekmeDegistir = useCallback((direction) => {
    const current = Math.max(0, odaBilgiSekmeler.indexOf(odaBilgiSekme));
    const next = Math.max(0, Math.min(odaBilgiSekmeler.length - 1, current + direction));
    if (next !== current) setOdaBilgiSekme(odaBilgiSekmeler[next]);
  }, [odaBilgiSekme]);
  const handleChatInfoTouchStart = useCallback((e) => {
    const t0 = e.touches?.[0];
    if (!t0) return;
    chatInfoTouchRef.current = { startX: t0.clientX, startY: t0.clientY, moved: false };
  }, []);
  const handleChatInfoTouchEnd = useCallback((e) => {
    const t1 = e.changedTouches?.[0];
    if (!t1) return;
    const dx = t1.clientX - chatInfoTouchRef.current.startX;
    const dy = Math.abs(t1.clientY - chatInfoTouchRef.current.startY);
    if (Math.abs(dx) > 52 && dy < 48) odaBilgiSekmeDegistir(dx < 0 ? 1 : -1);
  }, [odaBilgiSekmeDegistir]);

  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el || loadingOlder) return;

    const domAnchor = olderLoadDomAnchorRef.current;
    const anchor = olderLoadAnchorRef.current;
    if (!domAnchor && !anchor) return;

    /* ══════════════════════════════════════════════════════════════════
       ESKİ MESAJ YÜKLEME — KONUM KORUMA (AKICI, ZIPLAMASIZ)

       ÖNCEKİ SÜRÜMÜN SORUNU:
       Telafi, 520 ms boyunca bir ResizeObserver + birçok setTimeout ile
       konumu SÜREKLİ çapaya geri çekiyordu. Kullanıcı bu pencere içinde elle
       yukarı kaydırmaya devam ettiğinde, bu geri-çekmeler onun kaydırmasını
       EZİYOR ve ekranı ansızın eski konuma zıplatıyordu ("bir anda 29 Temmuz,
       10 Haziran'a atlıyor"). Ayrıca art arda yüklemeler üst üste biniyordu.

       YENİ YÖNTEM:
       Eklenen eski mesajların getirdiği yükseklik farkını TEK SEFERDE, senkron
       (boyamadan önce) telafi ederiz — göz hizasındaki mesaj yerinde kalır.
       Medya kutuları zaten SABİT ölçülü (176×268) olduğundan yüklenince
       yükseklik değişmez; bu yüzden uzun "oturma penceresi" GEREKSİZDİR ve
       kaldırıldı. Kullanıcının kaydırmasına artık hiç karışılmaz → akıcı,
       kesintisiz geçmiş kaydırma. Bir sonraki karede kısa bir soğuma ile
       bayraklar temizlenir; böylece art arda yüklemeler üst üste binmez.
       ══════════════════════════════════════════════════════════════════ */
    /* İstek sürerken kullanıcı kaydırmaya devam etmiş olabilir. İsteğin
       başladığı konuma geri çekmek yerine bu gerçek hareketi de hedefe ekle. */
    const kullaniciKaydirmaFarki = anchor
      ? el.scrollTop - anchor.scrollTop
      : 0;

    const yeniScrollTop = nativeScrollAnchorAktif ? null : (() => {
      if (domAnchor) {
        const hedef = Array.from(el.querySelectorAll("[id^='msg-']"))
          .find((node) => node.id === domAnchor.id);
        if (hedef && el.contains(hedef)) {
          return Math.max(0, hedef.offsetTop - domAnchor.offset + kullaniciKaydirmaFarki);
        }
      }
      if (anchor) {
        const delta = el.scrollHeight - anchor.scrollHeight;
        return Math.max(0, anchor.scrollTop + delta + kullaniciKaydirmaFarki);
      }
      return null;
    })();

    if (yeniScrollTop != null) {
      el.scrollTop = yeniScrollTop;
    }

    // Anchor'ları hemen tüket; tek seferlik telafi yeterli.
    olderLoadAnchorRef.current = null;
    olderLoadDomAnchorRef.current = null;

    /* Kısa soğuma: art arda yüklemelerin üst üste binmemesi için bir sonraki
       yüklemeye izin vermeden önce ~180 ms bekle. Bu süre kullanıcının
       kaydırmasına KARIŞMAZ (scrollTop'a dokunmaz); yalnızca yeni bir
       onLoadOlder tetiklenmesini geciktirir. */
    const soguma = window.setTimeout(() => {
      olderLoadRequestedRef.current = false;
      olderLoadAktifRef.current = false;
    }, 180);

    return () => window.clearTimeout(soguma);
  }, [gorunenMesajlar.length, loadingOlder, nativeScrollAnchorAktif]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;

    const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight;
    const enAltta = bottomGap < 150;
    // Kullanıcının en altta olup olmadığını güncelle (WhatsApp mantığı için)
    kullaniciEnAltaMiRef.current = enAltta;
    setAsagiInButonuGoster((prev) => {
      const next = mesajYuklendi && gorunenMesajlar.length > 0 && bottomGap > 320;
      return prev === next ? prev : next;
    });

    /* Hızlı iOS/Android momentumunda 300px eşik videoda veriden önce tükeniyor
       ve listenin tepesinde boş bir kare gösteriyordu. Yalnızca kullanıcı
       geçmişe doğru kaydırmaya başladıktan sonra, iki görünür ekran önceden
       aynı 30'luk premium paketi hazırla. Yetki, sayfa boyutu ve geçmiş sınırı
       değişmez; sadece ağ isteği zamanında başlar. */
    const eskiMesajOnYuklemeEsigi = Math.max(600, Math.round(el.clientHeight * 2));
    if (isPremium && userHistoryScrollIntentRef.current && hasMoreOlder && !loadingOlder && !aramaAcik && el.scrollTop <= eskiMesajOnYuklemeEsigi && !olderLoadRequestedRef.current) {
      olderLoadRequestedRef.current = true;
      olderLoadAktifRef.current = true;

      /* MADDE 2 (2. tur) — SAĞLAM KONUM KORUMA (DOM ÇAPASI)
         ─────────────────────────────────────────────────────────────
         ÖNCEKİ YÖNTEM scrollHeight deltasına dayanıyordu:
           delta = yeniScrollHeight - eskiScrollHeight;
           scrollTop = eskiScrollTop + delta;
         Eski mesajlardaki medyalar (foto/video) DOM'a eklendikten SONRA
         yüklendiği için, telafi anında scrollHeight henüz yanlıştı; medya
         yüklenince yükseklik tekrar değişip ekran zıplıyordu ("birden eskiye
         dönüyor") .

         YENİ YÖNTEM: yükleme öncesi görünür alanın EN ÜSTÜNDEKİ mesajı ve o
         mesajın viewport'a göre dikey ofsetini kaydederiz. Yükleme sonrası
         (ve medyalar oturduktan sonra) AYNI mesajı bulup ofsetini birebir
         geri veririz. Medya yüksekliği değişse bile o mesaj göz hizasında
         sabit kalır — hiç zıplama olmaz. */
      let capaEl = null;
      const cocuklar = el.querySelectorAll("[id^='msg-']");
      const ustSinir = el.scrollTop;
      for (let i = 0; i < cocuklar.length; i += 1) {
        if (cocuklar[i].offsetTop >= ustSinir - 4) { capaEl = cocuklar[i]; break; }
      }
      olderLoadDomAnchorRef.current = capaEl
        ? { id: capaEl.id, offset: capaEl.offsetTop - el.scrollTop }
        : null;
      // Yedek (çapa bulunamazsa): eski scrollHeight tabanlı yöntem.
      olderLoadAnchorRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop };

      Promise.resolve(onLoadOlder?.()).catch(() => {
        olderLoadAnchorRef.current = null;
        olderLoadDomAnchorRef.current = null;
        olderLoadRequestedRef.current = false;
        olderLoadAktifRef.current = false;
      });
    }

    if (isPremium || aramaAcik || premiumHistoryPopupShownRef.current || !userHistoryScrollIntentRef.current) return;
    const limit = Number(premiumConfig?.limits?.mesajGecmis ?? 30);
    if (!limit || limit <= 0 || gorunenMesajlar.length < limit) return;

    // Kullanıcı gerçekten eski mesajların başladığı en üst sınıra geldiğinde bilgilendir.
    const kaydirilabilir = el.scrollHeight > el.clientHeight + 12;
    if (kaydirilabilir && el.scrollTop <= 6) {
      premiumHistoryPopupShownRef.current = true;
      onPremiumLimitHit?.(
        t("chat.premium.history"),
        limit,
        limit,
        [t("chat.premium.b2"), t("chat.premium.historyFull"), t("chat.premium.mediaHistory")],
        t("chat.freeHistoryLimit", { limit })
      );
    }
  }, [isPremium, hasMoreOlder, loadingOlder, aramaAcik, premiumConfig?.limits?.mesajGecmis, gorunenMesajlar.length, mesajYuklendi, onLoadOlder, onPremiumLimitHit, t]);

  /* ══════════════════════════════════════════════════════════════════
     SOHBET KLAVYE TELAFİSİ — UYGULAMA GENELİYLE AYNI MEKANİZMA

     KÖK NEDEN (neden sohbette çalışmıyordu):
     Uygulamanın DİĞER sayfaları klavye açılınca düzgün yukarı kayıyor, çünkü
     `.page` elemanına `padding-bottom: var(--ios-kb-inset)` veriliyor ve normal
     akıştaki içerik bu dolgu kadar yukarı itiliyor.

     Sohbet ekranı ise `.chatPage.chatRoomFull { position: fixed; inset: 0 }`
     ile TAM EKRAN ve `.page`'in akışının TAMAMEN DIŞINDA. Dolayısıyla `.page`e
     verilen dolgu sohbeti hiç etkilemiyordu; üstelik `.page.chatPageActive`
     kuralı o dolguyu ayrıca sıfırlıyordu. Sonuç: klavye composer'ın üzerine
     biniyordu.

     Önceki denemelerde sohbet kabının YÜKSEKLİĞİNİ (height/top) değiştirmeye
     çalıştık; `inset:0` + `height:100dvh !important` + `contain: size` +
     `transform` birleşimi yüzünden WKWebView'de güvenilir sonuç vermedi.

     ÇÖZÜM — diğer sayfalarda ne çalışıyorsa aynısı:
     Sohbet kabının kendisine `padding-bottom: var(--chat-kb-h)` veriyoruz.
     Kap `box-sizing: border-box` ve dikey flex olduğu için bu dolgu içerik
     alanını küçültür; başlık/mesajlar/yazma alanı otomatik olarak klavyenin
     ÜSTÜNE yerleşir. Dolgu, konum/yükseklik kısıtlarıyla hiç çakışmaz.

     KLAVYE YÜKSEKLİĞİ KAYNAĞI:
     Birincil kaynak Capacitor Keyboard eklentisinin NATIVE olaylarıdır
     (`keyboardWillShow` → gerçek `keyboardHeight`). `Keyboard.resize:'none'`
     ayarındayken iOS'ta `visualViewport` güvenilir ölçüm vermeyebildiği için
     (önceki denemelerin başarısız olma nedeni) native değer esas alınır.
     `visualViewport` yalnızca native olay yoksa (web/PWA) yedek olarak kullanılır.
     ══════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const input = inputRef.current;
    const vv = window.visualViewport;
    let rafId = null;
    let closeTimer = null;
    let nativeAktif = false;          // native olay geldiyse yedek ölçüm devre dışı
    let showHandle = null;
    let hideHandle = null;
    const settleTimers = [];

    /* Değişken KÖK elemana (html) yazılır, sohbet kabına değil.
       NEDEN: Geri-kaydırma (swipe) mimarisi, `swipeBgLayer` içinde sohbet
       odasının İKİNCİ bir kopyasını render edebiliyor. Değişken yalnızca ön
       plandaki kaba yazıldığında arka plandaki kopya dolgusuz kalıyor ve
       klavye açılıp kapanırken ekranda çift/hayalet görüntü oluşuyordu.
       Kök elemana yazılınca DOM'daki tüm kopyalar aynı değeri miras alır. */
    const kbYaz = (px) => {
      document.documentElement.style.setProperty("--chat-kb-h", `${Math.max(0, Math.round(px))}px`);
    };

    const lockPage = () => {
      document.documentElement.classList.add("chat-native-keyboard-lock");
      document.body.classList.add("chat-native-keyboard-lock");
      window.scrollTo(0, 0);
    };

    const unlockPage = () => {
      document.documentElement.classList.remove("chat-native-keyboard-lock");
      document.body.classList.remove("chat-native-keyboard-lock");
      document.documentElement.style.setProperty("--chat-kb-h", "0px");
      const page = chatPageRef.current;
      if (page) {
        // Eski sürümlerden kalmış olabilecek satır içi geometriyi de temizle.
        page.style.removeProperty("height");
        page.style.removeProperty("top");
      }
    };

    const scrollMessagesToBottom = () => {
      requestAnimationFrame(() => {
        const el = messagesRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    };

    const klavyeAcildi = (yukseklik) => {
      // Klavye yüksekliği değişirse (emoji klavyesi, tahmin çubuğu vb.) bu
      // fonksiyon tekrar çağrılır; önceki oturma zamanlayıcıları birikmesin.
      settleTimers.forEach(clearTimeout);
      settleTimers.length = 0;
      lockPage();
      kbYaz(yukseklik);
      setEmojiPanel(false);
      setAttachMenuAcik(false);
      setKeyboardState((prev) => {
        const next = { open: true, height: yukseklik, visualHeight: 0, viewportTop: 0 };
        return prev.open === next.open && prev.height === next.height ? prev : next;
      });
      scrollMessagesToBottom();
      // Klavye animasyonu bitince son mesaj kesin görünsün.
      [90, 240].forEach((ms) => settleTimers.push(setTimeout(scrollMessagesToBottom, ms)));
    };

    const klavyeKapandi = () => {
      kbYaz(0);
      setKeyboardState((prev) => (prev.open ? { open: false, height: 0, visualHeight: 0, viewportTop: 0 } : prev));
      unlockPage();
    };

    /* ── BİRİNCİL KAYNAK: Capacitor native klavye olayları (YALNIZCA iOS) ──
       Android'de native taraf (MainActivity ime-inset) WebView'i klavye kadar
       ZATEN küçültüyor. Orada ayrıca alt dolgu vermek ÇİFT TELAFİ olur ve
       yazma alanı klavyeden çok yukarıda kalır. Bu yüzden native dinleyiciler
       ve dolgu mekanizması yalnızca iOS'ta devreye girer; Android davranışı
       bire bir korunur. */
    if (isIOSPlatform()) {
      try {
        const p1 = Keyboard.addListener("keyboardWillShow", (info) => {
          nativeAktif = true;
          const h = (info && Number(info.keyboardHeight)) || 0;
          if (h > 0) klavyeAcildi(h);
        });
        if (p1 && typeof p1.then === "function") {
          p1.then((h) => { showHandle = h; }).catch(() => {});
        } else {
          showHandle = p1;
        }

        const p2 = Keyboard.addListener("keyboardWillHide", () => {
          nativeAktif = true;
          klavyeKapandi();
        });
        if (p2 && typeof p2.then === "function") {
          p2.then((h) => { hideHandle = h; }).catch(() => {});
        } else {
          hideHandle = p2;
        }
      } catch (_) {
        // Native eklenti yoksa (tarayıcı/PWA) yedek ölçüme düşülür.
      }
    }

    /* ── YEDEK KAYNAK: visualViewport (native olay gelmiyorsa) ── */
    const applyViewport = () => {
      if (nativeAktif) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (nativeAktif) return;
        const layoutHeight = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0);
        const viewportHeight = Math.max(0, Math.round((vv && vv.height) || layoutHeight));
        const viewportTop = Math.max(0, Math.round((vv && vv.offsetTop) || 0));
        const keyboardHeight = Math.max(0, Math.round(layoutHeight - viewportHeight - viewportTop));
        const inputFocused = document.activeElement === inputRef.current;

        if (inputFocused && keyboardHeight > 80) {
          klavyeAcildi(keyboardHeight);
        } else if (!inputFocused) {
          klavyeKapandi();
        }
      });
    };

    const handleFocus = () => {
      clearTimeout(closeTimer);
      lockPage();
      applyViewport();
      [80, 180, 360].forEach((ms) => settleTimers.push(setTimeout(applyViewport, ms)));
    };

    const handleBlur = () => {
      closeTimer = setTimeout(() => {
        if (document.activeElement !== inputRef.current) klavyeKapandi();
      }, 160);
    };

    if (input) {
      input.addEventListener("focus", handleFocus);
      input.addEventListener("blur", handleBlur);
    }
    if (vv) {
      vv.addEventListener("resize", applyViewport);
      vv.addEventListener("scroll", applyViewport);
    }
    window.addEventListener("orientationchange", applyViewport);

    applyViewport();

    return () => {
      clearTimeout(closeTimer);
      settleTimers.forEach(clearTimeout);
      if (rafId) cancelAnimationFrame(rafId);
      if (input) {
        input.removeEventListener("focus", handleFocus);
        input.removeEventListener("blur", handleBlur);
      }
      if (vv) {
        vv.removeEventListener("resize", applyViewport);
        vv.removeEventListener("scroll", applyViewport);
      }
      window.removeEventListener("orientationchange", applyViewport);
      if (showHandle && typeof showHandle.remove === "function") showHandle.remove();
      if (hideHandle && typeof hideHandle.remove === "function") hideHandle.remove();
      setKeyboardState({ open: false, height: 0, visualHeight: 0, viewportTop: 0 });
      unlockPage();
    };
  }, [roomId]);

  // Geri/sürükle geçişinden hemen önce native klavye kilidini temizle.
  // Mesaj gönderdikten sonra input tekrar focus olduğu için iOS WebView bazen
  // chatRoomFull yüksekliği/top değerini keyboard viewportunda bırakıyordu; bu da
  // geri animasyonunda boş sayfa ve takılma yaratıyordu.
  useEffect(() => {
    const prepareBack = () => {
      try { inputRef.current?.blur?.(); } catch {}
      document.documentElement.classList.remove("chat-native-keyboard-lock");
      document.body.classList.remove("chat-native-keyboard-lock");
      document.documentElement.style.setProperty("--chat-kb-h", "0px");
      const page = chatPageRef.current;
      if (page) {
        // Eski sürümlerden kalabilecek satır içi geometriyi sıfırla; aksi halde
        // geri animasyonunda alt boşluk takılı kalıyordu.
        page.style.removeProperty("height");
        page.style.removeProperty("top");
        page.style.removeProperty("bottom");
        page.style.removeProperty("min-height");
        page.style.removeProperty("max-height");
      }
      setKeyboardState((prev) => prev.open ? { open: false, height: 0, visualHeight: 0, viewportTop: 0 } : prev);
    };

    window.__chatRoomPrepareBack = prepareBack;
    return () => {
      if (window.__chatRoomPrepareBack === prepareBack) delete window.__chatRoomPrepareBack;
      prepareBack();
    };
  }, [roomId]);

  const uyeSayisi   = oda?.uyeUidler?.length || 0;
  const canSend     = islemIzniVarMi("sendMessage");
  const canRename   = oda?.olusturanUid === user.uid || yoneticiMi;


  const nativeIndirDosya = useCallback(async (item, fallbackBaseName = "chat-dosya") => {
    if (!item || fotoIndiriliyor) return;
    setFotoIndiriliyor(true);
    try {
      const blob = await fetchChatFileBlob(item.rawUrl || item.url);
      const finalMime = blob.type || item.mimeType || "application/octet-stream";
      const finalName = safeChatDosyaAdi(item.fileName, finalMime, fallbackBaseName);
      const nativeBridge = window?.webkit?.messageHandlers?.aileNativeBridge;

      if (nativeBridge) {
        const base64 = await blobToBase64(blob);
        nativeBridge.postMessage({ action: "shareFile", base64, mimeType: finalMime, fileName: finalName });
        return;
      }

      // ANDROID (MADDE 8): Sohbetteki medya/dosya indirme de artık tarayıcıda
      // açmak yerine Android'in yerleşik PAYLAŞ/KAYDET sayfasını açar; kullanıcı
      // dosyayı "Dosyalara kaydet" veya "Fotoğraflar'a kaydet" ile saklayabilir.
      // Depolama izni gerektirmez (uygulama önbelleği + FileProvider).
      if (isAndroidPlatform()) {
        const paylasildi = await androidDosyaPaylas(blob, finalName, finalName);
        if (paylasildi) return;
      }

      if (navigator?.canShare && window.File) {
        const file = new File([blob], finalName, { type: finalMime });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: finalName });
          return;
        }
      }

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (_) {
      await appAlert(lang === "en" ? "File could not be downloaded." : "Dosya indirilemedi.");
    } finally {
      setFotoIndiriliyor(false);
    }
  }, [fotoIndiriliyor, lang]);

  const fotoNativeIndir = useCallback(async () => {
    return nativeIndirDosya(fotoOnizleme, "chat-foto");
  }, [nativeIndirDosya, fotoOnizleme]);

  const autoSavedMediaRef = useRef(new Set());
  const autoSaveInFlightRef = useRef(new Set());
  const autoSaveSeedRoomRef = useRef(null);

  useEffect(() => {
    // Odaya ilk girişte gelen geçmiş mesajlar asla otomatik galeriye yazılmamalı.
    // Mesajlar gerçekten yüklendikten sonra o an ekranda olan fotoğrafları seed ediyoruz.
    // Böylece chatten çıkıp girince veya uygulama yeniden açılınca eski fotoğraflar tekrar kaydedilmez.
    if (!roomId || !mesajYuklendi) return;
    if (autoSaveSeedRoomRef.current === roomId) return;
    autoSavedMediaRef.current = new Set(
      gorunenMesajlar
        .filter((m) => m.type === "image" && m.id)
        .map((m) => String(m.id))
    );
    autoSaveSeedRoomRef.current = roomId;
  }, [roomId, mesajYuklendi, gorunenMesajlar]);

  const nativeFotoGaleriyeKaydet = useCallback(async (message) => {
    if (!message?.mediaUrl) return false;
    const nativeBridge = window?.webkit?.messageHandlers?.aileNativeBridge;
    if (!nativeBridge) return false;

    const rawUrl = message.rawUrl || message.mediaUrl;
    const blob = await fetchChatFileBlob(rawUrl);
    const mimeType = blob.type || message.mediaType || "image/jpeg";
    if (!mimeType.startsWith("image/")) return false;

    const base64 = await blobToBase64(blob);
    nativeBridge.postMessage({
      action: "savePhotoToLibrary",
      base64,
      mimeType,
      fileName: safeChatDosyaAdi(message.fileName, mimeType, `chat-foto-${message.id || Date.now()}`),
    });
    return true;
  }, []);

  useEffect(() => {
    if (!medyaOtoKaydet || !roomId || autoSaveSeedRoomRef.current !== roomId) return;

    const yeniGelenFotolar = gorunenMesajlar
      .filter((m) => m.type === "image" && m.mediaUrl && !m.mediaExpired && !m.deletedAt && m.uid !== user?.uid);

    yeniGelenFotolar.forEach((m) => {
      const key = String(m.id || m.mediaUrl);
      if (autoSavedMediaRef.current.has(key) || autoSaveInFlightRef.current.has(key)) return;
      autoSaveInFlightRef.current.add(key);

      (async () => {
        try {
          // Önce sadece kontrol et: daha önce bu kullanıcı için galeriye kaydedilmişse tekrar indirme.
          // Kayıt defteri, uygulama silinip yeniden yüklense bile backend'de kalır.
          const check = await otomatikGaleriKayitKontrolService({ familyId, roomId, mesajId: m.id });
          if (check?.shouldSave === false) {
            autoSavedMediaRef.current.add(key);
            return;
          }

          const saved = await nativeFotoGaleriyeKaydet({
            ...m,
            rawUrl: m.mediaUrl,
            fileName: m.fileName || `chat-foto-${m.id}.jpg`,
            mediaType: m.mediaType || "image/jpeg",
          });

          if (saved) {
            await otomatikGaleriKayitIsaretleService({ familyId, roomId, mesajId: m.id });
            autoSavedMediaRef.current.add(key);
          }
        } catch (err) {
          // Hata olursa saved set'e alma; bir sonraki mesaj yenilemede tekrar deneyebilir.
          console.warn("[chat auto save photo]", err);
        } finally {
          autoSaveInFlightRef.current.delete(key);
        }
      })();
    });
  }, [medyaOtoKaydet, roomId, familyId, gorunenMesajlar, nativeFotoGaleriyeKaydet, user?.uid]);

  return (
    <div ref={chatPageRef} style={chatRoomStyle} className={`chatPage chatRoomFull ${transitionClass} ${mesajYuklendi ? "chatMessagesReady" : "chatMessagesBooting"} ${keyboardState.open ? "chatKeyboardOpen" : ""}`}>
      <style>{`
        .chatQuotedMessageFlash .chatBubble {
          animation: chatQuotedMessageFlashAnim 1.35s ease;
        }
        @keyframes chatQuotedMessageFlashAnim {
          0%, 100% { box-shadow: none; }
          20%, 75% { box-shadow: 0 0 0 3px rgba(99,102,241,.35), 0 0 28px rgba(99,102,241,.25); }
        }
        .chatQuoteBar {
          width: 100%;
          text-align: left;
          border: 0;
          cursor: pointer;
          appearance: none;
          display: flex;
          flex-direction: column;
        }
      `}</style>
      <div className="chatHeader chatHeaderCentered">
        <button className="chatGeriBtn" onClick={onGeri} aria-label={t("common.back")}>‹</button>
        <button
          type="button"
          className="chatHeaderRoomAvatar"
          onClick={() => { setYeniAd(oda?.ad || ""); setAdDuzenle(false); setOdaBilgiPanelAcik(true); }}
          aria-label={lang === "en" ? "Open chat info" : "Sohbet bilgilerini aç"}
        >
          {(oda?.avatarPreviewUrl || oda?.avatarUrl) ? <CachedImage src={getFileUrl(oda.avatarPreviewUrl || oda.avatarUrl)} cacheKey={odaAvatarCacheKey(oda)} alt="" decoding="async" /> : <span>💬</span>}
        </button>
        <button
          type="button"
          className="chatHeaderTitleCenter chatHeaderInfoTrigger"
          onClick={() => { setYeniAd(oda?.ad || ""); setAdDuzenle(false); setOdaBilgiPanelAcik(true); }}
          aria-label={lang === "en" ? "Open chat info" : "Sohbet bilgilerini aç"}
        >
          <span className="chatHeaderTitleText">{oda?.ad}</span>
          <span className="chatHeaderSub">{t("chat.memberCount", { count: uyeSayisi })}</span>
        </button>
        <div className="chatHeaderRightCluster">
          {pinnedMsg && (
            <button className="chatHeaderBtn" aria-label={t("chat.pinnedMessage")}
              onClick={() => document.getElementById(`msg-${pinnedMsg.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}>📌</button>
          )}
          <button className="chatHeaderAvatars chatHeaderAvatarsBtn" onClick={() => setUyePanelAcik(true)} title={t("chat.manageMembers")} aria-label={t("chat.manageMembers")}>
            {uyeler.filter((u) => oda?.uyeUidler?.includes(u.uid)).slice(0, 4).map((u, i) => (
              <AvatarImg
                key={u.uid}
                avatar={u.avatar || null}
                renk={uyeRenk(u.uid, uyeler)}
                initials={u.ad?.[0] || "?"}
                size={26}
                glow={false}
                radius="50%"
                className="chatHeaderAv"
                style={{ zIndex: 10 - i, marginLeft: i === 0 ? 0 : -8 }}
                title={`${u.ad} ${u.soyad}`}
              
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
            ))}
            <div className="chatHeaderAvAdd" title={t("chat.addMember")}>+</div>
          </button>
          <div className="chatHeaderActions">
            <button className="chatHeaderBtn" onClick={() => { setAramaAcik((v) => !v); setAramaMetni(""); }} aria-label={t("chat.search")}>🔍</button>

            {/* SESLİ ARAMA — sıralama: avatarlar → 🔍 → 📞 (en sağda).
                Arama düğmesi özellikle bir miktar daha küçük tutulur. */}
            <button
              className={`chatHeaderBtn chatCallHeaderBtn ${isPremium ? "" : "chatCallBtnLocked"}`}
              onClick={() => {
                if (!isPremium && onPremiumLimitHit) {
                  onPremiumLimitHit(
                    t("call.premiumTitle"),
                    0, 0,
                    [t("call.premiumB1"), t("call.premiumB2"), t("call.premiumB3")],
                    t("call.premiumNote")
                  );
                  return;
                }
                /* Arama artık MERKEZİ YÖNETİCİ üzerinden başlatılır; arayüz
                   uygulama kökündeki AramaEkrani'nda gösterilir. */
                const arayanUye = uyeler.find(
                  (u) => String(u?.uid || "") === String(user?.uid || "")
                );
                const arayanAd = `${arayanUye?.ad || ""} ${arayanUye?.soyad || ""}`.trim()
                  || arayanUye?.displayName
                  || tamAd
                  || user?.displayName
                  || "";
                callManager.baslat(oda?.id, {
                  name: arayanAd,
                  avatar: arayanUye?.avatar || "",
                }).catch((err) => {
                  const kod = err?.code || err?.data?.code;
                  if (kod === "PREMIUM_REQUIRED" || err?.status === 403) {
                    if (onPremiumLimitHit) {
                      onPremiumLimitHit(
                        t("call.premiumTitle"), 0, 0,
                        [t("call.premiumB1"), t("call.premiumB2"), t("call.premiumB3")],
                        t("call.premiumNote")
                      );
                    }
                    return;
                  }
                  if (err?.code === "MIC_PERMISSION_DENIED") { appAlert(t("call.micDenied")); return; }
                  if (kod === "CALL_ALREADY_ACTIVE") { appAlert(t("call.ongoing")); return; }
                  appAlert(t("call.startFailed"));
                });
              }}
              title={t("call.start")}
              aria-label={t("call.start")}
            >
              📞
            </button>
          </div>
        </div>
      </div>

      {/* Devam eden arama şeridi — "Katıl" ile sesli sohbete dahil olunur.
          Odada arama yoksa veya zaten aramadaysan hiçbir şey render etmez. */}
      <ChatAramaAfisi roomId={oda?.id} uyeler={uyeler} uyeRenk={uyeRenk} />

      {aramaAcik && (
        <div className="chatSearchBar">
          <span className="chatSearchIcon">🔍</span>
          <input className="chatSearchInput" placeholder={t("chat.searchInMessages")} autoFocus
            value={aramaMetni} onChange={(e) => setAramaMetni(e.target.value)} />
          {aramaMetni && <button className="chatSearchClear" onClick={() => setAramaMetni("")}>×</button>}
        </div>
      )}

      {pinnedMsg && !aramaAcik && (
        <div className="chatPinBar"
          onClick={() => document.getElementById(`msg-${pinnedMsg.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}>
          <span className="chatPinIcon">📌</span>
          <span className="chatPinText">{pinnedMsg.metin?.slice(0, 80) || t("chat.pinnedMessage")}</span>
          <span className="chatPinChevron">›</span>
        </div>
      )}

      {/* Üye Yönetim Paneli */}
      {odaBilgiPanelAcik && (
        <div className="modalOverlay chatInfoOverlay" onClick={() => setOdaBilgiPanelAcik(false)}>
          <div className="darkModal chatInfoPanel" onClick={(e) => e.stopPropagation()}>
            <div className="darkModalHeader chatInfoHeader">
              <span className="darkModalTitle">💬 {lang === "en" ? "Chat info" : "Sohbet bilgisi"}</span>
              <button className="darkModalClose" onClick={() => setOdaBilgiPanelAcik(false)}>×</button>
            </div>

            <div className="chatInfoHero">
              <button type="button" className="chatInfoAvatar chatInfoAvatarBtn" onClick={() => odaAvatarRef.current?.click?.()} disabled={odaAvatarYukleniyor} aria-label={lang === "en" ? "Change room photo" : "Sohbet fotoğrafını değiştir"}>
                {(oda?.avatarPreviewUrl || oda?.avatarUrl) ? <CachedImage src={getFileUrl(oda.avatarPreviewUrl || oda.avatarUrl)} cacheKey={odaAvatarCacheKey(oda)} alt="" decoding="async" /> : <span>💬</span>}
                <em>{odaAvatarYukleniyor ? "…" : "📷"}</em>
              </button>
              <input ref={odaAvatarRef} type="file" accept="image/*" style={{display:"none"}} onChange={(e) => odaAvatarSecildi(e.target.files?.[0])} />
              {!adDuzenle ? (
                <div className="chatInfoNameRow">
                  <div className="chatInfoNameBlock">
                    <button type="button" className="chatInfoNameEditInline" onClick={() => canRename && (setYeniAd(oda?.ad || ""), setAdDuzenle(true))}>
                      <span className="chatInfoName">{oda?.ad}</span>
                      {canRename && <span className="chatInfoPencil">✎</span>}
                    </button>
                    <div className="chatInfoSub">{t("chat.memberCount", { count: uyeSayisi })}</div>
                  </div>
                  <button
                    type="button"
                    className="chatInfoMemberStrip"
                    onClick={() => setUyePanelAcik(true)}
                    aria-label={lang === "en" ? "Open room members" : "Sohbet odası üyelerini aç"}
                  >
                    {uyeler.filter((u) => oda?.uyeUidler?.includes(u.uid)).slice(0,4).map((u) => (
                      <AvatarImg key={u.uid} avatar={u.avatar || null} renk={uyeRenk(u.uid, uyeler)} initials={u.ad?.[0] || "?"} size={28} glow={false} radius="50%" 
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
                    ))}
                    <span className="chatInfoMemberAdd">+</span>
                  </button>
                </div>
              ) : (
                <div className="chatInfoEditRow">
                  <input
                    className="darkInput chatInfoNameInput"
                    value={yeniAd}
                    onChange={(e) => setYeniAd(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") adKaydet(); if (e.key === "Escape") setAdDuzenle(false); }}
                    autoFocus
                  />
                  <button className="chatInfoSaveBtn" onClick={adKaydet}>✓</button>
                  <button className="chatInfoCancelBtn" onClick={() => setAdDuzenle(false)}>✕</button>
                </div>
              )}
            </div>

            <button type="button" className={`chatInfoAutoSave ${medyaOtoKaydet ? "aktif" : ""}`} onClick={medyaOtoKaydetDegistir}>
              <span>💾</span>
              <div>
                <strong>{lang === "en" ? "Save media to Photos" : "Medyayı Fotoğraflar’a kaydet"}</strong>
                <small>{lang === "en" ? "Off by default. When enabled, only new incoming photos are saved automatically." : "Varsayılan kapalıdır. Açıldığında sadece bundan sonra gelen yeni fotoğraflar otomatik kaydedilir."}</small>
              </div>
              <b>{medyaOtoKaydet ? (lang === "en" ? "On" : "Açık") : (lang === "en" ? "Off" : "Kapalı")}</b>
            </button>

            <div className="chatInfoTabs">
              <button className={odaBilgiSekme === "media" ? "aktif" : ""} onClick={() => setOdaBilgiSekme("media")}>🖼️ {lang === "en" ? "Media" : "Medya"}</button>
              <button className={odaBilgiSekme === "links" ? "aktif" : ""} onClick={() => setOdaBilgiSekme("links")}>🔗 {lang === "en" ? "Links" : "Bağlantı"}</button>
              <button className={odaBilgiSekme === "docs" ? "aktif" : ""} onClick={() => setOdaBilgiSekme("docs")}>📎 {lang === "en" ? "Docs" : "Belgeler"}</button>
            </div>

            <div
              className="chatInfoContent"
              onTouchStart={handleChatInfoTouchStart}
              onTouchEnd={handleChatInfoTouchEnd}
            >
              <div
                className="chatInfoSwipeTrack"
                style={{ transform: `translateX(-${odaBilgiSekmeIndex * 100}%)` }}
              >
                <div className="chatInfoPane">
                  {odaMedyaMesajlari.length ? (
                    <div className="chatInfoMediaGrid">
                      {odaMedyaMesajlari.map((m) => (
                        <button key={m.id} className="chatInfoMediaItem" onClick={() => openMediaPreview(previewItemFromMessage(m), odaMedyaMesajlari)}>
                          {m.type === "video" ? <><video src={getVideoPlaybackUrl(m.mediaUrl, m.fileName || m.dosyaAdi || `chat-video-${m.id || Date.now()}.mp4`)} preload="metadata" muted playsInline /><span className="chatInfoVideoBadge">▶</span></> : <RenewableFileImage src={m.mediaPreviewUrl || m.mediaUrl} fallbackSrc={m.mediaUrl} loadTimeoutMs={10000} alt={m.fileName || "media"} loading="lazy" decoding="async" /> }
                        </button>
                      ))}
                    </div>
                  ) : <div className="chatInfoEmpty">{lang === "en" ? "No media shared yet." : "Henüz medya paylaşılmadı."}</div>}
                </div>

                <div className="chatInfoPane">
                  {odaBaglantiMesajlari.length ? (
                    <div className="chatInfoList">
                      {odaBaglantiMesajlari.map((m) => m.links.map((link, idx) => {
                        const href = normalizeChatLink(link);
                        return (
                          <a key={`${m.id}-${idx}`} className="chatInfoListItem" href={href} target="_blank" rel="noreferrer">
                            <span className="chatInfoListIcon">🔗</span>
                            <span className="chatInfoListText">{link}</span>
                            <span className="chatInfoListMeta">↗</span>
                          </a>
                        );
                      }))}
                    </div>
                  ) : <div className="chatInfoEmpty">{lang === "en" ? "No links shared yet." : "Henüz bağlantı paylaşılmadı."}</div>}
                </div>

                <div className="chatInfoPane">
                  {odaBelgeMesajlari.length ? (
                    <div className="chatInfoList">
                      {odaBelgeMesajlari.map((m) => (
                        <button key={m.id} className="chatInfoListItem" onClick={() => nativeIndirDosya({
                          url: getFileUrl(m.mediaUrl), rawUrl: m.mediaUrl,
                          fileName: m.fileName || m.dosyaAdi || `chat-dosya-${m.id}`,
                          mimeType: m.mimeType || m.mediaType || m.fileType || m.dosyaTipi || "application/octet-stream",
                        })}>
                          <span className="chatInfoListIcon">📎</span>
                          <span className="chatInfoListText">{m.fileName || m.dosyaAdi || "Dosya"}</span>
                          <span className="chatInfoListMeta">⬇</span>
                        </button>
                      ))}
                    </div>
                  ) : <div className="chatInfoEmpty">{lang === "en" ? "No documents shared yet." : "Henüz belge paylaşılmadı."}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {uyePanelAcik && (
        <OdaUyePaneli
          oda={oda}
          uyeler={uyeler}
          user={user}
          familyId={familyId}
          yoneticiMi={yoneticiMi}
          onKapat={() => setUyePanelAcik(false)}
        />
      )}

      <div
        className={`chatMessages ${mesajlarKonumHazir ? "chatMessagesReady" : "chatMessagesBooting"}`}
        /* Geçmiş sayfasını başa eklerken konumu bu bileşendeki tek çapa
           motoru yönetir. Tarayıcının ikinci bir otomatik çapa uygulayıp aynı
           yüksekliği iki kez telafi etmesini engeller. */
        style={{ overflowAnchor: nativeScrollAnchorAktif ? "auto" : "none" }}
        ref={messagesRef}
        onScroll={handleMessagesScroll}
        onWheel={() => { userHistoryScrollIntentRef.current = true; }}
        onPointerDown={(e) => {
          userHistoryScrollIntentRef.current = true;
          chatTouchRef.current = { startX: e.clientX || 0, startY: e.clientY || 0, moved: false };
        }}
        onTouchStart={(e) => {
          userHistoryScrollIntentRef.current = true;
          const t0 = e.touches?.[0];
          if (t0) chatTouchRef.current = { startX: t0.clientX, startY: t0.clientY, moved: false };
        }}
        onTouchMove={(e) => {
          const t0 = e.touches?.[0];
          if (!t0) return;
          const dx = Math.abs(t0.clientX - chatTouchRef.current.startX);
          const dy = Math.abs(t0.clientY - chatTouchRef.current.startY);
          if (dx > 8 || dy > 8) chatTouchRef.current.moved = true;
        }}
        onTouchEnd={(e) => {
          const interactive = e.target?.closest?.('button,a,input,textarea,video,audio,.chatImageWrap,.chatVideoWrap,.chatFile,.chatBubble');
          if (!chatTouchRef.current.moved && !interactive) inputRef.current?.blur?.();
        }}
      >
        {mesajYuklendi && (aramaAcik && aramaMetni ? (
          filtrelenmis?.length
            ? filtrelenmis.map((msg) => (
                <div key={msg.id} id={`msg-${msg.id}`}>
                  <MesajItem msg={msg} benimMi={msg.uid === user?.uid} uyeler={uyeler}
                    user={user} familyId={familyId} roomId={roomId}
                    islemIzniVarMi={islemIzniVarMi} yoneticiMi={yoneticiMi} onReply={setReplyTo}
                    onQuoteClick={alintiyaGit}
                    onImagePreview={(item) => openMediaPreview(item, odaMedyaMesajlari)}
                    onFileDownload={(item) => nativeIndirDosya(item, "chat-dosya")}
                    onEdit={(m) => { setEditMsg(m); setMetin(m.metin); }} />
                </div>
              ))
            : <div className="chatEmpty"><div className="chatEmptyText">{t("chat.noResults")}</div></div>
        ) : (
          mesajGrupla().map((item) =>
            item.type === "sep" ? (
              <div key={item.key} className="chatDateSep"><span>{item.label}</span></div>
            ) : (
              <div key={item.msg.id} id={`msg-${item.msg.id}`}>
                <MesajItem msg={item.msg} benimMi={item.msg.uid === user?.uid} uyeler={uyeler}
                  user={user} familyId={familyId} roomId={roomId}
                  islemIzniVarMi={islemIzniVarMi} yoneticiMi={yoneticiMi} onReply={setReplyTo}
                  onQuoteClick={alintiyaGit}
                  onImagePreview={(item) => openMediaPreview(item, odaMedyaMesajlari)}
                  onFileDownload={(item) => nativeIndirDosya(item, "chat-dosya")}
                  onEdit={(m) => { setEditMsg(m); setMetin(m.metin); }} />
              </div>
            )
          )
        ))}

        {gorunenMesajlar.length === 0 && mesajYuklendi && !aramaAcik && (
          <div className="chatEmpty">
            <div className="chatEmptyIcon">💬</div>
            <div className="chatEmptyText">{t("chat.noMessages")}</div>
            <div className="chatEmptySubtext">{t("chat.noMessagesSub")}</div>
          </div>
        )}
        {!mesajYuklendi && (
          <div className="chatLoadingSkeleton">
            {[1,2,3,4].map(i => (
              <div key={i} className={`chatSkeletonRow ${i % 2 === 0 ? 'me' : ''}`}>
                <div className="chatSkeletonBubble" style={{ width: `${45 + (i * 13) % 35}%` }} />
              </div>
            ))}
          </div>
        )}

        {(mediaPreparing || uploading) && (
          <div className="chatUploadBar chatUploadBarPremium" role="status" aria-live="polite">
            <div className="chatUploadPulse">
              <span></span><span></span><span></span>
            </div>
            <div className="chatUploadMeta">
              <span className="chatUploadTitle">
                {mediaPreparing
                  ? (lang === "en" ? "Preparing media" : "Medya hazırlanıyor")
                  : (lang === "en" ? "Sending media" : "Medya gönderiliyor")}
              </span>
              <span className="chatUploadSub">
                {mediaPreparing
                  ? (lang === "en" ? "Optimizing for chat…" : "Sohbet için hazırlanıyor…")
                  : `%${Math.max(0, Math.min(100, Math.round(Number(uploadPct) || 0)))}`}
              </span>
            </div>
            <div className="chatUploadTrack" aria-hidden="true">
              <div className="chatUploadFill" style={{ width: `${mediaPreparing ? 28 : uploadPct}%` }} />
            </div>
          </div>
        )}
        <div ref={altRef} />
      </div>

      {asagiInButonuGoster && (
        <button
          type="button"
          className="chatScrollToBottomBtn"
          onClick={() => sohbetiEnAltaKaydir("smooth")}
          aria-label={lang === "en" ? "Go to latest messages" : "Güncel mesajlara git"}
        >
          <span>⌄</span>
        </button>
      )}

      {canSend && !sesKayitAktif && (
        <div className="chatQuickActions noSwipeBack" data-no-swipe-back="true">
          {QUICK_ACTIONS.map((qa) => (
            <button key={qa.key} className="chatQA"
              onClick={() => {
                if (qa.key === "photo") { medyaSec("camera"); }
                else if (qa.key === "file") { dosyaRef.current?.click(); }
                else if (qa.key === "poll") { oylamaModalAc(); }
                else if (qa.key === "menu") { menuGonder(); }
                else if (qa.key === "audio") { sesKaydiBaslat(); }
                else { setQaModal(qa); setQaBaslik(""); }
              }}>
              {qa.icon} {qa.key === "photo" ? (lang === "en" ? "Camera" : "Kamera") : t(qa.labelKey)}
            </button>
          ))}
        </div>
      )}
      {sesKayitAktif && (
        <div className="chatSesKayitBar">
          <button className="chatSesIptalBtn" onClick={sesKaydiIptal}>✕</button>
          <div className="chatSesKayitInfo">
            <span className="chatSesKayitDot" />
            <span className="chatSesKayitSure">{sesKayitSureStr(sesKayitSaniye)}</span>
            <span className="chatSesKayitEtiket">{lang === "en" ? "Recording…" : "Kaydediliyor…"}</span>
          </div>
          <button className="chatSesBirakBtn" onClick={sesKaydiDurdur} disabled={sesKayitYukleniyor}>
            {sesKayitYukleniyor ? "⏳" : "➤"}
          </button>
        </div>
      )}

      {(replyTo || editMsg) && (
        <div className="chatReplyBar">
          <div className="chatReplyContent">
            <span className="chatReplyLabel">{editMsg ? t("chat.editing") : `↩ ${replyTo?.ad}`}</span>
            <span className="chatReplyText">{editMsg ? editMsg.metin?.slice(0, 60) : replyTo?.metin?.slice(0, 60) || t("chat.media")}</span>
          </div>
          <button className="chatReplyClose" onClick={() => { setReplyTo(null); setEditMsg(null); setMetin(""); }}>×</button>
        </div>
      )}

      {canSend ? (
        <div className="chatInputWrap noSwipeBack" data-no-swipe-back="true">
          <div className="chatInputRow">
            <div style={{ position: "relative" }}>
              <button className="chatAttachBtn" onClick={() => setAttachMenuAcik((v) => !v)} aria-label={t("chat.addMedia")}>+</button>
              {attachMenuAcik && (
                <div className="chatAttachMenu">
                  <button className="chatAttachItem" onClick={() => { medyaSec("library"); setAttachMenuAcik(false); }}>📸 {t("chat.photo")}</button>
                  <button className="chatAttachItem" onClick={() => { dosyaRef.current?.click(); setAttachMenuAcik(false); }}>📎 {t("chat.file")}</button>
                  <div className="chatAttachInfo">
                    {lang === "en"
                      ? "Photos and voice recordings are kept for 30 days. Videos are kept for 10 days. Videos are compressed on iPhone before upload. Limit: 50 MB."
                      : "Fotoğraflar ve ses kayıtları 30 gün, videolar 10 gün saklanır. iPhone’da videolar yüklemeden önce sıkıştırılır. Limit: 50 MB."}
                  </div>
                </div>
              )}
            </div>
            <div className={`chatInputPill ${metin ? "chatInputPillActive" : ""}`}>
              <textarea ref={inputRef} className="chatInput" placeholder={t("chat.inputPlaceholder")}
                rows={1} value={metin} autoComplete="off" enterKeyHint="send" autoCorrect="on" autoCapitalize="sentences"
                onFocus={() => setAttachMenuAcik(false)}
                onChange={(e) => metinDegistir(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); gonder(); } }} />
              <div ref={inputMirrorRef} className="chatInputMirror" aria-hidden="true" />
              <button className="chatEmojiBtn" onClick={() => setEmojiPanel((v) => !v)} aria-label="Emoji">😊</button>
            </div>
            <button className={`chatSendBtn ${metin.trim() ? "chatSendBtnActive" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); inputRef.current?.focus?.(); }}
              onTouchStart={(e) => { e.preventDefault(); inputRef.current?.focus?.(); }}
              onTouchEnd={(e) => { e.preventDefault(); gonder(); }}
              onClick={gonder} disabled={!metin.trim() && !editMsg} aria-label={t("chat.send")}>➤</button>
          </div>
          {emojiPanel && (
            <div className="chatEmojiPanel">
              {EMOJILER.map((e) => (
                <button key={e} className="chatEmojiPanelItem"
                  onClick={() => { metinDegistir((inputRef.current?.value || metin) + e); setEmojiPanel(false); inputRef.current?.focus(); }}>{e}</button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="chatNoPermission">{t("chat.noSendPermission")}</div>
      )}

      <input ref={dosyaRef} type="file" style={{ display: "none" }}
        onChange={(e) => { if (e.target.files[0]) medyaYukle(e.target.files[0]); e.target.value = ""; }} />
      <input ref={fotografRef} type="file" accept="image/*,video/*" style={{ display: "none" }}
        onChange={(e) => { if (e.target.files[0]) medyaYukle(e.target.files[0]); e.target.value = ""; }} />
      <input ref={kameraRef} type="file" accept="image/*,video/*" capture="environment" style={{ display: "none" }}
        onChange={(e) => { if (e.target.files[0]) medyaYukle(e.target.files[0]); e.target.value = ""; }} />
      {/* MADDE 7: Android'de doğrudan kamerayı açan input (sadece image/* + capture) */}
      <input ref={kameraAndroidRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
        onChange={(e) => { if (e.target.files[0]) medyaYukle(e.target.files[0]); e.target.value = ""; }} />

      {qaModal && qaModal.key !== "menu" && (
        <div className="modalOverlay" onClick={() => setQaModal(null)}>
          <div className="darkModal" onClick={(e) => e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">{qaModal.icon} {t("chat.createQa", { label: t(qaModal.labelKey) })}</span>
              <button className="darkModalClose" onClick={() => setQaModal(null)}>×</button>
            </div>
            <div className="inputLabelGroup" style={{ marginBottom: "12px" }}>
              <label className="darkLabel">{t("chat.title")}</label>
              <input className="darkInput" placeholder={t("chat.titlePlaceholder", { label: t(qaModal.labelKey) })}
                value={qaBaslik} onChange={(e) => setQaBaslik(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && qaGonder()} autoFocus />
            </div>
            <button className="primaryButton" onClick={qaGonder}> {t("chat.createAndNotify")}</button>
            <button className="darkSecondaryBtn" style={{ marginTop: "8px" }} onClick={() => setQaModal(null)}> {t("common.cancel")}</button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MADDE 4 — OYLAMA OLUŞTURMA MODALI
          Tema (var(--*)) ve TR/EN dil desteği tamamen uyumludur.
          ══════════════════════════════════════════════════════════════ */}
      {oylamaModal && (
        <div className="modalOverlay" onClick={() => setOylamaModal(false)}>
          <div className="darkModal chatOylamaModal" onClick={(e) => e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">📊 {t("chat.pollCreate")}</span>
              <button className="darkModalClose" onClick={() => setOylamaModal(false)}>×</button>
            </div>

            <div className="inputLabelGroup" style={{ marginBottom: "12px" }}>
              <label className="darkLabel">{t("chat.pollQuestion")}</label>
              <input
                className="darkInput"
                placeholder={t("chat.pollQuestionPlaceholder")}
                value={oylamaSoru}
                maxLength={300}
                onChange={(e) => setOylamaSoru(e.target.value)}
                autoFocus
              />
            </div>

            <div className="inputLabelGroup">
              <label className="darkLabel">{t("chat.pollOptions")}</label>
              <div className="chatOylamaModalSecenekler">
                {oylamaSecenekler.map((secenek, index) => (
                  <div className="chatOylamaModalSatir" key={`oylama-secenek-${index}`}>
                    <span className="chatOylamaModalSira">{index + 1}</span>
                    <input
                      className="darkInput"
                      placeholder={t("chat.pollOptionPlaceholder", { index: index + 1 })}
                      value={secenek}
                      maxLength={120}
                      onChange={(e) => oylamaSecenekDegistir(index, e.target.value)}
                    />
                    {oylamaSecenekler.length > OYLAMA_MIN_SECENEK && (
                      <button
                        type="button"
                        className="chatOylamaModalSil"
                        onClick={() => oylamaSecenekSil(index)}
                        aria-label={t("common.delete")}
                      >×</button>
                    )}
                  </div>
                ))}
              </div>
              {oylamaSecenekler.length < OYLAMA_MAX_SECENEK && (
                <button type="button" className="chatOylamaModalEkle" onClick={oylamaSecenekEkle}>
                  + {t("chat.pollAddOption")}
                </button>
              )}
            </div>

            <div className="chatOylamaModalAyarlar">
              <button
                type="button"
                className={`chatOylamaModalAyar ${oylamaCoklu ? "aktif" : ""}`}
                onClick={() => setOylamaCoklu((v) => !v)}
              >
                <span className="chatOylamaModalKutu">{oylamaCoklu ? "✓" : ""}</span>
                {t("chat.pollMultiSetting")}
              </button>
              <button
                type="button"
                className={`chatOylamaModalAyar ${oylamaAnonim ? "aktif" : ""}`}
                onClick={() => setOylamaAnonim((v) => !v)}
              >
                <span className="chatOylamaModalKutu">{oylamaAnonim ? "✓" : ""}</span>
                {t("chat.pollAnonymousSetting")}
              </button>
            </div>

            <button
              className="primaryButton"
              onClick={oylamaGonder}
              disabled={!oylamaGecerliMi || oylamaGonderiliyor}
            >
              {oylamaGonderiliyor ? "⏳" : `📊 ${t("chat.pollSend")}`}
            </button>
            <button className="darkSecondaryBtn" style={{ marginTop: "8px" }} onClick={() => setOylamaModal(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* ── Fotoğraf Önizleme Popup ── */}
      {fotoOnizleme && ReactDOM.createPortal(
        <div
          className="chatPhotoPreview"
          onClick={() => setFotoOnizleme(null)}
          onTouchStart={handlePreviewTouchStart}
          onTouchMove={handlePreviewTouchMove}
          onTouchEnd={handlePreviewTouchEnd}
        >
          <button className="chatPhotoPreviewClose" onClick={() => setFotoOnizleme(null)}>×</button>
          {Array.isArray(fotoOnizleme.gallery) && fotoOnizleme.gallery.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); previewGit(-1); }} disabled={(fotoOnizleme.index || 0) <= 0}
                style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",width:42,height:42,borderRadius:"50%",border:"none",background:"rgba(255,255,255,0.16)",color:"#fff",fontSize:26,display:"flex",alignItems:"center",justifyContent:"center",opacity:(fotoOnizleme.index || 0) <= 0 ? 0.35 : 1}}>‹</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); previewGit(1); }} disabled={(fotoOnizleme.index || 0) >= fotoOnizleme.gallery.length - 1}
                style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",width:42,height:42,borderRadius:"50%",border:"none",background:"rgba(255,255,255,0.16)",color:"#fff",fontSize:26,display:"flex",alignItems:"center",justifyContent:"center",opacity:(fotoOnizleme.index || 0) >= fotoOnizleme.gallery.length - 1 ? 0.35 : 1}}>›</button>
              <div style={{position:"absolute",top:"max(54px, calc(env(safe-area-inset-top, 0px) + 18px))",left:"50%",transform:"translateX(-50%)",color:"#fff",fontSize:13,fontWeight:700,background:"rgba(0,0,0,0.35)",borderRadius:999,padding:"6px 10px"}}>
                {(fotoOnizleme.index || 0) + 1} / {fotoOnizleme.gallery.length}
              </div>
            </>
          )}
          <div className="chatPhotoPreviewStage" onClick={e => e.stopPropagation()}>
            {fotoOnizlemeVideoMu ? (
              <ChatModalVideo item={fotoOnizleme} />
            ) : (
              <>
                {!fotoOnizlemeYuklendi && <div className="chatPhotoLoading" role="status"><span /><small>{lang === "en" ? "Loading photo" : "Fotoğraf yükleniyor"}</small></div>}
                <img key={fotoOnizleme.mesajId || fotoOnizleme.url} src={fotoOnizleme.url} alt=""
                  className={fotoOnizlemeYuklendi ? "isLoaded" : ""}
                  onLoad={() => setFotoOnizlemeYuklendi(true)}
                  onClick={e => e.stopPropagation()}
                  onDoubleClick={(e) => { e.stopPropagation(); setPreviewZoom((z) => ({ scale: z.scale > 1 ? 1 : 2, x: 0, y: 0 })); }}
                  style={{transform:`translate3d(${previewZoom.x || 0}px, ${previewZoom.y || 0}px, 0) scale(${previewZoom.scale})`, transition: previewTouchRef.current.mode ? "none" : "transform 0.12s ease-out"}} />
              </>
            )}
          </div>
          <div className="chatPhotoPreviewActions" onClick={e => e.stopPropagation()}>
            <button type="button" className="isPrimary" onClick={fotoNativeIndir} disabled={fotoIndiriliyor || !fotoOnizlemeHazir}>
              {fotoIndiriliyor ? <MediaActionLoading /> : <MediaActionIcon type="download" />} {lang==="en"?"Download":"İndir"}
            </button>
            <button type="button" onClick={(event) => { event.stopPropagation(); setSecureViewerItem({
                ...fotoOnizleme,
                url: fotoOnizleme.url,
                fileName: fotoOnizleme.fileName || fotoOnizleme.dosyaAdi,
                mimeType: fotoOnizleme.mimeType,
                title: fotoOnizleme.fileName || fotoOnizleme.dosyaAdi || (lang === "en" ? "Chat media" : "Sohbet medyası"),
              }); }} disabled={!fotoOnizlemeHazir}>
              <MediaActionIcon type="view" /> {lang==="en"?"View":"Görüntüle"}
            </button>
            {fotoOnizleme.gonderenUid === user?.uid && (
              <button
                onClick={async () => {
                  try { await mesajSilService({ familyId, roomId, mesajId: fotoOnizleme.mesajId }); }
                  catch(e) { console.error("Foto sil:", e); }
                  setFotoOnizleme(null);
                }} className="isDanger">
                <MediaActionIcon type="delete" /> {lang==="en"?"Delete":"Sil"}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
      <SecureMediaViewer open={!!secureViewerItem} item={secureViewerItem} onClose={() => setSecureViewerItem(null)} lang={lang} />
    </div>
  );
}

const PREMIUM_INITIAL_MESSAGE_LIMIT = 30;
const PREMIUM_MESSAGE_PAGE_SIZE = 30;

/* ── Ana ChatPage ── */
function ChatPage({ familyId, user, tamAd, uyeler = [], yoneticiMi, islemIzniVarMi, setAktifSayfa, bildirimGoster, initialRoomId, onInitialRoomConsumed, onRoomOpened, onChatRoomStateChange, gorevEkle, urunEkle, isPremium = false, premiumConfig = {}, onPremiumLock, onPremiumLimitHit, onInternalBackTransition, isBgInstance = false }) {
  const [odalar, setOdalar]       = useState(() => getCachedChatRooms(familyId, user?.uid) || []);
  const [odalarYuklendi, setOdalarYuklendi] = useState(() => !!getCachedChatRooms(familyId, user?.uid));
  const [seciliOdaId, setSeciliOdaId] = useState(null);
  const [mesajlar, setMesajlar]   = useState([]);
  const [mesajYuklendi, setMesajYuklendi] = useState(false);
  const [premiumMesajLimit, setPremiumMesajLimit] = useState(PREMIUM_INITIAL_MESSAGE_LIMIT);
  const [eskiMesajYukleniyor, setEskiMesajYukleniyor] = useState(false);
  const eskiMesajYukleniyorRef = useRef(false);
  const [geciciOda, setGeciciOda] = useState(null);
  const [chatRoomTransition, setChatRoomTransition] = useState("idle");
  const chatOpenAnimatingRef = useRef(false);
  const listeningRoomRef = useRef(null);
  useEffect(() => {
    setPremiumMesajLimit(PREMIUM_INITIAL_MESSAGE_LIMIT);
    eskiMesajYukleniyorRef.current = false;
    setEskiMesajYukleniyor(false);
  }, [seciliOdaId]);

  useEffect(() => {
    if (!initialRoomId) return;

    setSeciliOdaId((prev) => {
      if (prev === initialRoomId) return prev;
      return initialRoomId;
    });

    onRoomOpened?.(initialRoomId);
    onInitialRoomConsumed?.();
  }, [initialRoomId, onInitialRoomConsumed, onRoomOpened]);

  const sohbetListesineDon = useCallback(() => {
    if (!seciliOdaId) return;
    if (typeof onInternalBackTransition === "function") {
      onInternalBackTransition();
      return;
    }
    setChatRoomTransition("slide-out-right");
    setTimeout(() => {
      setSeciliOdaId(null);
      setMesajYuklendi(false);
      setChatRoomTransition("idle");
    }, 210);
  }, [seciliOdaId, onInternalBackTransition]);

  // Global swipe-to-back zaten sayfa katmanını animasyonla taşıdığı için
  // bu durumda chat iç animasyonunu tekrar çalıştırmadan listeye anında dönmek gerekir.
  // Sağlık/çocuk/belgeler detay dönüşlerindeki commit mantığıyla aynı güvenli yapı.
  const sohbetListesineDonCommit = useCallback(() => {
    if (!seciliOdaId) return;
    setChatRoomTransition("idle");
    setSeciliOdaId(null);
    setMesajYuklendi(false);
  }, [seciliOdaId]);

  useEffect(() => {
    onChatRoomStateChange?.(!!seciliOdaId);

    if (seciliOdaId) {
      document.body.classList.add("chat-room-open");
      window.__chatRoomBack = sohbetListesineDon;
      window.__chatRoomCommitBack = sohbetListesineDonCommit;
    } else {
      document.body.classList.remove("chat-room-open");
      if (window.__chatRoomBack === sohbetListesineDon) delete window.__chatRoomBack;
      if (window.__chatRoomCommitBack === sohbetListesineDonCommit) delete window.__chatRoomCommitBack;
    }

    return () => {
      onChatRoomStateChange?.(false);
      document.body.classList.remove("chat-room-open");
      if (window.__chatRoomBack === sohbetListesineDon) delete window.__chatRoomBack;
      if (window.__chatRoomCommitBack === sohbetListesineDonCommit) delete window.__chatRoomCommitBack;
    };
  }, [seciliOdaId, sohbetListesineDon, sohbetListesineDonCommit, onChatRoomStateChange]);

  const aktifChatSessionVarRef = useRef(false);

  useEffect(() => {
    // Swipe arka planı için hazırlanan görünmez ChatPage örneği yalnızca listeyi
    // render eder. Aktif oturum sinyali göndermesi gerçek oda oturumuyla
    // çakışıp false -> true istekleri üretmemelidir.
    if (isBgInstance || !familyId || !user?.uid) return undefined;

    const aktiflikYaz = (aktif) => {
      aktifChatSessionService({
        familyId,
        active: !!aktif,
        roomId: aktif ? seciliOdaId : null,
      });
    };

    if (seciliOdaId) {
      aktiflikYaz(true);
      aktifChatSessionVarRef.current = true;
    } else if (aktifChatSessionVarRef.current) {
      // Liste ekranının ilk render'ında gereksiz active:false gönderme.
      // Yalnızca gerçekten açık bir odadan listeye dönüldüğünde kapat.
      aktiflikYaz(false);
      aktifChatSessionVarRef.current = false;
    }

    const heartbeat = seciliOdaId
      ? window.setInterval(() => {
          if (document.visibilityState !== "hidden") {
            aktiflikYaz(true);
          }
        }, 25000)
      : null;

    const pasifYap = () => {
      if (aktifChatSessionVarRef.current) aktiflikYaz(false);
    };
    const gorunurlukDegisti = () => {
      if (!seciliOdaId) return;
      if (document.visibilityState === "hidden") {
        pasifYap();
      } else {
        aktiflikYaz(true);
      }
    };

    window.addEventListener("pagehide", pasifYap);
    document.addEventListener("visibilitychange", gorunurlukDegisti);

    return () => {
      if (heartbeat) window.clearInterval(heartbeat);
      window.removeEventListener("pagehide", pasifYap);
      document.removeEventListener("visibilitychange", gorunurlukDegisti);
    };
  }, [familyId, user?.uid, seciliOdaId, isBgInstance]);

  // Gerçek component kapanışında veya kullanıcı/aile değişiminde, yalnızca
  // gerçekten açık kalmış bir oda varsa aktif oturumu kapat. Arka plan örneği
  // hiçbir zaman active-session sahibi değildir.
  useEffect(() => {
    if (isBgInstance || !familyId || !user?.uid) return undefined;
    return () => {
      if (!aktifChatSessionVarRef.current) return;
      aktifChatSessionVarRef.current = false;
      aktifChatSessionService({ familyId, active: false, roomId: null });
    };
  }, [familyId, user?.uid, isBgInstance]);

  useEffect(() => {
    if (!familyId || !user?.uid) {
      setOdalar([]);
      setOdalarYuklendi(false);
      return;
    }

    const cachedRooms = getCachedChatRooms(familyId, user.uid);
    if (cachedRooms) {
      preloadOdaAvatars(cachedRooms, getFileUrl);
      setOdalar(cachedRooms);
      setOdalarYuklendi(true);
    } else {
      setOdalar([]);
      setOdalarYuklendi(false);
    }

    return odalariDinleService(familyId, user.uid, (yeniOdalar) => {
      preloadOdaAvatars(yeniOdalar, getFileUrl);
      setOdalar(yeniOdalar);
      setOdalarYuklendi(true);
      setGeciciOda((prev) => {
        if (!prev) return null;
        return yeniOdalar.find((o) => o.id === prev.id) ? null : prev;
      });
    });
  }, [familyId, user?.uid]);


  const uygulanacakMesajLimiti = useCallback(() => {
    const limit = Number(premiumConfig?.limits?.mesajGecmis ?? 30);
    if (!isPremium) return limit > 0 ? limit : null;
    return premiumMesajLimit;
  }, [isPremium, premiumConfig?.limits?.mesajGecmis, premiumMesajLimit]);

  const mesajCacheUygula = useCallback((roomId) => {
    if (!familyId || !roomId) {
      setMesajlar([]);
      setMesajYuklendi(false);
      return;
    }
    const cachedMessages = getCachedChatMessages(familyId, roomId, uygulanacakMesajLimiti());
    if (cachedMessages) {
      setMesajlar(cachedMessages);
      setMesajYuklendi(true);
    } else {
      setMesajlar([]);
      setMesajYuklendi(false);
    }
  }, [familyId, uygulanacakMesajLimiti]);

  useEffect(() => {
    if (!familyId || !seciliOdaId) {
      setMesajlar([]);
      setMesajYuklendi(false);
      return;
    }

    let aktifDinleyici = true;
    const odaDegisti = listeningRoomRef.current !== seciliOdaId;
    listeningRoomRef.current = seciliOdaId;

    const uygulanacakLimit = uygulanacakMesajLimiti();
    const eskiPaketIstegi = !odaDegisti && eskiMesajYukleniyorRef.current;
    const cachedMessages = getCachedChatMessages(familyId, seciliOdaId, uygulanacakLimit);
    /* Daha büyük geçmiş sayfasına geçerken aynı paket önce üst bileşenden,
       sonra servisin cache callback'inden ve son olarak ağdan üç ayrı kez
       basılıyordu. Çapa ilk commit'te tüketildiği için ağ cevabı ikinci bir
       sıçrama yapıyordu. Sayfalama sırasında mevcut DOM'u koru ve yalnızca
       kesin ağ sonucunu tek commit olarak uygula. */
    if (cachedMessages && !eskiPaketIstegi) {
      setMesajlar(cachedMessages);
      setMesajYuklendi(true);
    } else if (odaDegisti) {
      setMesajlar([]);
      setMesajYuklendi(false);
    }
    const unsubscribe = mesajlariDinleService(
      familyId,
      seciliOdaId,
      (yeniMesajlar) => {
        if (aktifDinleyici) {
          setMesajlar(yeniMesajlar || []);
          setMesajYuklendi(true);
          eskiMesajYukleniyorRef.current = false;
          setEskiMesajYukleniyor(false);
        }
      },
      uygulanacakLimit,
      { emitCached: !eskiPaketIstegi }
    );

    return () => {
      aktifDinleyici = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [familyId, seciliOdaId, uygulanacakMesajLimiti]);

  useEffect(() => {
    if (!eskiMesajYukleniyor) return;
    const timeout = window.setTimeout(() => {
      eskiMesajYukleniyorRef.current = false;
      setEskiMesajYukleniyor(false);
    }, 10000);
    return () => window.clearTimeout(timeout);
  }, [eskiMesajYukleniyor]);

  const dahaEskiMesajlariYukle = useCallback(() => {
    if (!isPremium || eskiMesajYukleniyorRef.current || !seciliOdaId) return;
    eskiMesajYukleniyorRef.current = true;
    setEskiMesajYukleniyor(true);
    setPremiumMesajLimit((prev) => prev + PREMIUM_MESSAGE_PAGE_SIZE);
  }, [isPremium, seciliOdaId]);

  const handleOdaSec = (roomId) => {
    if (chatOpenAnimatingRef.current) return;
    const commit = () => {
      mesajCacheUygula(roomId);
      setSeciliOdaId(roomId);
      onRoomOpened?.(roomId);
    };

    // Chat oda ekranı position: fixed çalışıyor. Parent .swipePageWrap'e
    // transform animasyonu vermek iOS/WebView'de fixed katmanın ilk anda
    // küçük hesaplanıp sonra büyümesine neden oluyor. Bu yüzden oda açılışında
    // parent transition kullanılmaz; animasyon sadece chatRoomFull katmanında yapılır.
    chatOpenAnimatingRef.current = true;
    setChatRoomTransition("chat-room-enter");
    commit();
    setTimeout(() => {
      setChatRoomTransition("idle");
      chatOpenAnimatingRef.current = false;
    }, 240);
  };

  const handleYeniOdaOlustur = (id, ad, uyeUidler) => {
    const yeni = { id, ad, uyeUidler, olusturanUid: user.uid, lastMessage: "", lastMessageAt: null, createdAt: new Date() };
    const commit = () => {
      setMesajYuklendi(false);
      setGeciciOda(yeni);
      setSeciliOdaId(id);
      onRoomOpened?.(id);
    };

    // Yeni oda açılışında da parent wrapper transform edilmez;
    // fixed chat katmanı kendi içinde stabil şekilde içeri kayar.
    setChatRoomTransition("chat-room-enter");
    commit();
    setTimeout(() => setChatRoomTransition("idle"), 240);
  };

  const tumOdalar = geciciOda && !odalar.find((o) => o.id === geciciOda.id)
    ? [geciciOda, ...odalar]
    : odalar;

  const seciliOda = tumOdalar.find((o) => o.id === seciliOdaId) || null;

  return (
    <div className={`chatPageStack ${seciliOdaId ? "chatPageStackRoomOpen" : ""}`.trim()}>
      <OdaListesi
        odalar={tumOdalar} odalarYuklendi={odalarYuklendi} uyeler={uyeler} user={user}
        familyId={familyId} tamAd={tamAd}
        yoneticiMi={yoneticiMi} islemIzniVarMi={islemIzniVarMi}
        onOdaSec={handleOdaSec}
        onYeniOdaOlustur={handleYeniOdaOlustur}
        isPremium={isPremium}
        premiumConfig={premiumConfig}
        onPremiumLock={onPremiumLock}
        isBgInstance={isBgInstance}
      />

      {seciliOdaId && seciliOda && !isBgInstance && ReactDOM.createPortal(
        <SohbetEkrani
          oda={seciliOda} mesajlar={mesajlar} mesajYuklendi={mesajYuklendi} uyeler={uyeler}
          user={user} tamAd={tamAd} familyId={familyId}
          yoneticiMi={yoneticiMi} islemIzniVarMi={islemIzniVarMi}
          setAktifSayfa={setAktifSayfa}
          onGeri={sohbetListesineDon}
          transitionClass={chatRoomTransition}
          isPremium={isPremium}
          premiumConfig={premiumConfig}
          onPremiumLimitHit={onPremiumLimitHit}
          onLoadOlder={dahaEskiMesajlariYukle}
          hasMoreOlder={isPremium && mesajlar.length >= premiumMesajLimit}
          loadingOlder={eskiMesajYukleniyor}
        />,
        document.body
      )}
    </div>
  );
}

export default ChatPage;
